/**
 * @fileoverview Own the Documents workspace's navigation, selection, and coordinated actions.
 *
 * Document/revision identity connects library/search views to previews, editing,
 * comparison, and sharing. Focused controllers own those interactions;
 * `kc-internal-frontend/src/store/Documents/useDocumentData.js` owns shared metadata
 * subscriptions. `kc-internal-frontend/src/App.js` preserves this workspace across
 * navigation, so hidden and unmounted have different lifetimes.
 *
 * `kc-internal-backend/functions/simul_archive_write.py` owns canonical versions;
 * `kc-internal-backend/functions/document_access.py` owns capabilities;
 * `kc-internal-backend/functions/document_search/service.py` owns search semantics.
 * Mutations must reconcile metadata and preserve selection identity across async
 * work. Binary bodies and editor state remain outside the shared metadata cache.
 */

import useDocumentRequests from './useDocumentRequests';
import { documentPreviewSource } from './documentSources';
import PdfDocumentViewer from './PdfDocumentViewer';
import { WORD_DOCUMENT_ONLY_MESSAGE, isWordDocumentFilename } from './documentFilePolicy';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../api';
import { AuthContext } from '../../AuthContext';
import ToastContext from '../../ToastContext';
import AppIcon from '../shared/AppIcon';
import Button from '../shared/AppButton';
import AiDocumentEditPanel from './AiDocumentEditPanel';
import DocumentProofreadPanel, { proofreadRequestKey } from './DocumentProofreadPanel';
import TemplateWorkspace from './TemplateWorkspace';
import FileContextMenu from './FileContextMenu';
import DocumentTrashDialog from './DocumentTrashDialog';
import FolderContextMenu, {
  canTrashFolder,
  FolderCreateContextMenu,
} from './FolderContextMenu';
import useDocumentFileExplorerController, {
  documentFolderCount as folderCount,
  documentClipboardSelection,
  normalizeDocumentFolderPath as normalizeFolderPath,
  shouldUseDocumentFileViewer,
  useDocumentClipboardController,
  useExplorerContextMenus,
} from './DocumentFileExplorerController';
import FolderDestinationModal, {
  folderDestinationKey,
  folderDestinationScope,
  folderMovePayload,
} from './FolderDestinationModal';
import BulkSelectionBar, { useDocumentSelection } from './BulkSelectionBar';
import DocumentGovernanceSettings from './DocumentGovernanceSettings';
import DocumentFileTypeIcon from './DocumentFileTypeIcon';
import DocumentExplorerPreviewColumn from './DocumentExplorerPreviewColumn';
import DocumentFileViewerModal from './DocumentFileViewerModal';
import useDocumentData from '../../store/Documents/useDocumentData';
import { invalidateDocumentData } from '../../store/Documents/dataApi';
import {
  DEFAULT_DOCUMENTS_FOLDER_TAB,
  setDocumentsFolderTabs,
  setDocumentsWorkspaceField,
} from '../../store/Documents/reducer';
import DocumentSharePanel from './DocumentSharePanel';
import DocumentTransferManager, { droppedFiles, isExternalFileDrag } from './DocumentTransferManager';
import WordDocumentUploadField from './WordDocumentUploadField';
import MillerColumnExplorer, {
  MillerColumn,
  MillerColumnEmpty,
  MillerColumnHeader,
  MillerColumnItem,
  MillerColumnList,
  MillerColumnRow,
} from './MillerColumnExplorer';
import TrashView from './TrashView';
import {
  DocumentOrganizationActions,
  EMPTY_ORGANIZATION_FILTERS,
  RegistryOrganizationControls,
  formatFileSize,
} from './DocumentRegistryOrganization';
import {
  apiUrl,
  currentVersion,
  formatCreated,
  formatSize,
  isDocxVersion,
  versionLabel,
} from './archiveReviewUtils';
import NewFolderModal from './NewFolderModal';
import './DocumentsPage.css';

const INITIAL_COMPARISON = null;
const POLL_STATUSES = new Set(['queued', 'running']);
const WORD_EDIT_POLL_INTERVAL_MS = 2000;
const WORD_EDIT_LAUNCH_POLL_TIMEOUT_MS = 60000;
const CURRENT_COMPARISON_ARTIFACT_VERSION = 12;
const DOCUMENT_PAGE_SIZE = 50;
const FOLDER_PAGE_SIZE = 50;
const FOLDER_DRAWER_PAGE_SIZE = 50;

const FOLDER_CANVAS_ITEM_SELECTOR = [
  'button',
  'a',
  'input',
  'textarea',
  'select',
  'label',
  '[role="button"]',
  '[role="menuitem"]',
  '.documents-folder-column-item',
  '.documents-folder-column-file-row',
  '.documents-folder-drawer-file-row',
  '.documents-folder-drawer-subfolder',
].join(', ');



function isFolderCanvasBackground(event) {
  return !event.target?.closest?.(FOLDER_CANVAS_ITEM_SELECTOR);
}



function markExternalUploadTarget(event, folder, setTarget) {
  if (!folder || !isExternalFileDrag(event.dataTransfer)) return false;
  event.preventDefault();
  event.stopPropagation();
  event.dataTransfer.dropEffect = 'copy';
  setTarget(folderBrowserIdentity(folder));
  return true;
}



function leaveExternalUploadTarget(event, setTarget) {
  if (!isExternalFileDrag(event.dataTransfer)) return false;
  event.stopPropagation();
  if (!event.currentTarget.contains(event.relatedTarget)) setTarget('');
  return true;
}



function dropExternalUpload(event, folder, setTarget, onExternalFileDrop) {
  if (!folder || !isExternalFileDrag(event.dataTransfer)) return false;
  event.preventDefault();
  event.stopPropagation();
  const transfer = event.dataTransfer;
  setTarget('');
  onExternalFileDrop(folder, transfer);
  return true;
}



function folderUploadLabel(folder) {
  return `Upload to ${folder?.name || folderTitle(folderBrowserPath(folder) || folder?.folder_path) || 'folder'}`;
}

const COMPARISON_RENDERER_KEY = 'kc.documents.comparison-renderer.v1';
const DEFAULT_COMPARISON_RENDERER = 'docxodus';
const COMPARISON_RENDERERS = new Set(['libreoffice', DEFAULT_COMPARISON_RENDERER]);



function containModalFocus(event) {
  if (event.key !== 'Tab') return;
  const focusable = [...event.currentTarget.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )].filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
  if (!focusable.length) {
    event.preventDefault();
    event.currentTarget.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}



function DocumentShortcutHelp({ visible, onClose }) {
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  useEffect(

  () => {
    if (!visible) return undefined;
    returnFocusRef.current = document.activeElement;
    const closeButton = dialogRef.current?.querySelector('button');
    closeButton?.focus();
    return () => returnFocusRef.current?.focus?.();
  }, [visible]);
  if (!visible) return null;
  const shortcuts = [
    ['Ctrl/Cmd + A', 'Select this page'],
    ['Ctrl/Cmd + C', 'Copy selected documents'],
    ['Ctrl/Cmd + X', 'Cut selected documents'],
    ['Ctrl/Cmd + V', 'Paste into the open folder'],
    ['Delete', 'Move selected documents to trash'],
    ['F2', 'Rename one selected document'],
    ['Shift + F10', 'Open the item action menu'],
    ['Escape', 'Clear selection or close a dialog'],
  ];
  return (
    <div className="documents-shortcuts-backdrop" role="presentation" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <section
        ref={dialogRef}
        className="documents-shortcuts"
        role="dialog"
        aria-modal="true"
        aria-labelledby="documents-shortcuts-title"
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
          containModalFocus(event);
        }}>
        <header>
          <h2 id="documents-shortcuts-title">Document shortcuts</h2>
          <button type="button" aria-label="Close document shortcuts" onClick={onClose}><AppIcon icon="pi pi-times" aria-hidden="true" /></button>
        </header>
        <dl>{shortcuts.map(([keys, action]) => <React.Fragment key={keys}><dt><kbd>{keys}</kbd></dt><dd>{action}</dd></React.Fragment>)}</dl>
      </section>
    </div>
  );
}



function readComparisonRenderer() {
  if (typeof window === 'undefined') return DEFAULT_COMPARISON_RENDERER;
  const stored = window.localStorage.getItem(COMPARISON_RENDERER_KEY) || '';
  return COMPARISON_RENDERERS.has(stored) ? stored : DEFAULT_COMPARISON_RENDERER;
}



function canAttemptVersionComparison(version) {
  if (!version) return false;
  if (version.is_docx_known === false) return Boolean(version.object_key);
  if (version.is_docx === false) return false;
  return isDocxVersion(version) || Boolean(version.object_key);
}



function comparisonArtifactIsCurrent(comparison) {
  return Number(comparison?.artifact_version || 0) >= CURRENT_COMPARISON_ARTIFACT_VERSION;
}



function comparisonNeedsUpgrade(comparison) {
  return Boolean(
    comparison?.comparison_id &&
    comparison?.status === 'ready' &&
    !comparisonArtifactIsCurrent(comparison)
  );
}



function comparisonEnhancementPending(comparison) {
  return ['queued', 'running'].includes(comparison?.changed_pages_status);
}



function nonnegativeInteger(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.floor(number) : fallback;
}



function storedString(state, key) {
  return typeof state?.[key] === 'string' ? state[key] : '';
}



function storedDocumentUrl(state) {
  const value = storedString(state, 'documentUrl');
  return /^\/documents\/[^/?#]+(?:\?[^#]*)?$/.test(value) ? value : '';
}



function useDocumentsWorkspaceField(field) {
  const value = useSelector((state) => state.documents[field]);
  const dispatch = useDispatch();
  const store = useStore();
  const setValue = useCallback(

  (nextValue) => {
    const documentsState = store.getState().documents;
    const currentValue = documentsState[field];
    const value = typeof nextValue === 'function' ? nextValue(currentValue) : nextValue;
    if (field === 'folderTabs' || field === 'activeFolderTabId') {
      dispatch(setDocumentsFolderTabs({
        tabs: field === 'folderTabs' ? value : documentsState.folderTabs,
        activeTabId: field === 'activeFolderTabId' ? value : documentsState.activeFolderTabId,
      }));
      return;
    }
    dispatch(setDocumentsWorkspaceField({
      field,
      value,
    }));
  }, [dispatch, field, store]);
  return [value, setValue];
}



function Highlight({ text, term }) {
  if (!term || !text) return <>{text}</>;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, index) => (
        index % 2 === 1 ?
          <mark key={`${part}-${index}`} className="documents-archive-highlight">{part}</mark> :
          <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      ))}
    </>
  );
}



function CreatedCell({ value }) {
  const created = formatCreated(value);
  return (
    <time className="documents-archive-created" dateTime={created.dateTime} title={created.title}>
      <span>{created.date}</span>
      {created.time && <small>{created.time}</small>}
    </time>
  );
}



function CompactCreatedCell({ value }) {
  const created = formatCreated(value);
  const label = [created.date, created.time].filter(Boolean).join(' · ');
  return (
    <time className="documents-vcs-created-compact" dateTime={created.dateTime} title={created.title}>
      {label}
    </time>
  );
}



function folderTitle(folderPath) {
  const parts = String(folderPath || '').split('/').filter(Boolean);
  return parts[parts.length - 1] || 'Matter root';
}



function folderSearchText(folder) {
  const caseInfo = folder?.case_info || {};
  return [
    folder?.name,
    folder?.folder_path,
    folder?.source_simul_folder_path,
    folder?.matter_number,
    folder?.client_name,
    folder?.defendant_name,
    folder?.state,
    folder?.jurisdiction,
    ...(Array.isArray(folder?.case_tags) ? folder.case_tags : []),
    ...(Array.isArray(caseInfo.client_names) ? caseInfo.client_names : []),
    ...(Array.isArray(caseInfo.defendant_names) ? caseInfo.defendant_names : []),
    ...(Array.isArray(caseInfo.attorney_names) ? caseInfo.attorney_names : []),
    caseInfo.state,
    caseInfo.jurisdiction,
    caseInfo.case_number,
    caseInfo.calendar_title,
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase();
}



function folderDirectSearchText(folder) {
  return [
    folder?.name,
    folder?.folder_path ? folderTitle(folder.folder_path) : null,
    folder?.scope_folder_path ? folderTitle(folder.scope_folder_path) : null,
    folder?.source_simul_folder_path ? folderTitle(folder.source_simul_folder_path) : null,
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase();
}



function folderCaseSearchText(folder) {
  const caseInfo = folder?.case_info || {};
  return [
    folder?.matter_number,
    folder?.client_name,
    folder?.defendant_name,
    folder?.state,
    folder?.jurisdiction,
    ...(Array.isArray(folder?.case_tags) ? folder.case_tags : []),
    ...(Array.isArray(caseInfo.client_names) ? caseInfo.client_names : []),
    ...(Array.isArray(caseInfo.defendant_names) ? caseInfo.defendant_names : []),
    ...(Array.isArray(caseInfo.attorney_names) ? caseInfo.attorney_names : []),
    caseInfo.state,
    caseInfo.jurisdiction,
    caseInfo.case_number,
    caseInfo.calendar_title,
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase();
}



function folderPartyLabel(folder) {
  const caseInfo = folder?.case_info || {};
  const clients = Array.isArray(caseInfo.client_names) ? caseInfo.client_names.filter(Boolean) : [];
  const defendants = Array.isArray(caseInfo.defendant_names) ? caseInfo.defendant_names.filter(Boolean) : [];
  if (clients.length && defendants.length) return `${clients.join(', ')} v. ${defendants.join(', ')}`;
  return clients.join(', ') || defendants.join(', ');
}



function documentFolderSearchIdentities(document) {
  const identities = new Set();
  [
    document?.folder_id,
    document?.simul_parent_id,
    document?.simul_folder_id,
    document?.source_simul_folder_id,
    ...(Array.isArray(document?.simul_folder_ids) ? document.simul_folder_ids : []),
  ].filter(Boolean).forEach((value) => identities.add(`id:${String(value)}`));

  const matterNumber = String(document?.matter_number || '').trim().toLocaleLowerCase();
  const folderPath = normalizeFolderPath(document?.folder_path).toLocaleLowerCase();
  if (matterNumber || folderPath) identities.add(`internal:${matterNumber}:${folderPath}`);
  return identities;
}



function folderSearchIdentities(folder) {
  const identities = new Set([folderBrowserIdentity(folder)]);
  [folder?.folder_id, simulFolderId(folder)].filter(Boolean).forEach((value) => identities.add(`id:${String(value)}`));
  return identities;
}



function folderBrowserPath(folder) {
  const matterNumber = String(folder?.matter_number || '').trim();
  const sourcePath = normalizeFolderPath(folder?.source_simul_folder_path);
  if (!matterNumber && folder?.source === 'simul_migration' && sourcePath) {
    const parts = sourcePath.split('/');
    return normalizeFolderPath(parts[0]?.toLocaleLowerCase() === 'kneupper & covey documents'
      ? parts.slice(1).join('/')
      : sourcePath);
  }

  const scopePath = folderScopePath(folder);
  if (!matterNumber && folder?.root_kind === 'review') {
    if (scopePath.toLocaleLowerCase() === 'needs filing') return '';
    if (scopePath.toLocaleLowerCase().startsWith('needs filing/')) {
      return normalizeFolderPath(scopePath.split('/').slice(1).join('/'));
    }
  }
  return scopePath;
}



function isHiddenFolderBrowserRoot(folder) {
  const isNeedsFilingWrapper = (
    !String(folder?.matter_number || '').trim() &&
    folder?.root_kind === 'review' &&
    normalizeFolderPath(folder?.folder_path).toLocaleLowerCase() === 'needs filing'
  );
  const isSimulDocumentsWrapper = (
    !String(folder?.matter_number || '').trim() &&
    folder?.source === 'simul_migration' &&
    normalizeFolderPath(folder?.source_simul_folder_path).toLocaleLowerCase() === 'kneupper & covey documents'
  );
  return isNeedsFilingWrapper || isSimulDocumentsWrapper;
}



function folderBrowserIdentity(folder) {
  if (!folder) return '';
  if (folder.folder_type === 'simul') return `simul:${simulFolderId(folder)}`;
  return `internal:${String(folder.matter_number || '').trim().toLocaleLowerCase()}:${folderBrowserPath(folder).toLocaleLowerCase()}`;
}



function folderBrowserParentIdentity(folder) {
  if (!folder) return '';
  if (folder.folder_type === 'simul') {
    return folder.parent_folder_id ? `simul:${String(folder.parent_folder_id)}` : '';
  }
  if (folder.folder_type === 'matter') return '';
  const matter = String(folder.matter_number || '').trim().toLocaleLowerCase();
  const parentPath = folderBrowserPath(folder).split('/').slice(0, -1).join('/').toLocaleLowerCase();
  if (parentPath) return `internal:${matter}:${parentPath}`;
  return matter ? `internal:${matter}:` : '';
}

// Index each immutable folder array once instead of scanning the entire catalog
// for every visible row's child count. Weak keys release replaced catalogs.
const folderChildrenIndexes = new WeakMap();


function folderBrowserChildren(folder, folders) {
  let childrenByParent = folderChildrenIndexes.get(folders);
  if (!childrenByParent) {
    childrenByParent = new Map();
    folders.forEach((candidate) => {
      const parent = folderBrowserParentIdentity(candidate);
      if (!childrenByParent.has(parent)) childrenByParent.set(parent, []);
      childrenByParent.get(parent).push(candidate);
    });
    folderChildrenIndexes.set(folders, childrenByParent);
  }
  // Callers sort the result for their own presentation.
  return [...(childrenByParent.get(folderBrowserIdentity(folder)) || [])];
}



function folderBrowserBreadcrumbs(folder, folders) {
  const byIdentity = new Map(folders.map((candidate) => [folderBrowserIdentity(candidate), candidate]));
  const breadcrumbs = [];
  const visited = new Set();
  let current = folder;
  while (current && !visited.has(folderBrowserIdentity(current))) {
    visited.add(folderBrowserIdentity(current));
    breadcrumbs.unshift(current);
    current = byIdentity.get(folderBrowserParentIdentity(current));
  }
  return breadcrumbs;
}



function simulFolderId(folder) {
  return String(folder?.simul_folder_id || folder?.folder_id || '').trim();
}



function folderScopePath(folder) {
  if (folder?.folder_type === 'simul' || folder?.folder_type === 'matter') return '';
  return normalizeFolderPath(folder?.scope_folder_path || folder?.folder_path);
}



function folderIdentity(folder) {
  if (!folder) return '';
  if (folder.folder_type === 'simul') return `simul:${simulFolderId(folder)}`;
  return `internal:${String(folder.matter_number || '').trim().toLocaleLowerCase()}:${folderScopePath(folder).toLocaleLowerCase()}`;
}



function folderParentIdentity(folder) {
  if (!folder) return '';
  if (folder.folder_type === 'simul') {
    return folder.parent_folder_id ? `simul:${String(folder.parent_folder_id)}` : '';
  }
  if (folder.folder_type === 'matter') return '';
  const matter = String(folder.matter_number || '').trim().toLocaleLowerCase();
  const parentPath = normalizeFolderPath(
    folder.parent_folder_path || folderScopePath(folder).split('/').slice(0, -1).join('/')
  ).toLocaleLowerCase();
  if (parentPath) return `internal:${matter}:${parentPath}`;
  return matter ? `internal:${matter}:` : '';
}



function folderSourceLabel(folder) {
  if (folder?.folder_type === 'simul') return 'Simul';
  if (folder?.source_simul_folder_id || folder?.source_simul_folder_path || folder?.source === 'simul_migration') {
    return 'Local · Simul linked';
  }
  return 'Local';
}



function folderContentsSummary(folder, childCount = 0) {
  const directCount = folderCount(folder, 'direct_document_count', 'document_count');
  const descendantCount = Math.max(
    directCount,
    folderCount(folder, 'descendant_document_count', 'document_count')
  );
  const fileSummary = descendantCount > directCount
    ? `${directCount} here · ${descendantCount} including subfolders`
    : `${directCount} ${directCount === 1 ? 'file' : 'files'} here`;
  return `${folderSourceLabel(folder)} · ${fileSummary}${childCount ? ` · ${childCount} ${childCount === 1 ? 'subfolder' : 'subfolders'}` : ''}`;
}



function documentSourceLabel(document) {
  if (document?.available_locally === false) return 'Simul only';
  if (
    document?.simul_parent_id ||
    document?.source_simul_folder_id ||
    document?.simul_folder_path ||
    document?.source === 'simul_migration'
  ) return 'Local · Simul linked';
  return 'Local';
}



function documentListSignals(document) {
  const owner = document?.owner_name || document?.created_by_display || document?.latest_created_by_display || '';
  const changedAt = document?.modified_at || document?.updated_at || document?.latest_created_at || document?.created_at;
  const changed = changedAt ? formatCreated(changedAt).date : '';
  const size = document?.size_bytes != null ? formatFileSize(document.size_bytes) : '';
  return [documentSourceLabel(document), owner, changed, size].filter(Boolean).join(' · ');
}



function downloadLabel(version, documentFilename = '') {
  const candidate = [
    version?.original_filename,
    version?.filename,
    documentFilename,
    version?.object_key,
  ].find((value) => String(value || '').match(/\.[a-z0-9]{1,8}(?:$|[?#])/i));
  const match = String(candidate || '').match(/\.([a-z0-9]{1,8})(?:$|[?#])/i);
  if (match) return `Download ${match[1].toUpperCase()}`;
  if (isDocxVersion(version)) return 'Download DOCX';
  if (version?.mime_type === 'application/pdf') return 'Download PDF';
  return 'Download original';
}



function InlineOperationError({ message, className = 'documents-archive-error' }) {
  if (!message) return null;
  return (
    <div className={className} role="alert">
      <AppIcon icon="pi pi-exclamation-circle" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}



function canonicalFolderCatalog(folders = []) {
  const canonical = new Map();
  folders.forEach(

  (folder) => {
    const identity = folderIdentity(folder);
    if (!identity) return;
    const existing = canonical.get(identity);
    if (!existing) {
      canonical.set(identity, folder);
      return;
    }
    const preferred = folderCount(folder, 'descendant_document_count', 'document_count') >=
      folderCount(existing, 'descendant_document_count', 'document_count') ? folder : existing;
    canonical.set(identity, {
      ...existing,
      ...preferred,
      direct_document_count: Math.max(
        folderCount(existing, 'direct_document_count', 'document_count'),
        folderCount(folder, 'direct_document_count', 'document_count')
      ),
      descendant_document_count: Math.max(
        folderCount(existing, 'descendant_document_count', 'document_count'),
        folderCount(folder, 'descendant_document_count', 'document_count'),
        folderCount(existing, 'direct_document_count', 'document_count'),
        folderCount(folder, 'direct_document_count', 'document_count')
      ),
      child_folder_count: Math.max(
        folderCount(existing, 'child_folder_count'),
        folderCount(folder, 'child_folder_count')
      ),
    });
  });
  return [...canonical.values()];
}



function folderCreationPath(folder, folders = []) {
  if (!folder || folder.folder_type === 'matter') return '';
  if (folder.folder_type !== 'simul') return normalizeFolderPath(folder.folder_path);

  const foldersById = new Map(
    folders
      .filter((candidate) => candidate?.folder_id)
      .map((candidate) => [String(candidate.folder_id), candidate])
  );
  const parts = [];
  const visited = new Set();
  let current = folder;
  while (
    current?.folder_id &&
    Number(current.depth || 0) > 0 &&
    !visited.has(String(current.folder_id))
  ) {
    visited.add(String(current.folder_id));
    parts.unshift(current.name || folderTitle(current.folder_path));
    current = foldersById.get(String(current.parent_folder_id || ''));
  }
  if (folder.matter_number && parts.length > 0) parts.shift();
  return normalizeFolderPath(parts.join('/'));
}



function matterChoiceLabel(matter) {
  const number = String(matter?.MatterNumber || matter?.matter_number || '').trim();
  const client = String(matter?.Client || matter?.client || '').trim();
  const defendant = String(matter?.Defendant || matter?.defendant || '').trim();
  const state = String(matter?.Jurisdiction || matter?.State || matter?.state || '').trim();
  const parties = client && defendant
    ? `${client} v. ${defendant}`
    : client || defendant || 'Matter';
  return `${parties}${state ? ` · ${state}` : ''}${number ? ` · ${number}` : ''}`;
}



function creationDestination(form, matters = [], folders = []) {
  const kind = form?.destination_kind || 'matter';
  const folderPath = normalizeFolderPath(form?.folder_path);
  const destinationMatter = kind === 'internal' ? '' : String(form?.matter_number || '').trim();
  const destinationFolder = folders.find((folder) => (
    String(folder?.matter_number || '').trim() === destinationMatter &&
    normalizeFolderPath(folderCreationPath(folder, folders)).toLocaleLowerCase() === folderPath.toLocaleLowerCase()
  ));
  if (kind === 'internal') {
    return {
      matter_number: '',
      folder_path: folderPath || 'Firm documents',
      folder_id: String(destinationFolder?.folder_id || ''),
      plainLabel: `Internal / ${folderPath || 'Firm documents'}`,
      segments: ['Internal', ...(folderPath || 'Firm documents').split('/')],
      createsMatterFolder: false,
    };
  }

  const matterNumber = String(form?.matter_number || '').trim();
  const matter = matters.find((candidate) => (
    String(candidate?.MatterNumber || candidate?.matter_number || '').trim() === matterNumber
  ));
  const rootLabel = matter ? matterChoiceLabel(matter) : `Matter ${matterNumber}`;
  const hasMatterFolder = folders.some((folder) => (
    folder.folder_type === 'matter' &&
    String(folder.matter_number || '').trim() === matterNumber
  ));
  return {
    matter_number: matterNumber,
    folder_path: folderPath,
    folder_id: String(destinationFolder?.folder_id || ''),
    plainLabel: `${rootLabel}${folderPath ? ` / ${folderPath}` : ''}`,
    segments: [rootLabel, ...folderPath.split('/').filter(Boolean)],
    createsMatterFolder: Boolean(matterNumber && !hasMatterFolder),
  };
}



function documentLocationPath(document) {
  const matter = String(document?.matter_number || '').trim();
  if (matter) return `/case_details/${encodeURIComponent(matter)}?tab=documents`;
  if (document?.folder_id) return `/documents?folder=${encodeURIComponent(document.folder_id)}`;
  const path = normalizeFolderPath(document?.folder_path);
  return path ? `/documents?folder_path=${encodeURIComponent(path)}` : '';
}



function DocumentLocationBreadcrumb({ segments = [], to = '' }) {
  const contents = <>
    <AppIcon icon="pi pi-folder" aria-hidden="true" />
    {segments.map((segment, index) => (
      <React.Fragment key={`${segment}-${index}`}>
        {index > 0 && <AppIcon icon="pi pi-angle-right" aria-hidden="true" />}
        <span>{segment}</span>
      </React.Fragment>
    ))}
  </>;
  return (
    <nav className="documents-location-breadcrumb" aria-label="Document location">
      {to ? <Link to={to} title={segments.join(' / ')}>{contents}</Link> : contents}
    </nav>
  );
}



function PreviewLoadState({ title, detail, buttonLabel = '', onLoad = null, icon = 'pi pi-file-pdf' }) {
  return (
    <div className="documents-archive-preview-load">
      <AppIcon icon={icon} aria-hidden="true" />
      <strong>{title}</strong>
      <span>{detail}</span>
      {buttonLabel && <Button label={buttonLabel} icon="pi pi-eye" severity="secondary" onClick={onLoad} />}
    </div>
  );
}



function comparisonStatusLabel(status) {
  if (status === 'ready') return 'Ready';
  if (status === 'failed') return 'Failed';
  if (status === 'running') return 'Building';
  if (status === 'queued') return 'Queued';
  if (status === 'not_started') return 'Not generated';
  if (status === 'unsupported') return 'DOCX only';
  return 'Not started';
}



function changeStatusClass(status) {
  if (status === 'ready') return 'is-ready';
  if (status === 'queued' || status === 'running') return 'is-building';
  if (status === 'failed') return 'is-failed';
  if (status === 'unsupported') return 'is-unsupported';
  return 'is-idle';
}



function defaultCompareSelection(versions) {
  if (!versions.length) return { baseRevisionId: '', targetRevisionId: '' };
  const latestIndex = Math.max(versions.findIndex((version) => version.is_latest), 0);
  const target = versions[latestIndex] || versions[versions.length - 1];
  const base = versions[Math.max(0, latestIndex - 1)] || versions.find((version) => version.revision_id !== target?.revision_id);
  return {
    baseRevisionId: base?.revision_id || '',
    targetRevisionId: target?.revision_id || '',
  };
}



function versionById(versions, revisionId) {
  return versions.find((version) => version.revision_id === revisionId) || null;
}



function documentsRouteState(pathname) {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/documents') {
    return { applies: true, documentId: '' };
  }

  const match = normalizedPath.match(/^\/documents\/([^/]+)$/);
  if (!match) {
    return { applies: false, documentId: '' };
  }

  try {
    return { applies: true, documentId: decodeURIComponent(match[1]) };
  } catch (_err) {
    return { applies: true, documentId: match[1] };
  }
}



function documentDetailPath(documentId) {
  return `/documents/${encodeURIComponent(documentId)}`;
}



function documentVersionUrl(documentId, version) {
  if (!documentId || !version?.revision_id) return '';
  return new URL(`${documentDetailPath(documentId)}?version=${encodeURIComponent(version.revision_id)}`, window.location.origin).toString();
}



function shortRevision(revisionId) {
  const value = String(revisionId || '');
  return value.length > 12 ? `${value.slice(0, 12)}...` : value || '-';
}



function branchName(version) {
  return String(version?.branch_name || 'main').trim() || 'main';
}



function mergeForkBase(versions, source) {
  if (!source || branchName(source) === 'main') return null;
  const versionsByRevision = new Map(versions.map((version) => [version.revision_id, version]));
  const visited = new Set();
  let current = source;
  while (current?.revision_id && !visited.has(current.revision_id)) {
    visited.add(current.revision_id);
    if (current.forked_from_revision_id) {
      return versionsByRevision.get(current.forked_from_revision_id) || null;
    }
    const parent = versionsByRevision.get(current.parent_revision_id);
    if (!parent) return null;
    if (branchName(parent) !== branchName(current)) return parent;
    current = parent;
  }
  return null;
}



function versionDescendsFrom(versions, version, ancestorRevisionId) {
  const versionsByRevision = new Map(versions.map((candidate) => [candidate.revision_id, candidate]));
  const visited = new Set();
  let current = version;
  while (current?.revision_id && !visited.has(current.revision_id)) {
    if (current.revision_id === ancestorRevisionId) return true;
    visited.add(current.revision_id);
    current = versionsByRevision.get(current.parent_revision_id);
  }
  return false;
}



function mergePathState(versions, source, target) {
  const forkBase = mergeForkBase(versions, source);
  if (!forkBase || !target || !versionDescendsFrom(versions, target, forkBase.revision_id)) {
    return { mergeable: false, currentDiverged: false };
  }
  const changedFromFork =

  (version) => (
    version.content_sha256 && forkBase.content_sha256 ?
      version.content_sha256 !== forkBase.content_sha256 :
      version.revision_id !== forkBase.revision_id
  );
  return {
    mergeable: changedFromFork(source),
    currentDiverged: changedFromFork(target),
  };
}



function versionSourceLabel(version) {
  const provenance = String(version?.provenance || '').toLowerCase();
  if (provenance === 'ai_edit_adopted') return 'AI-assisted edit';
  if (provenance.includes('word')) return 'Microsoft Word edit';
  if (provenance.includes('upload')) return 'DOCX upload';
  if (provenance.includes('merge')) return 'Merged draft';
  if (provenance.includes('revert')) return 'Promoted version';
  if (provenance.includes('fork')) return 'Separate draft';
  if (provenance.includes('created')) return 'Created here';
  return 'Imported version';
}



function isAiAssistedVersion(version) {
  return String(version?.provenance || '').toLowerCase() === 'ai_edit_adopted';
}



function aiAssistanceForVersion(version) {
  if (!isAiAssistedVersion(version)) return null;
  const stored = version?.ai_assistance || {};
  const fallbackActor = {
    identifier: version?.created_by || '',
    name: version?.created_by_name || version?.created_by_display || '',
    email: version?.created_by_email || '',
    user_id: version?.created_by_user_id || '',
    display_name: version?.created_by_display || version?.created_by || 'Unknown user',
  };
  return {
    requested_by: stored.requested_by || fallbackActor,
    accepted_by: stored.accepted_by || fallbackActor,
    requested_at: stored.requested_at || version?.created_at || '',
    accepted_at: stored.accepted_at || version?.created_at || '',
    parent_revision_id: stored.parent_revision_id || version?.parent_revision_id || '',
    result_revision_id: stored.result_revision_id || version?.revision_id || '',
  };
}



function AiAssistedSignifier() {
  return (
    <span className="documents-ai-assisted-signifier">
      <AppIcon icon="pi pi-sparkles" aria-hidden="true" />
      AI-assisted
    </span>
  );
}



function lockedBy(version) {
  const status = String(version?.lock_status || '').trim().toLowerCase();
  const statusIsActive = ['active', 'locked', 'true'].includes(status);
  if (!version?.locked && !statusIsActive) return '';
  return String(version.locked_by_display || version.locked_by || version.lock_status || 'Unknown').trim();
}



function actorDisplay(actor, fallback = 'Unknown user') {
  if (!actor) return fallback;
  if (typeof actor === 'string') return actor || fallback;
  return actor.display_name || actor.name || actor.email || actor.identifier || fallback;
}



function actorInitials(actor) {
  const display = actorDisplay(actor, '?');
  const parts = display.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return display.slice(0, 2).toUpperCase();
}



function activityIcon(eventType) {
  if (eventType === 'ai_edit_adopted') return 'pi pi-sparkles';
  if (eventType.includes('lock')) return 'pi pi-lock';
  if (eventType.includes('word')) return 'pi pi-file-word';
  if (eventType.includes('merge')) return 'pi pi-code-branch';
  if (eventType.includes('upload')) return 'pi pi-upload';
  if (eventType.includes('fork')) return 'pi pi-share-alt';
  return 'pi pi-file-edit';
}



function isReadyChange(change) {
  return change?.status === 'ready' || change?.comparison?.status === 'ready';
}



function comparisonMatchesRevisionPair(comparison, firstRevisionId, secondRevisionId) {
  if (!comparison || !firstRevisionId || !secondRevisionId) return false;
  const baseRevisionId = comparison.base_revision_id || '';
  const targetRevisionId = comparison.target_revision_id || '';
  return (
    (baseRevisionId === firstRevisionId && targetRevisionId === secondRevisionId) ||
    (baseRevisionId === secondRevisionId && targetRevisionId === firstRevisionId)
  );
}



function changeMatchesRevisionPair(change, firstRevisionId, secondRevisionId) {
  return (
    comparisonMatchesRevisionPair(change, firstRevisionId, secondRevisionId) ||
    comparisonMatchesRevisionPair(change?.comparison, firstRevisionId, secondRevisionId)
  );
}



function readyComparisonMatchesMerge(comparison, sourceRevisionId, targetRevisionId) {
  return Boolean(
    comparison?.comparison_id &&
    comparison.status === 'ready' &&
    comparison.has_changes !== false &&
    comparison.base_revision_id === targetRevisionId &&
    comparison.target_revision_id === sourceRevisionId
  );
}



function sortedGraphVersions(versions) {
  return versions
    .filter((version) => version.provenance !== 'ai_edit_proposal')
    .sort(

    (left, right) => {
    const leftIndex = Number(left.version_index || 0);
    const rightIndex = Number(right.version_index || 0);
    if (leftIndex !== rightIndex) return rightIndex - leftIndex;
    return String(right.created_at || '').localeCompare(String(left.created_at || ''));
  });
}



function ownerEditEntries(versions) {
  return versions.reduce(

  (entries, version) => {
    const ownerEditId = version.is_mini_version ? String(version.owner_edit_id || '') : '';
    const previous = entries[entries.length - 1];
    if (ownerEditId && previous?.type === 'owner-edit' && previous.ownerEditId === ownerEditId) {
      previous.versions.push(version);
      return entries;
    }
    if (ownerEditId) {
      entries.push({ type: 'owner-edit', ownerEditId, versions: [version] });
    } else {
      entries.push({ type: 'version', version });
    }
    return entries;
  }, []);
}



function ownerEditGraphRows(entries, expandedOwnerEdits) {
  return entries.flatMap(

  (entry) => {
    if (entry.type === 'version') {
      return [{
        type: 'version',
        key: entry.version.revision_id,
        version: entry.version,
        graphVersion: entry.version,
        representedRevisionIds: [entry.version.revision_id],
      }];
    }

    const newest = entry.versions[0];
    const oldest = entry.versions[entry.versions.length - 1];
    const expanded = expandedOwnerEdits.has(entry.ownerEditId);
    const groupRevisionId = `owner-edit:${entry.ownerEditId}`;
    const groupGraphVersion = {
      ...newest,
      revision_id: groupRevisionId,
      parent_revision_id: expanded ? newest.revision_id : oldest.parent_revision_id,
      forked_from_revision_id: '',
      merged_into_revision_id: '',
    };
    const groupRow = {
      type: 'owner-edit',
      key: groupRevisionId,
      ownerEditId: entry.ownerEditId,
      versions: entry.versions,
      graphVersion: groupGraphVersion,
      representedRevisionIds: entry.versions.map((version) => version.revision_id),
    };
    if (!expanded) return [groupRow];
    return [
      groupRow,
      ...entry.versions.map((version) => ({
        type: 'mini-version',
        key: version.revision_id,
        version,
        ownerEditId: entry.ownerEditId,
        graphVersion: version,
        representedRevisionIds: [version.revision_id],
      })),
    ];
  });
}

const VERSION_GRAPH_ROW_HEIGHT = 62;
const VERSION_GRAPH_LANE_GAP = 15;
const VERSION_GRAPH_LANE_OFFSET = 10;
const VERSION_GRAPH_COLORS = ['#7ea38a', '#d8a93d', '#4f9daa', '#9b7ac2', '#c56b78'];



function versionGraphTopology(graphRows, branchNames) {
  const graphVersions = graphRows.map((row) => row.graphVersion);
  const revisionIndex = new Map(graphVersions.map((version, index) => [version.revision_id, index]));
  const laneForVersion =

  (version) => Math.max(0, branchNames.indexOf(branchName(version)));
  const pointFor =

  (version, index) => ({
    x: VERSION_GRAPH_LANE_OFFSET + laneForVersion(version) * VERSION_GRAPH_LANE_GAP,
    y: index * VERSION_GRAPH_ROW_HEIGHT + (VERSION_GRAPH_ROW_HEIGHT / 2),
  });
  const edges = [];
  const edgeKeys = new Set();

  const addEdge =

  (fromIndex, toIndex, lane, kind = 'history') => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
    const key = `${fromIndex}:${toIndex}:${kind}`;
    if (edgeKeys.has(key)) return;
    edgeKeys.add(key);
    const from = pointFor(graphVersions[fromIndex], fromIndex);
    const to = pointFor(graphVersions[toIndex], toIndex);
    const middleY = (from.y + to.y) / 2;
    const path = from.x === to.x ?
      `M ${from.x} ${from.y} L ${to.x} ${to.y}` :
      `M ${from.x} ${from.y} C ${from.x} ${middleY}, ${to.x} ${middleY}, ${to.x} ${to.y}`;
    edges.push({ key, path, lane, kind });
  };

  graphVersions.forEach(

  (version, index) => {
    const explicitParentId = version.parent_revision_id || version.forked_from_revision_id || '';
    let parentIndex = revisionIndex.has(explicitParentId) ? revisionIndex.get(explicitParentId) : -1;
    if (parentIndex < 0) {
      parentIndex = graphVersions.findIndex((candidate, candidateIndex) => (
        candidateIndex > index && branchName(candidate) === branchName(version)
      ));
    }
    addEdge(index, parentIndex, laneForVersion(version));

    const mergeIndex = revisionIndex.has(version.merged_into_revision_id) ?
      revisionIndex.get(version.merged_into_revision_id) : -1;
    addEdge(index, mergeIndex, laneForVersion(version), 'merge');
  });

  return {
    edges,
    points: graphVersions.map((version, index) => ({
      ...pointFor(version, index),
      lane: laneForVersion(version),
      revisionId: version.revision_id,
      representedRevisionIds: graphRows[index].representedRevisionIds,
    })),
    width: Math.max(34, VERSION_GRAPH_LANE_OFFSET * 2 + (branchNames.length - 1) * VERSION_GRAPH_LANE_GAP),
    height: graphVersions.length * VERSION_GRAPH_ROW_HEIGHT,
  };
}



function archiveSafetyState(document) {
  const health = document?.archive_health || document?.archiveHealth || null;
  const state = String(health?.state || document?.archive_health_state || '').trim();
  // Search results and lightweight listings may contain only some versions.
  // Only the server's complete snapshot can establish archive completeness.
  const expectedVersions = Number(health?.expected_versions ?? 0);
  const archivedVersions = Number(health?.archived_versions ?? 0);
  const failedVersions = Number(health?.failed_versions ?? 0);
  const firstReason = Array.isArray(health?.reasons) && health.reasons.length ? health.reasons[0] : '';
  const localCount = expectedVersions > 0 ?
    `${archivedVersions} of ${expectedVersions} versions local` :
    'Version completeness has not been reported yet';

  if (state === 'fully_archived') {
    return { label: 'Protected locally', tone: 'archive-protected', detail: localCount };
  }
  if (state === 'failed' || failedVersions > 0 || document?.archive_status === 'failed') {
    return { label: 'Archive failed', tone: 'archive-failed', detail: firstReason || 'Recover missing versions from a local backup.' };
  }
  if (state === 'partially_archived') {
    return { label: 'Partially archived', tone: 'archive-partial', detail: firstReason || localCount };
  }
  if (state === 'discovered_not_archived') {
    return { label: 'Not archived', tone: 'archive-dependent', detail: firstReason || 'No local version objects are recorded yet.' };
  }
  if (document?.archive_status === 'archived') {
    return {
      label: 'Archive recorded',
      tone: 'archive-unknown',
      detail: 'This document is recorded in the archive; version completeness is not reported.',
    };
  }
  return {
    label: 'Archive unknown',
    tone: 'archive-unknown',
    detail: firstReason || 'Local archive completeness is not reported.',
  };
}



function versionSummary(document, versions = []) {
  const count = Number(document?.version_count || versions.length || 0);
  return count === 1 ? '1 version' : `${count} versions`;
}



function ArchiveSafetyBadge({ state }) {
  return (
    <span className={`documents-workspace-state documents-workspace-state--${state.tone}`} title={state.detail}>
      {state.label}
    </span>
  );
}



function WorkflowActionButton({ icon, label, disabled, title, onClick, tone = 'neutral' }) {
  return (
    <button
      type="button"
      className={`documents-vcs-action documents-vcs-action--${tone}`}
      disabled={disabled}
      title={title}
      onClick={onClick}>
      <AppIcon icon={icon} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}



function EditableDocumentTitle({ document, onRename }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const filename = document?.filename || 'Untitled document';
  const canRename = document?.capabilities?.can_rename !== false && Boolean(onRename);
  if (!editing) return (
    <div className="documents-document-title">
      <h1 title={filename}>{filename}</h1>
      {canRename && <button type="button" aria-label="Rename document" title="Rename document" onClick={() => {
        setName(document?.filename || '');
        setError('');
        setEditing(true);
      }}><AppIcon icon="pi pi-pencil" aria-hidden="true" /></button>}
    </div>
  );
  return (
    <form className="documents-document-title-form" onSubmit={

    async (event) => {
      event.preventDefault();
      if (saving || !name.trim()) return;
      setSaving(true);
      setError('');
      try {
        await onRename(name.trim());
        setEditing(false);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Could not rename document.');
      } finally {
        setSaving(false);
      }
    }}>
      <div className="documents-document-title">
        <input aria-label="Document name" value={name} maxLength={255} required autoFocus disabled={saving}
          onFocus={(event) => event.target.select()}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !saving) { event.preventDefault(); setEditing(false); }
          }} />
        <button type="submit" aria-label="Save document name" title="Save document name" disabled={saving || !name.trim()}>
          <AppIcon icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} aria-hidden="true" />
        </button>
        <button type="button" aria-label="Cancel rename" title="Cancel rename" disabled={saving} onClick={() => setEditing(false)}>
          <AppIcon icon="pi pi-times" aria-hidden="true" />
        </button>
      </div>
      {error && <span className="documents-document-title-error" role="alert">{error}</span>}
    </form>
  );
}



function VersionControlCommandBar({
  selectedDocument,
  destinationSegments,
  archiveState,
  previewVersion,
  comparisonBase,
  comparisonTarget,
  comparisonRenderer,
  activePreviewMode,
  redlineAvailable,
  changesLoading,
  onBackToRegistry,
  onMoveDocument,
  onRenameDocument,
  onOpenShare,
  onShareLatest,
  onSetPreviewMode,
  onComparisonRendererChange,
  onOpenAi,
  aiAvailable,
  aiExpanded,
  aiReview,
  onOpenUpload,
  onOpenMerge,
  onPrepareMergeReview,
  canUpload,
  canMerge,
  mergeReplacesCurrent,
  canPrepareMergeReview,
  mergeReviewStatus,
  onEditWord,
  editStarting,
  writeSubmitting,
}) {
  const lockOwner = lockedBy(previewVersion);
  const mergeReviewInProgress = ['Queued', 'Building'].includes(mergeReviewStatus);
  const fallbackDestination = selectedDocument?.matter_number
    ? [`Matter ${selectedDocument.matter_number}`, ...normalizeFolderPath(selectedDocument.folder_path).split('/').filter(Boolean)]
    : selectedDocument?.folder_path
      ? ['Internal', ...normalizeFolderPath(selectedDocument.folder_path).split('/').filter(Boolean)]
      : ['Location not selected'];
  const documentDestination = destinationSegments?.length ? destinationSegments : fallbackDestination;
  const versionContext = activePreviewMode === 'redline' && comparisonBase && comparisonTarget
    ? `Comparing ${versionLabel(comparisonTarget)} to ${versionLabel(comparisonBase)}`
    : previewVersion ? versionLabel(previewVersion) : 'No version selected';
  const aiRun = aiReview?.run || null;
  const aiAction = aiReview?.action || '';

  return (
    <div className="documents-document-toolbar" aria-label="Version control actions">
      <button
        type="button"
        className="documents-document-toolbar__back"
        aria-label="All documents"
        title="All documents"
        onClick={onBackToRegistry}>
        <AppIcon icon="pi pi-arrow-left" aria-hidden="true" />
      </button>

      <div className="documents-document-toolbar__identity">
        <EditableDocumentTitle key={selectedDocument?.document_id} document={selectedDocument} onRename={onRenameDocument} />
        <div>
          <DocumentLocationBreadcrumb segments={documentDestination} to={documentLocationPath(selectedDocument)} />
          <span>{versionContext}</span>
        </div>
      </div>

      <div className="documents-document-toolbar__view-switch" role="tablist" aria-label="Preview type">
        <button
          type="button"
          className={activePreviewMode === 'version' ? 'is-active' : ''}
          aria-pressed={activePreviewMode === 'version'}
          onClick={() => onSetPreviewMode('version')}>
          <AppIcon icon="pi pi-file" aria-hidden="true" />
          Document
        </button>
        <button
          type="button"
          className={activePreviewMode === 'redline' ? 'is-active' : ''}
          aria-pressed={activePreviewMode === 'redline'}
          disabled={!redlineAvailable}
          onClick={() => onSetPreviewMode('redline')}>
          <AppIcon icon="pi pi-file-edit" aria-hidden="true" />
          Changes
        </button>
      </div>

      <label className="documents-document-toolbar__renderer" title="Choose the engine used to build redlines">
        <AppIcon icon="pi pi-sliders-h" aria-hidden="true" />
        <select
          aria-label="Comparison renderer"
          value={comparisonRenderer}
          onChange={(event) => onComparisonRendererChange(event.target.value)}>
          <option value="libreoffice">LibreOffice</option>
          <option value="docxodus">Docxodus</option>
        </select>
      </label>

      {aiRun ? (
        <div className="documents-document-toolbar__ai-review" role="group" aria-label="AI change review">
          <span><AppIcon icon="pi pi-sparkles" aria-hidden="true" />AI edits</span>
          <button
            type="button"
            className="is-reject"
            disabled={Boolean(aiAction)}
            onClick={aiReview.reject}>
            <AppIcon icon={aiAction === 'discard' ? 'pi pi-spin pi-spinner' : 'pi pi-times'} aria-hidden="true" />
            Reject proposal
          </button>
          <button
            type="button"
            className="is-accept"
            disabled={!aiRun.can_adopt || Boolean(aiAction)}
            title={aiRun.can_adopt ? 'Accept edits and save an AI checkpoint' : 'Proposal is not ready yet'}
            onClick={aiReview.accept}>
            <AppIcon icon={aiAction === 'adopt' ? 'pi pi-spin pi-spinner' : 'pi pi-check'} aria-hidden="true" />
            {aiAction === 'adopt' ? 'Saving' : 'Accept'}
          </button>
        </div>
      ) : null}

      <div className="documents-document-toolbar__actions">
        <WorkflowActionButton
          icon="pi pi-link"
          label="Link to Latest"
          title="Copy a link that opens the latest version for people who already have access."
          disabled={!selectedDocument?.document_id}
          onClick={onShareLatest} />
        {aiAvailable && (
          <WorkflowActionButton
            icon="pi pi-file-edit"
            label="Edit with AI"
            title="Propose document edits with AI."
            onClick={onOpenAi}
            tone={aiExpanded ? 'primary' : 'neutral'} />
        )}
        {lockOwner && (
          <span className="documents-vcs-lock-chip is-locked">
            <AppIcon icon="pi pi-lock" aria-hidden="true" />
            Locked by {lockOwner}
          </span>
        )}
        <WorkflowActionButton
          icon={editStarting ? 'pi pi-spin pi-spinner' : 'pi pi-file-word'}
          label={editStarting ? 'Opening Word' : 'Edit in Word'}
          disabled={!isDocxVersion(previewVersion) || Boolean(lockOwner) || editStarting || writeSubmitting}
          title={!isDocxVersion(previewVersion) ? 'Live Word editing requires DOCX. Download this file to edit it in Word, then upload the revised file.' : lockOwner ? 'This version is already open in Word. Unlock it only to recover a stale session.' : 'Open this version in Microsoft Word. Repeated saves become one version when Word closes.'}
          onClick={onEditWord}
          tone="primary" />
        {canMerge ? (
          <WorkflowActionButton
            icon="pi pi-sitemap"
            label={mergeReplacesCurrent ? 'Replace current' : 'Merge'}
            disabled={writeSubmitting}
            title={mergeReplacesCurrent ?
              'Replace the independently changed current version with the reviewed draft redline.' :
              'Merge the selected draft as a clean current version.'}
            onClick={onOpenMerge} />
        ) : canPrepareMergeReview ? (
          <WorkflowActionButton
            icon={(changesLoading || mergeReviewInProgress) ? 'pi pi-spin pi-spinner' : 'pi pi-file-edit'}
            label={mergeReviewInProgress ? mergeReviewStatus : 'Review changes'}
            disabled={changesLoading || mergeReviewInProgress || writeSubmitting}
            title="Compare this draft with the current version before merging."
            onClick={onPrepareMergeReview} />
        ) : null}
        <details className="documents-document-toolbar__more">
          <summary aria-label="More document actions" title="More document actions">
            <AppIcon icon="pi pi-ellipsis-v" aria-hidden="true" />
          </summary>
          <div>
            <button type="button" onClick={onOpenShare}>
              <AppIcon icon="pi pi-share-alt" aria-hidden="true" />
              Sharing settings
            </button>
            <button type="button" onClick={onMoveDocument}>
              <AppIcon icon="pi pi-folder" aria-hidden="true" />
              Move
            </button>
            <button type="button" disabled={!canUpload || writeSubmitting} onClick={onOpenUpload}>
              <AppIcon icon="pi pi-upload" aria-hidden="true" />
              Upload draft
            </button>
            {previewVersion?.revision_id && (
              <a
                href={apiUrl(`/simul/archive/versions/${encodeURIComponent(previewVersion.revision_id)}/download`)}
                target="_blank"
                rel="noreferrer">
                <AppIcon icon="pi pi-download" aria-hidden="true" />
                {downloadLabel(previewVersion, selectedDocument?.filename)}
              </a>
            )}
            <span className="documents-document-toolbar__archive">
              <ArchiveSafetyBadge state={archiveState} />
            </span>
          </div>
        </details>
      </div>
    </div>
  );
}



function NewDocumentModal({
  visible,
  form,
  file,
  templates,
  matters,
  matterNumbers,
  folders,
  mattersLoading,
  submitting,
  error,
  onClose,
  onChange,
  onFileChange,
  onSubmit,
}) {
  if (!visible) return null;
  const mode = form.mode || 'blank';
  const needsFile = mode === 'upload';
  const needsTemplate = mode === 'template';
  const destination = creationDestination(form, matters, folders);
  const knownMatters = new Map();
  matters.forEach((matter) => {
    const number = String(matter?.MatterNumber || matter?.matter_number || '').trim();
    if (number) knownMatters.set(number, matter);
  });
  matterNumbers.forEach((matterNumber) => {
    const number = String(matterNumber || '').trim();
    if (number && !knownMatters.has(number)) knownMatters.set(number, { MatterNumber: number });
  });
  folders.forEach((folder) => {
    const number = String(folder?.matter_number || '').trim();
    if (number && !knownMatters.has(number)) knownMatters.set(number, { MatterNumber: number });
  });
  const matterOptions = [...knownMatters.values()].sort((left, right) => (
    matterChoiceLabel(left).localeCompare(matterChoiceLabel(right))
  ));
  const matterFolderOptions = new Map([['', 'Matter folder']]);
  folders
    .filter((folder) => String(folder.matter_number || '').trim() === destination.matter_number)
    .forEach((folder) => {
      const path = folderCreationPath(folder, folders);
      if (path) matterFolderOptions.set(path.toLowerCase(), path);
    });
  if (form.folder_path) {
    const selectedPath = normalizeFolderPath(form.folder_path);
    matterFolderOptions.set(selectedPath.toLowerCase(), selectedPath);
  }
  const internalFolders = new Map([['firm documents', 'Firm documents']]);
  folders
    .filter((folder) => !String(folder.matter_number || '').trim())
    .forEach((folder) => {
      const path = folderCreationPath(folder, folders);
      if (path) internalFolders.set(path.toLowerCase(), path);
    });
  const disabled = submitting || !String(form.title || '').trim() ||
    (form.destination_kind !== 'internal' && !destination.matter_number) ||
    (form.destination_kind === 'internal' && !destination.folder_path) ||
    (needsFile && (!file || !isWordDocumentFilename(file.name))) ||
    (needsTemplate && !form.template_id);
  return (
    <div
      className="documents-vcs-modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      <form
        className="documents-vcs-modal documents-new-document-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="documents-new-title"
        onSubmit={(event) => {
          event.preventDefault();
          if (!disabled) onSubmit();
        }}>
        <header className="documents-new-document-modal__header">
          <span aria-hidden="true"><AppIcon icon="pi pi-file-edit" /></span>
          <div>
            <h2 id="documents-new-title">New Document</h2>
            <p>Choose where it belongs before you start.</p>
          </div>
          <button type="button" aria-label="Close new document" onClick={onClose}><AppIcon icon="pi pi-times" /></button>
        </header>

        <fieldset className="documents-new-document-modal__save-in">
          <legend>Save in</legend>
          <div className="documents-new-document-modal__destination-kinds" role="radiogroup" aria-label="Save in">
            <button
              type="button"
              role="radio"
              aria-checked={form.destination_kind !== 'internal'}
              className={form.destination_kind !== 'internal' ? 'is-selected' : ''}
              onClick={() => onChange({ destination_kind: 'matter', matter_number: '', folder_path: '' })}>
              <AppIcon icon="pi pi-briefcase" aria-hidden="true" />
              <span><strong>Matter</strong><small>Client case documents</small></span>
              <AppIcon icon="pi pi-check" aria-hidden="true" />
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={form.destination_kind === 'internal'}
              className={form.destination_kind === 'internal' ? 'is-selected' : ''}
              onClick={() => onChange({ destination_kind: 'internal', matter_number: '', folder_path: 'Firm documents' })}>
              <AppIcon icon="pi pi-building" aria-hidden="true" />
              <span><strong>Internal / no matter</strong><small>Firm-wide documents</small></span>
              <AppIcon icon="pi pi-check" aria-hidden="true" />
            </button>
          </div>

          {form.destination_kind === 'internal' ? (
            <label>
              <span>Internal folder</span>
              <input
                list="documents-new-internal-folders"
                value={form.folder_path || ''}
                onChange={(event) => onChange({ folder_path: event.target.value })}
                placeholder="Firm documents" />
              <datalist id="documents-new-internal-folders">
                {[...internalFolders.values()].map((path) => <option key={path} value={path} />)}
              </datalist>
            </label>
          ) : (
            <>
              <label>
                <span>Matter</span>
                <select
                  aria-label="Matter destination"
                  value={form.matter_number || ''}
                  onChange={(event) => onChange({ matter_number: event.target.value, folder_path: '' })}>
                  <option value="">{mattersLoading ? 'Loading matters…' : 'Select a matter'}</option>
                  {matterOptions.map((matter) => {
                    const number = String(matter?.MatterNumber || matter?.matter_number || '').trim();
                    return <option key={number} value={number}>{matterChoiceLabel(matter)}</option>;
                  })}
                </select>
              </label>
              {destination.matter_number && (
                <label>
                  <span>Folder</span>
                  <select
                    aria-label="Folder destination"
                    value={normalizeFolderPath(form.folder_path)}
                    onChange={(event) => onChange({ folder_path: event.target.value })}>
                    {[...matterFolderOptions.values()].map((path) => (
                      <option key={path || 'matter-root'} value={path}>{path || 'Matter folder'}</option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}

          <div className="documents-new-document-modal__destination-preview" role="status">
            <span>Destination</span>
            <DocumentLocationBreadcrumb segments={destination.segments} />
            {destination.createsMatterFolder && (
              <small>The matter’s document folder will be created with this document.</small>
            )}
          </div>
        </fieldset>

        <div className="documents-new-document-modal__details">
          <label>
            <span>Start from</span>
            <select value={mode} onChange={(event) => onChange({ mode: event.target.value })}>
              <option value="blank">Blank document</option>
              <option value="template">Approved template</option>
              <option value="upload">Upload Word document</option>
            </select>
          </label>
          <label>
            <span>Title</span>
            <input value={form.title || ''} onChange={(event) => onChange({ title: event.target.value })} autoFocus />
          </label>
        </div>
        {needsTemplate && (
          <label>
            <span>Template</span>
            <select value={form.template_id || ''} onChange={(event) => onChange({ template_id: event.target.value })}>
              <option value="">Select a template</option>
              {templates.map((template) => <option key={template.template_id} value={template.template_id}>{template.title}</option>)}
            </select>
          </label>
        )}
        {needsFile && <WordDocumentUploadField file={file} onFileChange={onFileChange} disabled={submitting} />}
        {needsFile && file && !isWordDocumentFilename(file.name) && <div className="documents-archive-error" role="alert">{WORD_DOCUMENT_ONLY_MESSAGE}</div>}
        {error && <div className="documents-archive-error" role="alert">{error}</div>}
        <footer>
          <button type="button" className="documents-archive-link-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="p-button p-component" disabled={disabled}>
            <span className="p-button-label">{submitting ? 'Creating' : 'Create Document'}</span>
          </button>
        </footer>
      </form>
    </div>
  );
}



function ArchiveWriteModal({
  modal,
  form,
  file,
  selectedVersion,
  latestVersion,
  selectedChange,
  redlineReady,
  mergeReplacesCurrent,
  submitting,
  error,
  onClose,
  onChange,
  onFileChange,
  onSubmit,
}) {
  if (!modal) return null;
  const type = modal.type;
  const uploadCreatesFork = type === 'upload' && Boolean(form.creates_fork);
  const editCreatesFork = type === 'edit' && Boolean(form.creates_fork);
  const title = {
    fork: 'Create Separate Draft',
    lock: 'Lock Version',
    unlock: 'Unlock Version',
    upload: uploadCreatesFork ? `Create Draft from ${versionLabel(selectedVersion)}` : 'Upload New Version',
    edit: `Edit ${versionLabel(selectedVersion)} in Word`,
    merge: mergeReplacesCurrent ? 'Replace Current with Reviewed Draft' : 'Merge As Current',
    revert: 'Promote to Current',
    metadata: `Name or Note ${versionLabel(selectedVersion)}`,
  }[type] || 'Document Action';
  const requiresReason = type === 'unlock' || type === 'revert';
  const requiresFile = type === 'upload';
  const submitDisabled = submitting ||
    (requiresReason && !String(form.reason || '').trim()) ||
    (requiresFile && !file) ||
    (uploadCreatesFork && !form.confirm) ||
    (editCreatesFork && !form.confirm) ||
    ((type === 'merge' || type === 'revert') && !form.confirm);

  return (
    <div
      className="documents-vcs-modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      <div className="documents-vcs-modal" role="dialog" aria-modal="true" aria-labelledby="documents-vcs-modal-title">
        <header>
          <h2 id="documents-vcs-modal-title">{title}</h2>
          <button type="button" aria-label="Close document action" onClick={onClose}>
            <AppIcon icon="pi pi-times" aria-hidden="true" />
          </button>
        </header>

        <div className="documents-vcs-modal-context">
          <span>Selected: {selectedVersion ? versionLabel(selectedVersion) : '-'}</span>
          <span>Current: {latestVersion ? versionLabel(latestVersion) : '-'}</span>
          {type === 'merge' && (
            <span>Redline: {redlineReady ? 'ready' : comparisonStatusLabel(selectedChange?.status)}</span>
          )}
        </div>

        {(type === 'fork' || uploadCreatesFork) && (
          <label>
            <span>Draft name</span>
            <input
              value={form.branch_name || ''}
              onChange={(event) => onChange({ branch_name: event.target.value })}
              placeholder="review-draft" />
          </label>
        )}

        {type === 'upload' && (
          <WordDocumentUploadField file={file} onFileChange={onFileChange} disabled={submitting} />
        )}

        {(uploadCreatesFork || editCreatesFork) && (
          <div className="documents-vcs-modal-warning" role="note">
            <AppIcon icon="pi pi-code-branch" aria-hidden="true" />
            <span>{versionLabel(selectedVersion)} is not the current version. Continuing will start a separate draft path.</span>
          </div>
        )}

        {type === 'merge' && mergeReplacesCurrent && (
          <div className="documents-vcs-modal-warning" role="note">
            <AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" />
            <span>Main changed after this draft started. This publishes the reviewed redline as current; tracked changes still need final acceptance in Word.</span>
          </div>
        )}

        {type === 'metadata' ? (
          <>
            <label>
              <span>Name (optional)</span>
              <input
                value={form.version_name || ''}
                maxLength={120}
                onChange={(event) => onChange({ version_name: event.target.value })}
                placeholder="Client-approved language" />
            </label>
            <label>
              <span>Note (optional)</span>
              <textarea
                value={form.version_note || ''}
                maxLength={2000}
                onChange={(event) => onChange({ version_note: event.target.value })}
                rows={4}
                placeholder="Why this version matters" />
            </label>
          </>
        ) : (
          <label>
            <span>{requiresReason ? 'Reason' : 'Reason (optional)'}</span>
            <textarea
              value={form.reason || ''}
              onChange={(event) => onChange({ reason: event.target.value })}
              rows={3}
              placeholder={requiresReason ? 'Required for audit history' : 'Optional audit note'} />
          </label>
        )}

        {type === 'fork' && (
          <label className="documents-vcs-modal-check">
            <input
              type="checkbox"
              checked={Boolean(form.lock_after_create)}
              onChange={(event) => onChange({ lock_after_create: event.target.checked })} />
            <span>Reserve the new draft for editing</span>
          </label>
        )}

        {type === 'merge' && (
          <label className="documents-vcs-modal-check">
            <input
              type="checkbox"
              checked={Boolean(form.confirm)}
              onChange={(event) => onChange({ confirm: event.target.checked })} />
            <span>{mergeReplacesCurrent ?
              'I confirm the reviewed draft should replace the independently changed current version' :
              'I confirm the selected draft should become the current version'}</span>
          </label>
        )}

        {type === 'revert' && (
          <label className="documents-vcs-modal-check">
            <input
              type="checkbox"
              checked={Boolean(form.confirm)}
              onChange={(event) => onChange({ confirm: event.target.checked })} />
            <span>Promote {versionLabel(selectedVersion)} by copying it into a new current version</span>
          </label>
        )}

        {(uploadCreatesFork || editCreatesFork) && (
          <label className="documents-vcs-modal-check">
            <input
              type="checkbox"
              checked={Boolean(form.confirm)}
              onChange={(event) => onChange({ confirm: event.target.checked })} />
            <span>Create a separate draft from {versionLabel(selectedVersion)}</span>
          </label>
        )}

        {type === 'upload' && file && !isWordDocumentFilename(file.name) && <div className="documents-archive-error" role="alert">{WORD_DOCUMENT_ONLY_MESSAGE}</div>}
        {error && <div className="documents-archive-error" role="alert">{error}</div>}

        <footer>
          <button type="button" className="documents-archive-link-button" onClick={onClose}>Cancel</button>
          <Button
            label={submitting ? 'Saving' : title}
            disabled={submitDisabled || (type === 'upload' && file && !isWordDocumentFilename(file.name))}
            onClick={onSubmit} />
        </footer>
      </div>
    </div>
  );
}



function VersionGraphPanel({
  versions,
  selectedVersionId,
  comparisonBaseId,
  comparisonTargetId,
  comparison,
  comparisonLoading,
  comparisonError,
  selectedDocument,
  onSelectVersion,
  baseRevisionId,
  targetRevisionId,
  canCompare,
  onBaseRevisionChange,
  onTargetRevisionChange,
  onStartComparison,
  onCompareVersions,
  onPromoteVersion,
  isVersionPromotable,
  onUnlockVersion,
  onShareVersion,
  onProofreadVersion,
  onEditMetadata,
  writeSubmitting,
  expanded,
  onExpand,
}) {
  const graphVersions = useMemo(() => sortedGraphVersions(versions), [versions]);
  const ownerEdits = useMemo(() => ownerEditEntries(graphVersions), [graphVersions]);
  const [expandedOwnerEdits, setExpandedOwnerEdits] = useState(() => new Set());
  const graphRows = useMemo(
    () => ownerEditGraphRows(ownerEdits, expandedOwnerEdits),
    [expandedOwnerEdits, ownerEdits],
  );
  const branchNames = ['main', ...new Set(
    graphRows.map((row) => branchName(row.graphVersion)).filter((name) => name !== 'main')
  )];
  const latestRevisionId = currentVersion(selectedDocument, graphVersions)?.revision_id || '';
  const hasComparisonPair = Boolean(
    comparisonBaseId && comparisonTargetId && comparisonBaseId !== comparisonTargetId
  );
  const comparisonBase = versionById(graphVersions, comparisonBaseId);
  const comparisonTarget = versionById(graphVersions, comparisonTargetId);
  const pairStatus = comparisonLoading ? 'Building' :
    comparisonError ? 'Failed' : comparisonStatusLabel(comparison?.status);
  const pairStatusClass = comparisonLoading ? 'is-building' :
    comparisonError ? 'is-failed' : changeStatusClass(comparison?.status);
  const topology = versionGraphTopology(graphRows, branchNames);
  const [contextMenu, setContextMenu] = useState(null);
  const versionMenuRef = useRef(null);

  useEffect(

  () => {
    if (!contextMenu) return undefined;
    versionMenuRef.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
    const closeMenu =

    () => setContextMenu(null);
    const handleKeyDown =

    (event) => {
      if (event.key === 'Escape') { closeMenu(); contextMenu.trigger?.focus(); }
    };
    window.addEventListener('pointerdown', closeMenu);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', closeMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  const comparisonPairFor =

  (version) => {
    const selected = versionById(graphVersions, selectedVersionId);
    let other = selected && selected.revision_id !== version.revision_id ? selected : null;
    if (!other) {
      const versionIndex = graphVersions.findIndex((item) => item.revision_id === version.revision_id);
      other = graphVersions[versionIndex + 1] || null;
    }
    if (!other) return null;
    const versionPosition = graphVersions.findIndex((item) => item.revision_id === version.revision_id);
    const otherPosition = graphVersions.findIndex((item) => item.revision_id === other.revision_id);
    return versionPosition < otherPosition ? { base: other, target: version } : { base: version, target: other };
  };

  const openVersionContextMenu =

  (event, version) => {
    const pair = comparisonPairFor(version);
    const canPromote = isVersionPromotable(version);
    const canUnlock = Boolean(lockedBy(version) && version.lock_id);
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      version,
      trigger: event.currentTarget,
      pair,
      canPromote,
      canUnlock,
      x: Math.min(event.clientX || event.currentTarget.getBoundingClientRect().left, Math.max(8, window.innerWidth - 248)),
      y: Math.min(event.clientY || event.currentTarget.getBoundingClientRect().bottom, Math.max(8, window.innerHeight - 310)),
    });
  };
  const openVersionMenuFromKey =

  (event, version) => {
    if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) openVersionContextMenu(event, version);
  };

  return (
    <section className={`documents-sidebar-accordion-section documents-vcs-graph-panel ${expanded ? 'is-expanded' : ''}`} aria-label="Version graph">
      <button
        type="button"
        className="documents-sidebar-accordion-trigger"
        aria-label="Versions"
        aria-expanded={expanded}
        aria-controls="documents-sidebar-versions"
        onClick={onExpand}>
        <div className="documents-archive-section-title">
          <h2>Versions</h2>
          <span>{graphVersions.length ? `${graphVersions.length} versions` : 'No versions'}</span>
        </div>
        <span className="documents-sidebar-accordion-action">
          <AppIcon icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden="true" />
        </span>
      </button>
      <div id="documents-sidebar-versions" className="documents-vcs-graph" hidden={!expanded}>
        {hasComparisonPair && (
          <div className="documents-vcs-comparison-pair" aria-label="Selected comparison" role="status">
            <span className="documents-vcs-comparison-pair-versions">
              <strong>{versionLabel(comparisonBase)}</strong>
              <AppIcon icon="pi pi-arrow-right" aria-hidden="true" />
              <strong>{versionLabel(comparisonTarget)}</strong>
            </span>
            <span className={`documents-vcs-comparison-pair-status ${pairStatusClass}`}>
              {pairStatus}
            </span>
          </div>
        )}
        <details className="documents-archive-custom-compare">
          <summary>Compare other versions</summary>
          <div className="documents-archive-compare-form">
            <label>
              <span>Older version</span>
              <select value={baseRevisionId} onChange={(event) => onBaseRevisionChange(event.target.value)}>
                {graphVersions.length === 0 && <option value="">No versions</option>}
                {graphVersions.map((version) => (
                  <option key={version.revision_id} value={version.revision_id}>
                    {versionLabel(version)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Newer version</span>
              <select value={targetRevisionId} onChange={(event) => onTargetRevisionChange(event.target.value)}>
                {graphVersions.length === 0 && <option value="">No versions</option>}
                {graphVersions.map((version) => (
                  <option key={version.revision_id} value={version.revision_id}>
                    {versionLabel(version)}
                  </option>
                ))}
              </select>
            </label>
            <Button
              label={comparisonLoading ? 'Comparing' : 'Compare'}
              icon={comparisonLoading ? 'pi pi-spin pi-spinner' : 'pi pi-file-edit'}
              disabled={!canCompare || comparisonLoading}
              onClick={onStartComparison} />
          </div>
        </details>
        {graphVersions.length === 0 ? (
          <div className="documents-archive-empty">No versions found.</div>
        ) : (
          <div
            className="documents-vcs-version-list"
            style={{
              '--documents-version-graph-width': `${topology.width}px`,
              '--documents-version-row-height': `${VERSION_GRAPH_ROW_HEIGHT}px`,
            }}>
            <svg
              className="documents-vcs-topology"
              viewBox={`0 0 ${topology.width} ${topology.height}`}
              width={topology.width}
              height={topology.height}
              role="img"
                  aria-label={`Version relationship graph: ${graphVersions.length} versions and ${topology.edges.length} ${topology.edges.length === 1 ? 'connection' : 'connections'}`}>
              {topology.edges.map((edge) => (
                <path
                  key={edge.key}
                  className={edge.kind === 'merge' ? 'is-merge' : ''}
                  d={edge.path}
                  style={{ '--documents-version-edge-color': VERSION_GRAPH_COLORS[edge.lane % VERSION_GRAPH_COLORS.length] }} />
              ))}
              {topology.points.map((point) => {
                const isCompared = point.representedRevisionIds.some((revisionId) => (
                  revisionId === comparisonBaseId || revisionId === comparisonTargetId
                ));
                const isActive = point.representedRevisionIds.includes(selectedVersionId) || isCompared;
                return (
                  <circle
                    key={point.revisionId}
                    className={isActive ? 'is-active' : ''}
                    cx={point.x}
                    cy={point.y}
                    r={isActive ? 4.3 : 3.5}
                    style={{ '--documents-version-edge-color': VERSION_GRAPH_COLORS[point.lane % VERSION_GRAPH_COLORS.length] }} />
                );
              })}
            </svg>
            {graphRows.map(

            (row) => {
          if (row.type === 'owner-edit') {
            const newest = row.versions[0];
            const ownerName = newest.created_by_display || newest.created_by || 'Unknown user';
            const isOwnerEditExpanded = expandedOwnerEdits.has(row.ownerEditId);
            const isOwnerEditSelected = row.representedRevisionIds.some((revisionId) => (
              revisionId === selectedVersionId ||
              revisionId === comparisonBaseId ||
              revisionId === comparisonTargetId
            ));
            const isOwnerEditHead = !isOwnerEditExpanded && row.representedRevisionIds.includes(latestRevisionId);
            const checkpointCount = row.versions.length;
            const newestAssistance = aiAssistanceForVersion(newest);
            const toggleOwnerEdit =

            () => setExpandedOwnerEdits((current) => {
              const next = new Set(current);
              if (next.has(row.ownerEditId)) next.delete(row.ownerEditId);
              else next.add(row.ownerEditId);
              return next;
            });

            return (
              <div
                key={row.key}
                className={`documents-vcs-version-row documents-vcs-owner-edit-row ${isOwnerEditSelected ? 'is-selected' : ''} ${isOwnerEditHead ? 'is-head' : ''}`}>
                <button
                  type="button"
                  className="documents-vcs-version-main"
                  onClick={toggleOwnerEdit}
                  onContextMenu={(event) => openVersionContextMenu(event, newest)}
                  onKeyDown={(event) => openVersionMenuFromKey(event, newest)}
                  aria-expanded={isOwnerEditExpanded}
                  aria-label={`${isOwnerEditExpanded ? 'Collapse' : 'Expand'} ${ownerName} edit with ${checkpointCount} AI ${checkpointCount === 1 ? 'checkpoint' : 'checkpoints'}`}>
                  <span className="documents-vcs-version-graph-space" aria-hidden="true" />
                  <span className="documents-vcs-version-copy">
                    <span className="documents-vcs-version-title">
                      <strong className="documents-vcs-version-label">AI edit</strong>
                      <AiAssistedSignifier />
                      {isOwnerEditHead && <em>Current</em>}
                    </span>
                    <span className="documents-vcs-version-meta">
                      <span title={newest.created_by_email || newest.created_by || '-'}>{ownerName}</span>
                      <span>{checkpointCount} saved {checkpointCount === 1 ? 'version' : 'versions'}</span>
                      <CompactCreatedCell value={newestAssistance?.accepted_at || newest.created_at} />
                    </span>
                  </span>
                </button>
                <div className="documents-vcs-version-actions">
                  <button
                    type="button"
                    aria-label={`${isOwnerEditExpanded ? 'Collapse' : 'Expand'} ${ownerName} edit`}
                    title={`${isOwnerEditExpanded ? 'Collapse' : 'Expand'} edit`}
                    onClick={toggleOwnerEdit}>
                    <AppIcon icon={isOwnerEditExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} size={16} weight="bold" aria-hidden="true" />
                  </button>
                </div>
              </div>
            );
          }

          const version = row.version;
          const isMiniVersion = row.type === 'mini-version';
          const lockOwner = lockedBy(version);
          const isComparisonBase = hasComparisonPair && version.revision_id === comparisonBaseId;
          const isComparisonTarget = hasComparisonPair && version.revision_id === comparisonTargetId;
          const isSelected = version.revision_id === selectedVersionId || isComparisonBase || isComparisonTarget;
          const isHead = version.revision_id === latestRevisionId;
          const rowLabel = isMiniVersion ? `AI checkpoint ${version.mini_version_index || ''}`.trim() : versionLabel(version);
          const assistance = aiAssistanceForVersion(version);
          const visibleLabel = isMiniVersion ? `Edit ${version.mini_version_index || ''}`.trim() : versionLabel(version);
          const authorName = version.created_by_display || version.created_by || '-';
          const detailLabel = isMiniVersion && version.checkpoint_summary ? version.checkpoint_summary : authorName;

          return (
            <div
              key={version.revision_id}
              onContextMenu={(event) => openVersionContextMenu(event, version)}
              className={`documents-vcs-version-row ${isMiniVersion ? 'is-mini-version' : ''} ${isSelected ? 'is-selected' : ''} ${isComparisonBase ? 'is-compare-base' : ''} ${isComparisonTarget ? 'is-compare-target' : ''} ${isHead ? 'is-head' : ''}`}>
              <button
                type="button"
                className="documents-vcs-version-main"
                onClick={() => onSelectVersion(version.revision_id)}
                onContextMenu={(event) => openVersionContextMenu(event, version)}
                onKeyDown={(event) => openVersionMenuFromKey(event, version)}
                title="View changes from the prior version"
                aria-pressed={isSelected}
                aria-label={`Open ${rowLabel}`}>
                <span className="documents-vcs-version-graph-space" aria-hidden="true" />
                <span className="documents-vcs-version-copy">
                  <span className="documents-vcs-version-title">
                    <strong className="documents-vcs-version-label">{visibleLabel}</strong>
                    {!isMiniVersion && version.version_name && (
                      <span className="documents-vcs-version-name-inline" title={version.version_name}>{version.version_name}</span>
                    )}
                    {assistance && !isMiniVersion && <AiAssistedSignifier />}
                    {isHead && <em>Current</em>}
                    {lockOwner && <em className="is-lock">Locked</em>}
                  </span>
                  <span className="documents-vcs-version-meta">
                    <span title={isMiniVersion ? detailLabel : (version.created_by_email || version.created_by || '-')}>{detailLabel}</span>
                    <CompactCreatedCell value={assistance?.accepted_at || version.created_at} />
                    <span>{formatSize(version.size)}</span>
                  </span>
                </span>
              </button>
              <div className="documents-vcs-version-actions">
                <button
                  type="button"
                  aria-label={`Copy link to ${rowLabel}`}
                  title={`Copy link to ${rowLabel}`}
                  onClick={() => onShareVersion(version)}>
                  <AppIcon icon="pi pi-link" size={16} weight="bold" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={`Name or note ${rowLabel}`}
                  title={`Name or note ${rowLabel}`}
                  onClick={() => onEditMetadata(version)}>
                  <AppIcon icon="pi pi-pencil" size={16} weight="bold" aria-hidden="true" />
                </button>
              </div>
            </div>
            );
            })}
          </div>
        )}
        {contextMenu && (
          <div
            className="documents-vcs-version-context-menu"
            ref={versionMenuRef}
            role="menu"
            aria-label={`${versionLabel(contextMenu.version)} actions`}
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={

            (event) => {
              if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const items = [...event.currentTarget.querySelectorAll('[role="menuitem"]:not(:disabled)')];
              const index = items.indexOf(document.activeElement);
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 :
                (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
              items[next]?.focus();
            }}>
            <button type="button" role="menuitem"
              disabled={!isDocxVersion(contextMenu.version) || !contextMenu.version.content_sha256}
              title={!isDocxVersion(contextMenu.version) ? 'Proofreading requires a DOCX version.' : !contextMenu.version.content_sha256 ? 'This version has not been archived yet.' : 'Review this saved version for errors.'}
              onClick={() => { onProofreadVersion(contextMenu.version); setContextMenu(null); }}>
              <AppIcon icon="pi pi-check-square" aria-hidden="true" />Proofread
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onShareVersion(contextMenu.version);
                setContextMenu(null);
              }}>
              <AppIcon icon="pi pi-link" aria-hidden="true" />
              Copy link
            </button>
            <a
              role="menuitem"
              href={apiUrl(`/simul/archive/versions/${encodeURIComponent(contextMenu.version.revision_id)}/download`)}
              target="_blank"
              rel="noreferrer"
              onClick={() => setContextMenu(null)}>
              <AppIcon icon="pi pi-download" aria-hidden="true" />
              Download {versionLabel(contextMenu.version)}
            </a>
            {contextMenu.pair && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onCompareVersions(contextMenu.pair.base.revision_id, contextMenu.pair.target.revision_id);
                  setContextMenu(null);
                }}>
                <AppIcon icon="pi pi-file-edit" aria-hidden="true" />
                Compare with {versionLabel(
                  contextMenu.pair.base.revision_id === contextMenu.version.revision_id ?
                    contextMenu.pair.target : contextMenu.pair.base
                )}
              </button>
            )}
            {contextMenu.canPromote && (
              <button
                type="button"
                role="menuitem"
                disabled={writeSubmitting}
                onClick={() => {
                  onPromoteVersion(contextMenu.version);
                  setContextMenu(null);
                }}>
                <AppIcon icon="pi pi-history" aria-hidden="true" />
                Promote to current
              </button>
            )}
            {contextMenu.canUnlock && (
              <button
                type="button"
                role="menuitem"
                disabled={writeSubmitting}
                onClick={() => {
                  onUnlockVersion(contextMenu.version);
                  setContextMenu(null);
                }}>
                <AppIcon icon="pi pi-lock-open" aria-hidden="true" />
                Unlock
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}



function ActivityPanel({ activity, versions, expanded, onExpand }) {
  return (
    <section className={`documents-sidebar-accordion-section documents-activity-panel ${expanded ? 'is-expanded' : ''}`} aria-label="Document activity">
      <button
        type="button"
        className="documents-sidebar-accordion-trigger"
        aria-label="Activity"
        aria-expanded={expanded}
        aria-controls="documents-sidebar-activity"
        onClick={onExpand}>
        <div className="documents-archive-section-title">
          <h2>Activity</h2>
          <span>{activity.length ? `${activity.length} events` : 'No activity'}</span>
        </div>
        <AppIcon icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden="true" />
      </button>
      <div id="documents-sidebar-activity" className="documents-activity-list" hidden={!expanded}>
        {activity.length === 0 ? (
          <div className="documents-archive-empty">No document activity has been recorded.</div>
        ) : activity.map(

        (item) => {
          const assistance = item.event_type === 'ai_edit_adopted'
            ? (item.metadata?.ai_assistance || {
                requested_by: item.actor,
                accepted_by: item.actor,
                accepted_at: item.created_at,
                parent_revision_id: '',
                result_revision_id: item.revision_id,
              })
            : null;
          const parentVersion = versionById(versions || [], assistance?.parent_revision_id);
          const resultVersion = versionById(versions || [], assistance?.result_revision_id || item.revision_id);
          return (
          <article className="documents-activity-item" key={item.activity_id}>
            <span className="documents-activity-avatar" title={actorDisplay(item.actor)}>
              {actorInitials(item.actor)}
            </span>
            <span className="documents-activity-icon" aria-hidden="true">
              <AppIcon icon={activityIcon(String(item.event_type || ''))} />
            </span>
            <span className="documents-activity-copy">
              <strong>{actorDisplay(item.actor)}</strong>
              <span className="documents-activity-action">
                {item.action || 'Updated document'}
                {assistance && <AiAssistedSignifier />}
              </span>
              {assistance && (
                <small>
                  Requested by {actorDisplay(assistance.requested_by)} · accepted by {actorDisplay(assistance.accepted_by)}
                </small>
              )}
              {assistance && (
                <small title={`${assistance.parent_revision_id || ''} → ${assistance.result_revision_id || item.revision_id || ''}`}>
                  Main {parentVersion ? versionLabel(parentVersion) : shortRevision(assistance.parent_revision_id)} → {resultVersion ? versionLabel(resultVersion) : shortRevision(assistance.result_revision_id || item.revision_id)}
                </small>
              )}
              {item.reason && <small>{item.reason}</small>}
            </span>
            <CreatedCell value={assistance?.accepted_at || item.created_at} />
          </article>
          );
        })}
      </div>
    </section>
  );
}



function DocumentPreviewSurface({
  activePreviewMode,
  previewVersion,
  documentFilename = '',
  redlineReady,
  redlineAvailable,
  comparison,
  comparisonRenderer,
  comparisonLoading = false,
  comparisonError,
  previewRequested,
  autoLoadPreview = false,
  condensedHeader = false,
  onRequestPreview,
  onRetryComparison = null,
  onSetPreviewMode,
  onComparisonRendererChange,
  className = '',
  commentAnchor = null,
}) {
  const shouldLoadPreview = previewRequested || autoLoadPreview;
  const versionPreviewReady = Boolean(previewVersion?.preview_ready || (
    previewVersion?.preview_status === 'ready' && previewVersion?.preview_object_key
  ));
  const versionPreviewFailed = previewVersion?.preview_status === 'failed';
  const [redlinePageMode, setRedlinePageMode] = useState('all');
  const [selectedChangePage, setSelectedChangePage] = useState(null);
  const changesOnlyAvailable = Boolean(comparison?.changes_pdf_object_key);
  const effectiveRedlinePageMode = changesOnlyAvailable && redlinePageMode === 'changes' ? 'changes' : 'all';
  const redlinePdfPath = effectiveRedlinePageMode === 'changes' ?
    `/simul/archive/comparisons/${encodeURIComponent(comparison?.comparison_id || '')}/pdf/changes` :
    `/simul/archive/comparisons/${encodeURIComponent(comparison?.comparison_id || '')}/pdf`;
  const changePageNumbers = [...new Set((comparison?.changed_page_numbers || [])
    .map((page) => Number(page))
    .filter((page) => Number.isInteger(page) && page > 0))]
    .sort((first, second) => first - second);
  const markerTypesByPage = new Map((comparison?.changed_page_markers || []).map((marker) => [
    Number(marker?.page_number),
    ['added', 'removed', 'moved', 'mixed', 'modified'].includes(marker?.change_type) ? marker.change_type : 'modified',
  ]));
  const changeMarkers = changePageNumbers.map((page) => ({
    page,
    type: markerTypesByPage.get(page) || 'mixed',
  }));
  const pageCount = Math.max(Number(comparison?.page_count) || 0, ...changePageNumbers, 1);
  const minimapLineCount = Math.min(96, Math.max(40, pageCount * 4));
  const selectedChangeIndex = changePageNumbers.indexOf(selectedChangePage);
  const activeChangePage = selectedChangePage ?? changePageNumbers[0] ?? null;
  const selectedPdfPage = selectedChangePage === null ? null : (
    effectiveRedlinePageMode === 'changes' ? selectedChangeIndex + 1 : selectedChangePage
  );
  const redlinePdfSrc = `${apiUrl(redlinePdfPath)}${selectedPdfPage ? `#page=${selectedPdfPage}` : ''}`;
  const commentsAdded = Number(comparison?.comment_changes?.added || 0);
  const commentsRemoved = Number(comparison?.comment_changes?.removed || 0);
  const noChanges = comparison?.has_changes === false;
  const comparisonFailed = Boolean(comparisonError || comparison?.status === 'failed');
  const comparisonStarting = Boolean(comparisonLoading && !comparison?.comparison_id);
  const comparisonBuilding = Boolean(
    comparison?.comparison_id && POLL_STATUSES.has(comparison?.status)
  );
  const showPreviewHeader = !condensedHeader || (activePreviewMode === 'redline' && redlineReady);

  useEffect(() => {
    setRedlinePageMode('all');
    setSelectedChangePage(null);
  }, [comparison?.comparison_id]);

  return (
    <div className={`documents-archive-preview ${className}`}>
      {showPreviewHeader && (
        <div className={`documents-archive-preview-header ${condensedHeader ? 'is-condensed' : ''}`}>
          {!condensedHeader && (
            <div className="documents-archive-preview-switch" role="tablist" aria-label="Preview type">
              <button
                type="button"
                className={activePreviewMode === 'version' ? 'is-active' : ''}
          aria-pressed={activePreviewMode === 'version'}
                onClick={() => onSetPreviewMode('version')}>
                <AppIcon icon="pi pi-file" aria-hidden="true" />
                <span>Document</span>
              </button>
              <button
                type="button"
                className={activePreviewMode === 'redline' ? 'is-active' : ''}
          aria-pressed={activePreviewMode === 'redline'}
                disabled={!redlineAvailable}
                onClick={() => onSetPreviewMode('redline')}>
                <AppIcon icon="pi pi-file-edit" aria-hidden="true" />
                <span>Changes</span>
              </button>
            </div>
          )}
        {activePreviewMode === 'redline' && redlineReady && (commentsAdded > 0 || commentsRemoved > 0) && (
          <div
            className="documents-archive-comment-changes"
            aria-label="Comment changes">
            {commentsRemoved > 0 && (
              <span className="is-removed">
                <AppIcon icon="pi pi-comments" aria-hidden="true" />
                {commentsRemoved} {commentsRemoved === 1 ? 'comment' : 'comments'} removed
              </span>
            )}
            {commentsAdded > 0 && (
              <span className="is-added">
                <AppIcon icon="pi pi-comments" aria-hidden="true" />
                {commentsAdded} {commentsAdded === 1 ? 'comment' : 'comments'} added
              </span>
            )}
          </div>
        )}
        {activePreviewMode === 'redline' && redlineReady && !noChanges && (
          <div className="documents-redline-legend" aria-label="Redline colors">
            <span className="is-added">Added</span>
            <span className="is-moved">Moved</span>
            <span className="is-removed">Deleted</span>
            <span className="is-modified">Formatting</span>
          </div>
        )}
        <div className="documents-archive-preview-actions">
          {!condensedHeader && activePreviewMode === 'version' && previewVersion && (
            <a
              href={apiUrl(`/simul/archive/versions/${encodeURIComponent(previewVersion.revision_id)}/download`)}
              target="_blank"
              rel="noreferrer">
              {downloadLabel(previewVersion, documentFilename)}
            </a>
          )}
          {activePreviewMode === 'redline' && redlineReady && !noChanges && (
            <>
              {changesOnlyAvailable && (
                <label className="documents-archive-page-filter">
                  <span>Pages</span>
                  <select value={redlinePageMode} onChange={(event) => setRedlinePageMode(event.target.value)}>
                    <option value="all">All pages</option>
                    <option value="changes">Only pages with changes ({comparison.changed_page_count})</option>
                  </select>
                </label>
              )}
              <a
                href={apiUrl(`/simul/archive/comparisons/${encodeURIComponent(comparison.comparison_id)}/docx`)}
                target="_blank"
                rel="noreferrer">
                DOCX
              </a>
              <a
                href={apiUrl(redlinePdfPath)}
                target="_blank"
                rel="noreferrer">
                PDF
              </a>
            </>
          )}
        </div>
        </div>
      )}
      {activePreviewMode === 'redline' && !redlineReady && !noChanges && (
        <div className="documents-comparison-progress" role="status">
          <AppIcon icon={comparisonFailed ? 'pi pi-exclamation-circle' : 'pi pi-spin pi-spinner'} aria-hidden="true" />
          <span>{comparisonFailed ? 'Redline needs attention' : comparisonStarting ? 'Starting redline' : comparisonBuilding ? 'Building redline' : 'Redline not started'}</span>
          {comparisonFailed ? (
            <>
              <span>{comparisonError || comparison?.error || 'The comparison could not be created.'}</span>
              {onRetryComparison && <button type="button" onClick={onRetryComparison}>Try Again</button>}
            </>
          ) : <span>Showing {versionLabel(previewVersion)} while changes are prepared.</span>}
        </div>
      )}
      {activePreviewMode === 'redline' && (redlineReady || noChanges) ? (
        noChanges ? (
          <PreviewLoadState
            icon="pi pi-check-circle"
            title="No differences found"
            detail="These two versions contain the same document content."
            buttonLabel="Show document"
            onLoad={() => onSetPreviewMode('version')} />
        ) : redlineReady ? (
          shouldLoadPreview ? (
            <div className="documents-redline-preview-body">
              <PdfDocumentViewer
                title="Redline PDF preview"
                src={redlinePdfSrc} />
              {changePageNumbers.length > 0 && (
                <nav className="documents-change-minimap" aria-label="Change minimap">
                  <div className="documents-change-minimap-track">
                    <div className="documents-change-minimap-lines" aria-hidden="true">
                      {Array.from({ length: minimapLineCount }, (_, index) => (
                        <span
                          key={index}
                          style={{
                            '--documents-minimap-line-position': `${2 + (index / (minimapLineCount - 1)) * 96}%`,
                            '--documents-minimap-line-width': `${48 + ((index * 17) % 40)}%`,
                          }} />
                      ))}
                    </div>
                    {changeMarkers.map(

                    (marker) => {
                      const position = pageCount <= 1 ? 2 : 2 + ((marker.page - 1) / (pageCount - 1)) * 96;
                      const markerWidth = 64 + ((marker.page * 19) % 32);
                      const markerLabel = {
                        added: 'addition',
                        removed: 'deletion',
                        moved: 'moved text',
                        mixed: 'added and removed changes',
                        modified: 'formatting change',
                      }[marker.type];
                      return (
                        <button
                          key={marker.page}
                          type="button"
                          className={`is-${marker.type} ${activeChangePage === marker.page ? 'is-active' : ''}`}
                          style={{
                            '--documents-change-position': `${position}%`,
                            '--documents-change-width': `${markerWidth}%`,
                          }}
                          aria-label={`Jump to ${markerLabel} on original page ${marker.page}`}
                          aria-pressed={activeChangePage === marker.page}
                          title={`${markerLabel.charAt(0).toUpperCase()}${markerLabel.slice(1)} · page ${marker.page}`}
                          onClick={() => setSelectedChangePage(marker.page)}>
                          <span aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </nav>
              )}
            </div>
          ) : (
            <PreviewLoadState
              title="Redline preview is ready"
              detail="Load the PDF only when you need to inspect the rendered comparison."
              buttonLabel="Load Redline Preview"
              onLoad={onRequestPreview} />
          )
        ) : null
      ) : previewVersion && !versionPreviewReady ? (
        <PreviewLoadState
          icon={versionPreviewFailed ? 'pi pi-exclamation-circle' : 'pi pi-spin pi-spinner'}
          title={versionPreviewFailed ? 'Preview needs attention' : 'Preparing document preview'}
          detail={versionPreviewFailed
            ? previewVersion.preview_error || 'The document details are available, but its PDF preview could not be prepared.'
            : 'Document details are ready. The PDF preview is being prepared in the background.'} />
      ) : previewVersion ? (
        shouldLoadPreview ? (
          <PdfDocumentViewer
            title="Archived version preview"
            anchor={commentAnchor}
            src={documentPreviewSource(previewVersion, 'word')} />
        ) : (
          <PreviewLoadState
            title={`${versionLabel(previewVersion)} is selected`}
            detail="Version details are ready. Load the PDF preview when you need the document rendering."
            buttonLabel="Load Version Preview"
            onLoad={onRequestPreview} />
        )
      ) : (
        <div className="documents-archive-empty">No version selected.</div>
      )}
    </div>
  );
}



function folderContext(folder) {
  if (folder?.folder_type === 'simul') return folder.matter_number ? `Matter ${folder.matter_number}` : 'Simul folder';
  if (folder?.folder_type === 'collection') return 'Simul collection';
  if (folder?.folder_type === 'matter') return 'Matter folder';
  if (folder?.matter_number) return `Matter ${folder.matter_number}`;
  return 'Internal folder';
}



function isMatterFolder(folder) {
  return folder?.folder_type === 'matter' || Boolean(String(folder?.matter_number || '').trim());
}



function FolderPagination({ label, page, total, onPageChange }) {
  const pageCount = Math.max(1, Math.ceil(total / FOLDER_PAGE_SIZE));
  if (pageCount <= 1) return null;
  const start = page * FOLDER_PAGE_SIZE + 1;
  const end = Math.min((page + 1) * FOLDER_PAGE_SIZE, total);
  return (
    <nav className="documents-folder-pagination" aria-label={`${label} pages`}>
      <span>{start}-{end} of {total}</span>
      <div>
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          aria-label={`Previous ${label.toLowerCase()} page`}>
          <AppIcon icon="pi pi-chevron-left" aria-hidden="true" />
        </button>
        <label>
          <span>Page</span>
          <select
            aria-label={`${label} page`}
            value={page + 1}
            onChange={(event) => onPageChange(Number(event.target.value) - 1)}>
            {Array.from({ length: pageCount }, (_unused, index) => (
              <option key={index + 1} value={index + 1}>{index + 1}</option>
            ))}
          </select>
          <span>of {pageCount}</span>
        </label>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page + 1 >= pageCount}
          aria-label={`Next ${label.toLowerCase()} page`}>
          <AppIcon icon="pi pi-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}



function FolderCatalogEmptyState({ locked, onShowAllFolders }) {
  if (!locked) return <div className="documents-archive-empty">No folders are available for this view.</div>;
  return (
    <div className="documents-folder-locked-empty">
      <span aria-hidden="true"><AppIcon icon="pi pi-lock-open" /></span>
      <strong>No documents are locked</strong>
      <small>Folders appear here while a document is locked for editing.</small>
      <button type="button" onClick={onShowAllFolders}>Show all folders</button>
    </div>
  );
}



function DocumentRegistryPager({
  status,
  loading,
  documentFirst,
  pageSize,
  totalFiltered,
  onPreviousPage,
  onNextPage,
}) {
  return (
    <nav className="documents-workspace-registry-pager" aria-label="Document search pages">
      <span>{status}</span>
      <div>
        <button type="button" onClick={onPreviousPage} disabled={loading || documentFirst === 0} aria-label="Previous document page">
          <AppIcon icon="pi pi-chevron-left" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onNextPage}
          disabled={loading || documentFirst + pageSize >= totalFiltered}
          aria-label="Next document page">
          <AppIcon icon="pi pi-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}



function FolderWorkspaceModeSwitch({
  display,
  matterSuggestionTotal,
  browseDropTarget,
  onBrowseDragOver,
  onBrowseDragLeave,
  onBrowseDrop,
  onChange,
}) {
  const modes = [
    ['folders', 'Browse', 'pi pi-folder-open'],
    ['files', 'Search', 'pi pi-search'],
    ['templates', 'Templates', 'pi pi-clone'],
    ['review', 'Matter review', 'pi pi-sitemap'],
  ];
  return (
    <div className="documents-workspace-display-segment" role="tablist" aria-label="Folder workspace view">
      {modes.map(([value, label, icon]) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={display === value}
          className={`${display === value ? 'is-active' : ''} ${value === 'folders' && browseDropTarget ? 'is-drop-target' : ''}`.trim()}
          onDragOver={value === 'folders' ? onBrowseDragOver : undefined}
          onDragLeave={value === 'folders' ? onBrowseDragLeave : undefined}
          onDrop={value === 'folders' ? onBrowseDrop : undefined}
          onClick={() => onChange(value)}>
          <AppIcon icon={icon} aria-hidden="true" />
          {label}
          {value === 'review' && matterSuggestionTotal > 0 && (
            <span className="documents-matter-review-tab-count">{matterSuggestionTotal}</span>
          )}
        </button>
      ))}
    </div>
  );
}



function FolderColumnsBrowser({
  folders,
  activeFolder,
  documents,
  total,
  hasNext,
  loading,
  loadingMore,
  error,
  foldersLoading,
  foldersError,
  folderSearch,
  nonMatterPage,
  matterPage,
  onNonMatterPageChange,
  onMatterPageChange,
  onOpenFolder,
  onExternalFileDrop,
  onCloseFolder,
  onFolderSearchChange,
  onOpenDocument,
  onMoveDocument,
  onRenameDocument,
  onTrashDocument,
  onMoveFolder,
  onTrashFolder,
  canTrashFolderItem,
  onMergeFolder,
  onCopyFolder,
  onCopyDocumentToClipboard,
  onCutDocument,
  onCopyFolderToClipboard,
  onCutFolder,
  onPasteFolder,
  canPaste = false,
  onDropMove,
  onRenameFolder,
  onNewDocument,
  onNewFolder,
  onLoadMore,
  locked = false,
  onShowAllFolders,
  embedded = false,
}) {
  const {
    fileContextMenu,
    folderContextMenu,
    folderCreateContextMenu,
    openFileContextMenu,
    openFolderContextMenu,
    openFolderCreateContextMenu,
    openFileContextMenuFromKey,
    openFolderContextMenuFromKey,
    closeFileContextMenu,
    closeFolderContextMenu,
    closeFolderCreateContextMenu,
  } = useExplorerContextMenus();
  const [folderTabs, setFolderTabs] = useDocumentsWorkspaceField('folderTabs');
  const [activeFolderTabId, setActiveFolderTabId] = useDocumentsWorkspaceField('activeFolderTabId');
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverTabId, setDragOverTabId] = useState('');
  const [dragOverFolderKey, setDragOverFolderKey] = useState('');
  const [uploadDropTargetKey, setUploadDropTargetKey] = useState('');
  const [movingToTabId, setMovingToTabId] = useState('');
  const [tabMoveError, setTabMoveError] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState('');
  const [renameDraft, setRenameDraft] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [selectedPreviewDocument, setSelectedPreviewDocument] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(() => (
    activeFolder && isMatterFolder(activeFolder) ? 'matter' : 'non-matter'
  ));
  const columnsViewportRef = useRef(null);
  const tabNavigationReadyRef = useRef(false);
  const pendingTabNavigationRef = useRef(null);
  const dragTabHoverRef = useRef(null);
  const simulFolders = folders.filter((folder) => folder.folder_type === 'simul');
  const filedFolders = folders.filter((folder) => (
    folder.folder_type !== 'simul' && !isHiddenFolderBrowserRoot(folder)
  ));
  const simulFoldersById = new Map(simulFolders.map((folder) => [simulFolderId(folder), folder]));
  const simulRoots = simulFolders.filter((folder) => (
    !folder.parent_folder_id || !simulFoldersById.has(folder.parent_folder_id)
  ));
  const simulRootChildren = simulRoots.length === 1
    ? simulFolders.filter((folder) => folder.parent_folder_id === simulFolderId(simulRoots[0]))
    : [];
  const topLevelSimulFolders = simulRootChildren.length > 0 ? simulRootChildren : simulRoots;
  const browsableSimulFolders = simulRootChildren.length > 0
    ? simulFolders.filter((folder) => simulFolderId(folder) !== simulFolderId(simulRoots[0]))
    : simulFolders;
  const internalIdentities = new Set(filedFolders.map(folderBrowserIdentity));
  const topLevelFiledFolders = filedFolders.filter((folder) => !internalIdentities.has(folderBrowserParentIdentity(folder)));
  const browserFolders = [...browsableSimulFolders, ...filedFolders];
  const allRoots = [...topLevelSimulFolders, ...topLevelFiledFolders]
    .sort((left, right) => String(left.name || left.folder_path).localeCompare(String(right.name || right.folder_path)));
  const browserFoldersByIdentity = new Map(browserFolders.map((folder) => [folderBrowserIdentity(folder), folder]));
  const browserFoldersById = new Map(browserFolders.map((folder) => [String(folder.folder_id || ''), folder]));
  const resolveTabFolder =

  (tab) => {
    if (!tab?.folderId && !tab?.folderKey) return null;
    return browserFoldersById.get(String(tab.folderId || '')) || browserFoldersByIdentity.get(tab.folderKey) || null;
  };
  const tabDetailsForFolder =

  (folder) => {
    if (!folder) return { ...DEFAULT_DOCUMENTS_FOLDER_TAB };
    const label = folder.name || folderTitle(folder.folder_path) || 'Folder';
    return {
      folderId: String(folder.folder_id || ''),
      folderKey: folderBrowserIdentity(folder),
      label,
      title: folderBrowserPath(folder) || folder.folder_path || label,
    };
  };
  const nonMatterRoots = allRoots.filter((folder) => !isMatterFolder(folder));
  const matterRoots = allRoots.filter(isMatterFolder);
  const nonMatterCount = browserFolders.filter((folder) => !isMatterFolder(folder)).length;
  const matterCount = browserFolders.filter(isMatterFolder).length;
  const currentNonMatterPage = Math.min(
    nonMatterPage,
    Math.max(0, Math.ceil(nonMatterRoots.length / FOLDER_PAGE_SIZE) - 1)
  );
  const currentMatterPage = Math.min(
    matterPage,
    Math.max(0, Math.ceil(matterRoots.length / FOLDER_PAGE_SIZE) - 1)
  );
  const selectedRoots = selectedCategory === 'matter' ? matterRoots : nonMatterRoots;
  const selectedPage = selectedCategory === 'matter' ? currentMatterPage : currentNonMatterPage;
  const pagedRoots = selectedRoots.slice(
    selectedPage * FOLDER_PAGE_SIZE,
    (selectedPage + 1) * FOLDER_PAGE_SIZE
  );
  const breadcrumbs = activeFolder ? folderBrowserBreadcrumbs(activeFolder, browserFolders) : [];
  const activeIdentity = folderBrowserIdentity(activeFolder);
  const normalizedFolderSearch = String(folderSearch || '').trim();
  const isSearching = normalizedFolderSearch.length >= 3;
  const hasShortSearch = normalizedFolderSearch.length > 0 && normalizedFolderSearch.length < 3;

  const replaceTabLocation =

  (tabId, folder, { clearPreview = false } = {}) => {
    const details = tabDetailsForFolder(folder);
    setFolderTabs((current) => current.map((tab) => (
      tab.id === tabId ? {
        ...tab,
        ...details,
        id: tab.id,
        previewDocumentId: clearPreview ? '' : tab.previewDocumentId,
      } : tab
    )));
  };

  const navigateToFolder =

  (folder, tabId = activeFolderTabId, { preservePreview = false } = {}) => {
    replaceTabLocation(tabId, folder, { clearPreview: !preservePreview });
    if (!preservePreview) setSelectedPreviewDocument(null);
    pendingTabNavigationRef.current = folder ? folderBrowserIdentity(folder) : '';
    if (folder) onOpenFolder(folder);
    else onCloseFolder();
  };

  const navigateToTab =

  (tab) => {
    if (!tab) return;
    setActiveFolderTabId(tab.id);
    const folder = resolveTabFolder(tab);
    pendingTabNavigationRef.current = folder ? folderBrowserIdentity(folder) : '';
    if (folder) onOpenFolder(folder);
    else onCloseFolder();
  };

  const openFolderInNewTab =

  (folder) => {
    const details = tabDetailsForFolder(folder);
    const tab = {
      ...details,
      id: `folder-tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    setFolderTabs((current) => [...current, tab].slice(-20));
    navigateToTab(tab);
  };

  const closeFolderTab =

  (event, tabId) => {
    event.stopPropagation();
    const closingIndex = folderTabs.findIndex((tab) => tab.id === tabId);
    if (closingIndex < 0) return;
    let remaining = folderTabs.filter((tab) => tab.id !== tabId);
    if (remaining.length === 0) remaining = [{ ...DEFAULT_DOCUMENTS_FOLDER_TAB }];
    setFolderTabs(remaining);
    if (activeFolderTabId === tabId) {
      navigateToTab(remaining[Math.min(closingIndex, remaining.length - 1)]);
    }
  };

  const beginDrag =

  (event, type, item) => {
    const identifier = type === 'folder' ? item.folder_id : item.document_id;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${type}:${identifier || ''}`);
    setDraggedItem({ type, item });
    setTabMoveError('');
  };

  const endDrag =

  () => {
    if (dragTabHoverRef.current?.timeoutId) clearTimeout(dragTabHoverRef.current.timeoutId);
    dragTabHoverRef.current = null;
    setDraggedItem(null);
    setDragOverTabId('');
    setDragOverFolderKey('');
  };

  const canDropAtFolder =

  (destination) => {
    if (!draggedItem || movingToTabId || !destination?.folder_id) return false;
    if (draggedItem.type === 'file') {
      return String(draggedItem.item.folder_id || '') !== String(destination.folder_id || '');
    }
    const source = draggedItem.item;
    if (!source?.can_move_internally || !source.folder_id) return false;
    if (String(source.folder_id) === String(destination.folder_id)) return false;
    if (String(source.parent_folder_id || '') === String(destination.folder_id)) return false;
    let ancestor = destination;
    while (ancestor) {
      if (String(ancestor.folder_id || '') === String(source.folder_id)) return false;
      ancestor = browserFoldersById.get(String(ancestor.parent_folder_id || '')) || null;
    }
    return true;
  };

  const canDropOnTab =

  (tab) => (
    tab.id !== activeFolderTabId && canDropAtFolder(resolveTabFolder(tab))
  );

  const beginTabHover =

  (tab) => {
    if (!canDropOnTab(tab) || dragTabHoverRef.current?.tabId === tab.id) return;
    if (dragTabHoverRef.current?.timeoutId) clearTimeout(dragTabHoverRef.current.timeoutId);
    const timeoutId = setTimeout(() => {
      navigateToTab(tab);
      setDragOverTabId('');
      dragTabHoverRef.current = null;
    }, 650);
    dragTabHoverRef.current = { tabId: tab.id, timeoutId };
  };

  const cancelTabHover =

  (tabId) => {
    if (dragTabHoverRef.current?.tabId !== tabId) return;
    clearTimeout(dragTabHoverRef.current.timeoutId);
    dragTabHoverRef.current = null;
  };

  const dropOnTab =

  async (event, tab) => {
    if (!canDropOnTab(tab)) return;
    event.preventDefault();
    const moveItem = draggedItem;
    const destination = resolveTabFolder(tab);
    cancelTabHover(tab.id);
    setMovingToTabId(tab.id);
    setDragOverTabId('');
    setTabMoveError('');
    try {
      await onDropMove(moveItem, destination);
    } catch (requestError) {
      setTabMoveError(
        requestError?.response?.data?.error || requestError.message || `Could not move ${moveItem.type}.`
      );
    } finally {
      setMovingToTabId('');
      setDraggedItem(null);
    }
  };

  const dropOnFolder =

  async (event, destination) => {
    if (!canDropAtFolder(destination)) return;
    event.preventDefault();
    event.stopPropagation();
    const moveItem = draggedItem;
    setMovingToTabId(`folder:${folderBrowserIdentity(destination)}`);
    setDragOverFolderKey('');
    setTabMoveError('');
    try {
      await onDropMove(moveItem, destination);
    } catch (requestError) {
      setTabMoveError(
        requestError?.response?.data?.error || requestError.message || `Could not move ${moveItem.type}.`
      );
    } finally {
      setMovingToTabId('');
      setDraggedItem(null);
    }
  };

  const beginRename =

  (folder, title) => {
    setRenamingFolderId(String(folder?.folder_id || ''));
    setRenameDraft(title);
    setRenameError('');
  };
  const cancelRename =

  () => {
    if (renameSubmitting) return;
    setRenamingFolderId('');
    setRenameDraft('');
    setRenameError('');
  };
  const submitRename =

  async (folder) => {
    const name = renameDraft.trim();
    if (!name || renameSubmitting) return;
    setRenameSubmitting(true);
    setRenameError('');
    try {
      await onRenameFolder(folder, name);
      setRenamingFolderId('');
      setRenameDraft('');
    } catch (requestError) {
      setRenameError(
        requestError?.response?.data?.error || requestError.message || 'Could not rename folder.'
      );
    } finally {
      setRenameSubmitting(false);
    }
  };

  useEffect(

  () => {
    if (foldersLoading || tabNavigationReadyRef.current) return;
    tabNavigationReadyRef.current = true;
    const selectedTab = folderTabs.find((tab) => tab.id === activeFolderTabId) || folderTabs[0];
    const restoredActiveFolder = activeFolder && (
      browserFoldersById.get(String(activeFolder.folder_id || '')) ||
      browserFoldersByIdentity.get(folderBrowserIdentity(activeFolder))
    );
    if (restoredActiveFolder) navigateToFolder(restoredActiveFolder, selectedTab?.id, { preservePreview: true });
    else navigateToTab(selectedTab);
    // Restore once when the folder catalog is ready; later navigation is user-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolderTabId, folderTabs, foldersLoading]);

  useEffect(

  () => {
    if (!tabNavigationReadyRef.current) return;
    const currentIdentity = activeFolder ? folderBrowserIdentity(activeFolder) : '';
    if (pendingTabNavigationRef.current !== null) {
      if (pendingTabNavigationRef.current === currentIdentity) pendingTabNavigationRef.current = null;
      return;
    }
    replaceTabLocation(activeFolderTabId, activeFolder);
    // The identity is the stable navigation signal; object-only catalog refreshes are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolderTabId, activeIdentity]);

  useEffect(() => {
    setFolderTabs((current) => current.map(

    (tab) => {
      const resolved = resolveTabFolder(tab);
      if (!resolved) return tab;
      const details = tabDetailsForFolder(resolved);
      return (
        tab.folderId === details.folderId &&
        tab.folderKey === details.folderKey &&
        tab.label === details.label &&
        tab.title === details.title
      ) ? tab : { ...tab, ...details };
    }));
    // Folder IDs keep tabs stable while this effect refreshes renamed or moved path labels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders]);

  useEffect(() => {
    if (activeFolder) setSelectedCategory(isMatterFolder(activeFolder) ? 'matter' : 'non-matter');
    else if (selectedCategory === 'non-matter' && nonMatterRoots.length === 0 && matterRoots.length > 0) setSelectedCategory('matter');
  }, [activeFolder, matterRoots.length, nonMatterRoots.length, selectedCategory]);

  useEffect(

  () => {
    const activeTab = folderTabs.find((tab) => tab.id === activeFolderTabId);
    const previewDocumentId = String(activeTab?.previewDocumentId || '');
    const previewDocument = previewDocumentId
      ? documents.find((document) => String(document.document_id) === previewDocumentId) || null
      : null;
    setSelectedPreviewDocument(previewDocument);
  }, [activeFolderTabId, activeIdentity, documents, folderTabs]);

  useEffect(() => {
    const viewport = columnsViewportRef.current;
    if (!viewport) return;
    if (typeof viewport.scrollTo === 'function') {
      viewport.scrollTo({ left: viewport.scrollWidth, behavior: 'smooth' });
    } else {
      viewport.scrollLeft = viewport.scrollWidth;
    }
  }, [activeIdentity, breadcrumbs.length, selectedPreviewDocument?.document_id]);

  const selectPreviewDocument =

  (document) => {
    setSelectedPreviewDocument(document);
    setFolderTabs((current) => current.map((tab) => (
      tab.id === activeFolderTabId
        ? { ...tab, previewDocumentId: String(document?.document_id || '') }
        : tab
    )));
  };

  const selectCategory =

  (category) => {
    setSelectedCategory(category);
    if (activeFolder && (category === 'matter') !== isMatterFolder(activeFolder)) onCloseFolder();
  };
  const changeRootPage = selectedCategory === 'matter' ? onMatterPageChange : onNonMatterPageChange;
  const activeChildren = activeFolder
    ? folderBrowserChildren(activeFolder, browserFolders)
      .sort((left, right) => String(left.name || left.folder_path).localeCompare(String(right.name || right.folder_path)))
    : [];
  const directCount = activeFolder ? folderCount(activeFolder, 'direct_document_count', 'document_count') : 0;
  const descendantCount = activeFolder ? Math.max(
    directCount,
    folderCount(activeFolder, 'descendant_document_count', 'document_count')
  ) : 0;
  const folderInventoryCount = nonMatterCount + matterCount;
  const openCategoryFolder =

  (folder) => navigateToFolder(folder);
  const renderFolderRow =

  (folder, selected) => {
    const label = folder.name || folderTitle(folder.folder_path);
    const pathLabel = folderBrowserPath(folder) || folder.folder_path || label;
    const childCount = folderBrowserChildren(folder, browserFolders).length || folderCount(folder, 'child_folder_count');
    return (
      <MillerColumnRow
        key={folderIdentity(folder)}
        selected={selected}
        dropTarget={dragOverFolderKey === folderBrowserIdentity(folder)}
        uploadTarget={uploadDropTargetKey === folderBrowserIdentity(folder)}
        className="documents-folder-column-item is-folder-row"
        draggable={Boolean(folder.can_move_internally)}
        onDragStart={(event) => beginDrag(event, 'folder', folder)}
        onDragEnd={endDrag}
        data-upload-label={uploadDropTargetKey === folderBrowserIdentity(folder) ? folderUploadLabel(folder) : undefined}
        onDragEnter={(event) => markExternalUploadTarget(event, folder, setUploadDropTargetKey)}
        onDragOver={

        (event) => {
          if (markExternalUploadTarget(event, folder, setUploadDropTargetKey)) return;
          if (!canDropAtFolder(folder)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setDragOverFolderKey(folderBrowserIdentity(folder));
        }}
        onDragLeave={(event) => {
          if (leaveExternalUploadTarget(event, setUploadDropTargetKey)) return;
          if (!event.currentTarget.contains(event.relatedTarget)) setDragOverFolderKey('');
        }}
        onDrop={(event) => {
          if (dropExternalUpload(event, folder, setUploadDropTargetKey, onExternalFileDrop)) return;
          dropOnFolder(event, folder);
        }}
        onClick={() => openCategoryFolder(folder)}
        onContextMenu={(event) => openFolderContextMenu(event, folder)}>
        <MillerColumnItem
          className="documents-folder-column-item-open"
          aria-label={`Open folder ${pathLabel} in column view`}
          aria-pressed={selected}
          onKeyDown={(event) => {
            openFolderContextMenuFromKey(event, folder);
          }}>
          <span
            className="documents-drag-handle"
            title={folder.can_move_internally ? `Drag ${label} to move` : undefined}
            aria-hidden="true">
            <AppIcon icon={selected ? 'pi pi-folder-open' : 'pi pi-folder'} />
          </span>
          <span>
            <strong title={label}>{label}</strong>
            <small>{folderContentsSummary(folder, childCount)}</small>
          </span>
        </MillerColumnItem>
      </MillerColumnRow>
    );
  };

  const openFolderCreateMenu =

  (event, folder) => {
    if (!folder || !isFolderCanvasBackground(event)) return;
    openFolderCreateContextMenu(event, folder, folder.folder_type !== 'simul');
  };

  return (
    <div className="documents-workspace-folder-browser is-columns" aria-label="Document folders">
      {!embedded && <div className="documents-workspace-folder-browser-head">
        <span>
          <strong>Folders</strong>
          <small>{folderInventoryCount} {folderInventoryCount === 1 ? 'folder' : 'folders'}</small>
        </span>
        <span className="documents-workspace-folder-head-actions">
          <span className="documents-workspace-folder-actions">
            <button
              type="button"
              onClick={() => onNewFolder(activeFolder && activeFolder.folder_type !== 'simul' ? activeFolder : null)}
              aria-label={activeFolder && activeFolder.folder_type !== 'simul' ? 'New subfolder' : 'New folder'}
              title={activeFolder && activeFolder.folder_type !== 'simul' ? 'Create a subfolder here' : 'Create a folder'}>
              <AppIcon icon="pi pi-folder-plus" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => onNewDocument(activeFolder)}
              aria-label="New Document"
              title={activeFolder ? 'Create a document here' : 'Create a document'}>
              <AppIcon icon="pi pi-file-plus" aria-hidden="true" />
            </button>
          </span>
        </span>
      </div>}
      <div className="documents-folder-tabs" role="tablist" aria-label="Folder tabs">
        {folderTabs.map(

        (tab) => {
          const tabFolder = resolveTabFolder(tab);
          const isActive = tab.id === activeFolderTabId;
          const isPinned = (
            tab.id === DEFAULT_DOCUMENTS_FOLDER_TAB.id &&
            !tab.folderId &&
            !tab.folderKey
          );
          const canAcceptDrop = canDropOnTab(tab);
          const isDropTarget = canAcceptDrop && dragOverTabId === tab.id;
          const isMovingHere = movingToTabId === tab.id;
          const isUploadTarget = Boolean(tabFolder && uploadDropTargetKey === folderBrowserIdentity(tabFolder));
          return (
            <div
              key={tab.id}
              className={`documents-folder-tab ${isPinned ? 'is-pinned' : ''} ${isActive ? 'is-active' : ''} ${isDropTarget ? 'is-drop-target' : ''} ${isUploadTarget ? 'is-upload-drop-target' : ''}`}
              data-upload-label={isUploadTarget ? folderUploadLabel(tabFolder) : undefined}
              onDragEnter={(event) => markExternalUploadTarget(event, tabFolder, setUploadDropTargetKey)}
              onDragOver={

              (event) => {
                if (markExternalUploadTarget(event, tabFolder, setUploadDropTargetKey)) return;
                if (!canAcceptDrop) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                setDragOverTabId(tab.id);
                beginTabHover(tab);
              }}
              onDragLeave={(event) => {
                if (leaveExternalUploadTarget(event, setUploadDropTargetKey)) return;
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setDragOverTabId('');
                  cancelTabHover(tab.id);
                }
              }}
              onDrop={(event) => {
                if (dropExternalUpload(event, tabFolder, setUploadDropTargetKey, onExternalFileDrop)) return;
                dropOnTab(event, tab);
              }}>
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`Folder tab: ${tab.label}`}
                title={tab.title}
                onClick={() => navigateToTab(tab)}>
                <AppIcon icon={isMovingHere ? 'pi pi-spin pi-spinner' : isActive ? 'pi pi-folder-open' : 'pi pi-folder'} aria-hidden="true" />
                <span>{tab.label}</span>
              </button>
              {!isPinned && (
                <button
                  type="button"
                  className="documents-folder-tab-close"
                  aria-label={`Close folder tab ${tab.label}`}
                  title="Close tab"
                  onClick={(event) => closeFolderTab(event, tab.id)}>
                  <AppIcon icon="pi pi-times" aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <InlineOperationError message={tabMoveError} className="documents-folder-tab-error" />
      {foldersError ? (
        <div className="documents-archive-error" role="alert">{foldersError}</div>
      ) : foldersLoading ? (
        <div className="documents-archive-empty">Loading folders...</div>
      ) : folders.length === 0 ? (
        <FolderCatalogEmptyState locked={locked} onShowAllFolders={onShowAllFolders} />
      ) : (
        <MillerColumnExplorer className="documents-folder-columns" ref={columnsViewportRef} role="region" aria-label="Folder column view">
          <MillerColumn category className="documents-folder-column" aria-label="Folder categories">
            <MillerColumnHeader><AppIcon icon="pi pi-folder" aria-hidden="true" /><strong>Locations</strong></MillerColumnHeader>
            <MillerColumnList className="documents-folder-column-list">
              {[
                ['non-matter', 'Non-matter folders', 'pi pi-folder', nonMatterCount, nonMatterRoots.length],
                ['matter', 'Matter folders', 'pi pi-briefcase', matterCount, matterRoots.length],
              ].map(([value, label, icon, count, rootCount]) => (
                <MillerColumnRow
                  as="button"
                  type="button"
                  key={value}
                  selected={selectedCategory === value}
                  className="documents-folder-column-item"
                  aria-pressed={selectedCategory === value}
                  onClick={() => selectCategory(value)}>
                  <AppIcon icon={icon} aria-hidden="true" />
                  <span><strong>{label}</strong><small>{count} total · {rootCount} top-level</small></span>
                </MillerColumnRow>
              ))}
            </MillerColumnList>
          </MillerColumn>
          <MillerColumn className="documents-folder-column" aria-label={selectedCategory === 'matter' ? 'Matter folders' : 'Non-matter folders'}>
            <MillerColumnHeader>
              <AppIcon icon={selectedCategory === 'matter' ? 'pi pi-briefcase' : 'pi pi-folder'} aria-hidden="true" />
              <strong>{selectedCategory === 'matter' ? 'Matter folders' : 'Non-matter folders'}</strong>
            </MillerColumnHeader>
            <MillerColumnList className="documents-folder-column-list">
              {pagedRoots.map((folder) => renderFolderRow(folder, breadcrumbs[0] && folderBrowserIdentity(breadcrumbs[0]) === folderBrowserIdentity(folder)))}
              {pagedRoots.length === 0 && (
                <MillerColumnEmpty className="documents-folder-column-empty">No folders here yet.</MillerColumnEmpty>
              )}
            </MillerColumnList>
            <FolderPagination
              label={selectedCategory === 'matter' ? 'Matter folder' : 'Non-matter folder'}
              page={selectedPage}
              total={selectedRoots.length}
              onPageChange={changeRootPage} />
          </MillerColumn>
          {breadcrumbs.map(

          (folder, index) => {
            const isActiveColumn = index === breadcrumbs.length - 1;
            const canRename = Boolean(
              isActiveColumn &&
              folder.folder_type === 'location' &&
              folder.can_move_internally
            );
            const isRenaming = canRename && renamingFolderId === String(folder.folder_id || '');
            const selectedChild = breadcrumbs[index + 1];
            const children = folderBrowserChildren(folder, browserFolders)
              .sort((left, right) => String(left.name || left.folder_path).localeCompare(String(right.name || right.folder_path)));
            const title = folder.name || folderTitle(folder.folder_path);
            return (
              <MillerColumn
                active={isActiveColumn}
                uploadTarget={uploadDropTargetKey === folderBrowserIdentity(folder)}
                className="documents-folder-column"
                aria-label={`Contents of ${title}`}
                data-upload-label={uploadDropTargetKey === folderBrowserIdentity(folder) ? folderUploadLabel(folder) : undefined}
                key={folderBrowserIdentity(folder)}
                onDragEnter={(event) => {
                  if (markExternalUploadTarget(event, folder, setUploadDropTargetKey)) return;
                  if (canDropAtFolder(folder)) event.preventDefault();
                }}
                onDragOver={

                (event) => {
                  if (markExternalUploadTarget(event, folder, setUploadDropTargetKey)) return;
                  if (!canDropAtFolder(folder)) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                }}
                onDragLeave={(event) => leaveExternalUploadTarget(event, setUploadDropTargetKey)}
                onDrop={(event) => {
                  if (dropExternalUpload(event, folder, setUploadDropTargetKey, onExternalFileDrop)) return;
                  dropOnFolder(event, folder);
                }}
                onContextMenu={(event) => openFolderCreateMenu(event, folder)}>
                <MillerColumnHeader>
                  <AppIcon icon={isActiveColumn ? 'pi pi-folder-open' : 'pi pi-folder'} aria-hidden="true" />
                  {isRenaming ? (
                    <span className="documents-folder-column-rename">
                      <input
                        type="text"
                        aria-label="Folder name"
                        value={renameDraft}
                        maxLength={160}
                        disabled={renameSubmitting}
                        autoFocus
                        onChange={(event) => setRenameDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            submitRename(folder);
                          } else if (event.key === 'Escape') {
                            event.preventDefault();
                            cancelRename();
                          }
                        }} />
                      <button
                        type="button"
                        aria-label="Save folder name"
                        title="Save"
                        disabled={renameSubmitting || !renameDraft.trim()}
                        onClick={() => submitRename(folder)}>
                        <AppIcon icon={renameSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        aria-label="Cancel folder rename"
                        title="Cancel"
                        disabled={renameSubmitting}
                        onClick={cancelRename}>
                        <AppIcon icon="pi pi-times" aria-hidden="true" />
                      </button>
                    </span>
                  ) : canRename ? (
                    <button
                      type="button"
                      className="documents-folder-column-title-button"
                      aria-label={`Rename folder ${title}`}
                      title="Rename folder"
                      onClick={() => beginRename(folder, title)}>
                      <strong>{title}</strong>
                      <AppIcon icon="pi pi-pencil" aria-hidden="true" />
                    </button>
                  ) : (
                    <strong title={title}>{title}</strong>
                  )}
                </MillerColumnHeader>
                {isActiveColumn && <InlineOperationError message={renameError} />}
                {isActiveColumn && (
                  <>
                    <label className="documents-folder-column-search">
                      <AppIcon icon="pi pi-search" aria-hidden="true" />
                      <input
                        type="search"
                        aria-label={`Search within ${title}`}
                        value={folderSearch}
                        onChange={(event) => onFolderSearchChange(event.target.value)}
                        placeholder="Search this folder" />
                    </label>
                    <div className="documents-folder-column-summary" role="status">
                      {loading
                        ? 'Loading files…'
                        : hasShortSearch
                          ? 'Type 3 characters to search files.'
                          : isSearching
                            ? `${total} ${total === 1 ? 'match' : 'matches'}`
                            : `${activeChildren.length} ${activeChildren.length === 1 ? 'subfolder' : 'subfolders'} · ${directCount} ${directCount === 1 ? 'file' : 'files'} here${descendantCount !== directCount ? ` · ${descendantCount} including subfolders` : ''}`}
                    </div>
                  </>
                )}
                <MillerColumnList
                  uploadTarget={uploadDropTargetKey === folderBrowserIdentity(folder)}
                  className="documents-folder-column-list"
                  data-upload-label={uploadDropTargetKey === folderBrowserIdentity(folder) ? folderUploadLabel(folder) : undefined}
                  onDragEnter={(event) => markExternalUploadTarget(event, folder, setUploadDropTargetKey)}
                  onDragOver={(event) => markExternalUploadTarget(event, folder, setUploadDropTargetKey)}
                  onDragLeave={(event) => leaveExternalUploadTarget(event, setUploadDropTargetKey)}
                  onDrop={(event) => dropExternalUpload(event, folder, setUploadDropTargetKey, onExternalFileDrop)}
                  onContextMenu={isActiveColumn ? (event) => openFolderCreateMenu(event, folder) : undefined}>
                  {children.map((child) => renderFolderRow(child, Boolean(selectedChild && folderBrowserIdentity(selectedChild) === folderBrowserIdentity(child))))}
                  {isActiveColumn && error && <div className="documents-archive-error" role="alert">{error} <button type="button" disabled={loading || loadingMore} onClick={onLoadMore}>Retry</button></div>}
                  {isActiveColumn && loading && documents.length === 0 && <MillerColumnEmpty className="documents-folder-column-empty">Loading folder files...</MillerColumnEmpty>}
                  {isActiveColumn && !loading && documents.map((document) => {
                    const needsImport = document.available_locally === false;
                    return (
                      <MillerColumnRow
                        file
                        selected={String(selectedPreviewDocument?.document_id || '') === String(document.document_id)}
                        className={`documents-folder-column-file-row ${draggedItem?.type === 'file' && draggedItem.item.document_id === document.document_id ? 'is-dragging' : ''}`}
                        key={document.document_id}
                        draggable={!needsImport}
                        onDragStart={needsImport ? undefined : (event) => beginDrag(event, 'file', document)}
                        onDragEnd={needsImport ? undefined : endDrag}
                        onClick={() => selectPreviewDocument(document)}
                        onDoubleClick={needsImport ? undefined : () => onOpenDocument(document.document_id)}
                        onContextMenu={(event) => openFileContextMenu(event, document)}>
                        <MillerColumnItem
                          className="documents-folder-column-file"
                          disabled={needsImport}
                          aria-label={`Select folder file: ${document.filename || 'Untitled document'}`}
                          aria-pressed={String(selectedPreviewDocument?.document_id || '') === String(document.document_id)}
                          title={needsImport ? 'Not archived before Simul closed' : 'Double-click to open'}
                          onKeyDown={(event) => {
                            if (openFileContextMenuFromKey(event, document)) {
                              return;
                            } else if (event.key === 'Enter' && !needsImport) {
                              event.preventDefault();
                              onOpenDocument(document.document_id);
                            }
                          }}>
                          <span
                            className="documents-drag-handle"
                            title={!needsImport ? `Drag ${document.filename || 'file'} to move` : undefined}
                            aria-hidden="true">
                            <DocumentFileTypeIcon file={document} />
                          </span>
                          <span>
                            <strong title={document.filename}>{document.filename || 'Untitled document'}</strong>
                            <small>{`${documentSourceLabel(document)} · ${needsImport ? 'Not archived' : versionSummary(document)}`}</small>
                            {isSearching && document.match?.snippet && (
                              <small className="documents-folder-column-snippet"><Highlight text={document.match.snippet} term={normalizedFolderSearch} /></small>
                            )}
                            {isSearching && (document.simul_folder_path || document.folder_path) && (
                              <small className="documents-folder-column-location">
                                <AppIcon icon="pi pi-folder" aria-hidden="true" />
                                <Highlight text={document.simul_folder_path || document.folder_path} term={normalizedFolderSearch} />
                              </small>
                            )}
                          </span>
                        </MillerColumnItem>
                      </MillerColumnRow>
                    );
                  })}
                  {isActiveColumn && !loading && !error && children.length === 0 && documents.length === 0 && (
                    <MillerColumnEmpty className="documents-folder-column-empty">{isSearching ? 'No files match in this folder.' : 'Empty folder'}</MillerColumnEmpty>
                  )}
                  {isActiveColumn && !loading && !error && hasNext && (
                    <div className="documents-folder-column-load-more">
                      <button type="button" disabled={loadingMore} onClick={onLoadMore}>
                        {loadingMore ? 'Loading…' : `Load ${Math.min(FOLDER_DRAWER_PAGE_SIZE, Math.max(0, total - documents.length))} more`}
                      </button>
                    </div>
                  )}
                </MillerColumnList>
              </MillerColumn>
            );
          })}
          <DocumentExplorerPreviewColumn
            document={selectedPreviewDocument}
            onOpen={selectedPreviewDocument?.available_locally === false
              ? undefined
              : (document) => onOpenDocument(document.document_id)} />
        </MillerColumnExplorer>
      )}
      <FileContextMenu
        menu={fileContextMenu}
        onClose={closeFileContextMenu}
        onMove={onMoveDocument}
        onRename={onRenameDocument}
        onTrash={onTrashDocument}
        onCopyToClipboard={onCopyDocumentToClipboard}
        onCut={onCutDocument} />
      <FolderContextMenu
        menu={folderContextMenu}
        onClose={closeFolderContextMenu}
        onNewSubfolder={onNewFolder}
        canCreateSubfolder={(folder) => folder.folder_type !== 'simul'}
        onOpenInNewTab={openFolderInNewTab}
        onMove={onMoveFolder}
        onTrash={onTrashFolder}
        canTrash={canTrashFolderItem}
        onMergeWithMatter={onMergeFolder}
        canMergeWithMatter={(folder) => folderDestinationScope(folder, folders) === 'internal'}
        onCopy={onCopyFolder}
        onCopyToClipboard={onCopyFolderToClipboard}
        onCut={onCutFolder}
        onPaste={onPasteFolder}
        canPaste={canPaste} />
      <FolderCreateContextMenu
        menu={folderCreateContextMenu}
        onClose={closeFolderCreateContextMenu}
        onNewFolder={onNewFolder}
        onNewDocument={onNewDocument}
        onPaste={onPasteFolder}
        canPaste={canPaste} />
    </div>
  );
}



function defaultSuggestedMatter() {
  return '';
}



function casePartyLabel(names, fallback) {
  const values = (names || []).filter(Boolean);
  if (values.length === 0) return fallback;
  if (values.length <= 2) return values.join(' & ');
  return `${values.slice(0, 2).join(' & ')} +${values.length - 2}`;
}



function candidateCaseLabel(candidate) {
  const client = casePartyLabel(candidate?.client_names, 'Client not listed');
  const defendant = casePartyLabel(candidate?.defendant_names, 'Defendant not listed');
  const state = candidate?.state || 'State not listed';
  return `${client} v. ${defendant} · ${state}`;
}



function AmbiguousMatterReview({
  suggestions,
  total,
  loading,
  error,
  matterNumbers,
  selections,
  submitting,
  onSelectMatter,
  onAssignMatter,
  onOpenDocument,
}) {
  const candidatesByMatter = new Map();
  suggestions.forEach((suggestion) => {
    (suggestion.matter_match_candidates || []).forEach((candidate) => {
      if (candidate.matter_number && !candidatesByMatter.has(candidate.matter_number)) {
        candidatesByMatter.set(candidate.matter_number, candidate);
      }
    });
  });
  const matterOptions = [...new Set([...matterNumbers, ...candidatesByMatter.keys()].filter(Boolean))].sort();

  return (
    <section className="documents-matter-review" aria-label="Ambiguous document matter review">
      <header className="documents-matter-review-head">
        <span className="documents-matter-review-heading">
          <span className="documents-matter-review-heading-icon" aria-hidden="true">
            <AppIcon icon="pi pi-sitemap" />
          </span>
          <span>
            <strong>Choose the right matter</strong>
            <small>Client names are shown first; competing defendant matches stay visible for context.</small>
          </span>
        </span>
        <span className="documents-matter-review-count" aria-label={`${total} documents need review`}>
          {total} remaining
        </span>
      </header>

      <datalist id="documents-matter-review-options">
        {matterOptions.map((matter) => (
          <option
            key={matter}
            value={matter}
            label={candidatesByMatter.has(matter) ? candidateCaseLabel(candidatesByMatter.get(matter)) : ''} />
        ))}
      </datalist>

      {error ? (
        <div className="documents-archive-error" role="alert">{error}</div>
      ) : loading ? (
        <div className="documents-archive-empty">Loading matter suggestions...</div>
      ) : suggestions.length === 0 ? (
        <div className="documents-matter-review-complete">
          <AppIcon icon="pi pi-check-circle" aria-hidden="true" />
          <strong>All ambiguous documents are filed.</strong>
        </div>
      ) : (
        <div className="documents-matter-review-grid">
          {suggestions.map(

          (suggestion) => {
            const candidates = suggestion.matter_match_candidates || [];
            const selectedMatter = selections[suggestion.document_id] ?? defaultSuggestedMatter(suggestion);
            const topConfidence = Number(candidates[0]?.confidence || 0);
            const tied = candidates.length > 1 && topConfidence === Number(candidates[1]?.confidence || 0);
            const isSubmitting = Boolean(submitting[suggestion.document_id]);
            const clientNames = suggestion.extracted_client_names || [];
            const defendantNames = suggestion.extracted_defendant_names || [];
            return (
              <article className="documents-matter-review-card" key={suggestion.document_id}>
                <header>
                  <span className="documents-matter-review-file-icon" aria-hidden="true">
                    <DocumentFileTypeIcon file={suggestion} />
                  </span>
                  <span>
                    <strong title={suggestion.filename}>{suggestion.filename || 'Untitled document'}</strong>
                    {tied && <small><AppIcon icon="pi pi-exclamation-circle" aria-hidden="true" /> Equal best matches</small>}
                  </span>
                  <button
                    type="button"
                    className="documents-matter-review-open"
                    onClick={() => onOpenDocument(suggestion.document_id)}
                    aria-label={`Open ambiguous document: ${suggestion.filename || 'Untitled document'}`}
                    title="Open document">
                    <AppIcon icon="pi pi-external-link" aria-hidden="true" />
                  </button>
                </header>

                <div className="documents-matter-review-evidence">
                  <strong className="documents-matter-review-evidence-source">
                    <AppIcon icon="pi pi-file-search" aria-hidden="true" />
                    Extracted from document
                  </strong>
                  {suggestion.simul_folder_path && <>
                    <span className="documents-matter-review-evidence-label">Simul folder</span>
                    <span className="documents-matter-review-folder-path">
                      <AppIcon icon="pi pi-folder" aria-hidden="true" />
                      {suggestion.simul_folder_path}
                    </span>
                  </>}
                  <span className="documents-matter-review-evidence-label">Client</span>
                  <span className="documents-matter-review-party-list">
                    {clientNames.length > 0 ? <>
                      {clientNames.slice(0, 2).map((name, index) => <span key={`${name}-${index}`}>{name}</span>)}
                      {clientNames.length > 2 && (
                        <span title={clientNames.slice(2).join(' · ')}>+{clientNames.length - 2}</span>
                      )}
                    </> : <em>Not found</em>}
                  </span>
                  <span className="documents-matter-review-evidence-label is-defendant">Defendant</span>
                  <span className="documents-matter-review-party-list is-defendant">
                    {defendantNames.length > 0 ? <>
                      {defendantNames.slice(0, 2).map((name, index) => <span key={`${name}-${index}`} title={name}>{name}</span>)}
                      {defendantNames.length > 2 && (
                        <span title={defendantNames.slice(2).join(' · ')}>+{defendantNames.length - 2}</span>
                      )}
                    </> : <em>Not found</em>}
                  </span>
                </div>

                <div className="documents-matter-review-candidates" role="group" aria-label={`Matter choices for ${suggestion.filename || 'document'}`}>
                  {candidates.map(

                  (candidate) => {
                    const isSelected = selectedMatter === candidate.matter_number;
                    const candidateClient = casePartyLabel(candidate.client_names, 'Client not listed');
                    const candidateDefendant = casePartyLabel(candidate.defendant_names, 'Defendant not listed');
                    const candidateState = candidate.state || 'State not listed';
                    const confidence = Math.round(Number(candidate.confidence || 0) * 100);
                    return (
                      <button
                        type="button"
                        key={candidate.matter_number}
                        className={isSelected ? 'is-selected' : ''}
                        aria-pressed={isSelected}
                        aria-label={`${candidateClient} versus ${candidateDefendant}, ${candidateState}, matter ${candidate.matter_number}, ${confidence}% match`}
                        title={candidate.reason || ''}
                        onClick={() => onSelectMatter(suggestion.document_id, candidate.matter_number)}>
                        <span className="documents-matter-review-candidate-client">
                          <AppIcon icon={isSelected ? 'pi pi-check-circle' : 'pi pi-circle'} aria-hidden="true" />
                          <strong>{candidateClient}</strong>
                        </span>
                        <span className="documents-matter-review-candidate-defendant" title={(candidate.defendant_names || []).join(' · ')}>
                          <span>v.</span>
                          {candidateDefendant}
                        </span>
                        <span className="documents-matter-review-candidate-meta">
                          <small title={candidate.jurisdiction || candidateState}>
                            <AppIcon icon="pi pi-map-marker" aria-hidden="true" />
                            {candidateState}
                          </small>
                          <code>{candidate.matter_number}</code>
                          <strong>{confidence}%</strong>
                        </span>
                        {candidate.simul_folder_peer_count > 0 && (
                          <span className="documents-matter-review-folder-evidence">
                            <AppIcon icon="pi pi-folder-open" aria-hidden="true" />
                            {candidate.simul_folder_peer_count} of {candidate.simul_folder_peer_total} assigned folder peers
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <footer>
                  <label>
                    <span>Choose another matter</span>
                    <input
                      list="documents-matter-review-options"
                      value={selectedMatter}
                      onChange={(event) => onSelectMatter(suggestion.document_id, event.target.value)}
                      placeholder="Type client or matter number" />
                  </label>
                  <button
                    type="button"
                    className="documents-workspace-action-button is-primary"
                    disabled={isSubmitting || !String(selectedMatter || '').trim()}
                    onClick={() => onAssignMatter(suggestion, selectedMatter)}>
                    <AppIcon icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-folder'} aria-hidden="true" />
                    {isSubmitting ? 'Assigning...' : 'Assign matter'}
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}



function RecentDocuments({ documents, loading, error, onOpenDocument }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className={`documents-recent${expanded ? ' is-expanded' : ' is-collapsed'}`} aria-label="Recent documents">
      <header className="documents-recent-header">
        <span className="documents-recent-heading">
          <AppIcon icon="pi pi-history" aria-hidden="true" />
          <strong>Recent documents</strong>
        </span>
        {documents.length > 0 && <span>{documents.length}</span>}
      </header>
      <div className="documents-recent-list" id="documents-recent-list">
        {loading ? (
          <span className="documents-recent-empty">Loading recent documents…</span>
        ) : error ? (
          <span className="documents-recent-empty">Recent documents are unavailable.</span>
        ) : documents.length === 0 ? (
          <span className="documents-recent-empty">Documents you open will appear here.</span>
        ) : documents.map(

        (document) => {
          const folderPath = document.folder_path || document.simul_folder_path || '';
          const folderParts = folderPath.split('/').filter(Boolean);
          const locationLabel = folderParts[folderParts.length - 1] ||
            (document.matter_number ? 'Matter documents' : 'Firm documents');
          const created = formatCreated(document.last_opened_at);
          return (
            <button
              key={document.document_id}
              type="button"
              className="documents-recent-item"
              aria-label={`Open recent document: ${document.filename || 'Untitled document'}`}
              onClick={() => onOpenDocument(document.document_id, null, document.revision_id)}>
              <span className="documents-recent-file-icon" aria-hidden="true">
                <DocumentFileTypeIcon file={document} />
              </span>
              <span className="documents-recent-copy">
                <strong title={document.filename}>{document.filename || 'Untitled document'}</strong>
                <span className="documents-recent-footer">
                  <small>
                    <span className="documents-recent-signifier" title={folderPath || locationLabel}><AppIcon icon="pi pi-folder" aria-hidden="true" />{locationLabel}</span>
                  </small>
                  <time dateTime={created.dateTime} title={created.title}>Opened {created.date}</time>
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {!loading && !error && documents.length > 1 && (
        <div className="documents-recent-expand-bar">
          <button
            type="button"
            className="documents-recent-expand-button"
            aria-label={expanded ? 'Collapse recent documents' : 'Expand recent documents'}
            title={expanded ? 'Collapse recent documents' : 'Expand recent documents'}
            aria-expanded={expanded}
            aria-controls="documents-recent-list"
            onClick={() => setExpanded((previous) => !previous)}>
            <AppIcon icon="pi pi-chevron-down" aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
}



function RegistryView({
  recentDocuments,
  recentDocumentsLoading,
  recentDocumentsError,
  documents,
  documentsLoading,
  documentsError,
  documentsWarning,
  search,
  debouncedSearch,
  matterNumber,
  folderPath,
  dateFrom,
  dateTo,
  matterNumbers,
  folders,
  catalogFolders,
  foldersLoading,
  foldersError,
  registryStatus,
  registryDisplay,
  nonMatterFolderPage,
  matterFolderPage,
  matterSuggestions,
  matterSuggestionTotal,
  matterSuggestionsLoading,
  matterSuggestionsError,
  matterSuggestionSelections,
  matterSuggestionSubmitting,
  activeFolder,
  folderDocuments,
  folderDocumentsTotal,
  folderDocumentsHasNext,
  folderDocumentsLoading,
  folderDocumentsLoadingMore,
  folderDocumentsError,
  folderSearch,
  totalFiltered,
  totalDocuments,
  documentFirst,
  pageSize,
  onSearchChange,
  onApplyFilters,
  onRegistryStatusChange,
  onRegistryDisplayChange,
  onNonMatterFolderPageChange,
  onMatterFolderPageChange,
  onSelectSuggestedMatter,
  onAssignSuggestedMatter,
  onOpenFolder,
  onExternalFileDrop,
  onNewFolder,
  onCloseFolder,
  onFolderSearchChange,
  onRefresh,
  onClearFilters,
  onOpenDocument,
  onMoveDocument,
  onMoveFolder,
  onMergeFolder,
  onCopyFolder,
  onDropMove,
  onRenameFolder,
  onNewDocument,
  onLoadMoreFolderDocuments,
  onPreviousPage,
  onNextPage,
  bulkBusy,
  onBulkOperation,
  onStartBulkMove,
  onStartBulkCopy,
  onCopyDocument,
  onCopyDocumentToClipboard,
  onCutDocument,
  onCopyFolderToClipboard,
  onCutFolder,
  onPasteFolder,
  canPaste,
  onCutSelection,
  onCopySelection,
  onDuplicateDocument,
  onRenameDocument,
  onShowShortcuts,
  organizationFilters,
  organizationPreferences,
  registrySort,
  organizationBusy,
  onOrganizationFiltersChange,
  onOrganizationUpdate,
  onRegistrySortChange,
  onSaveOrganizationView,
  onApplyOrganizationView,
  onDeleteOrganizationView,
  onRegistryColumnsChange,
  bulkResult,
  onDismissBulkResult,
}) {
  const auth = useContext(AuthContext);
  const visibleDocuments = documents;
  const resultStart = totalFiltered > 0 ? documentFirst + 1 : 0;
  const resultEnd = Math.min(documentFirst + visibleDocuments.length, totalFiltered);
  const registryPagerStatus = documentsLoading && visibleDocuments.length === 0
    ? 'Searching…'
    : documentsError && visibleDocuments.length === 0
      ? 'Search unavailable'
      : `${resultStart}-${resultEnd} of ${totalFiltered}${totalFiltered !== totalDocuments ? ` matches · ${totalDocuments} total` : ''}${documentsError ? ' · Refresh failed' : documentsLoading ? ' · Refreshing…' : ''}`;
  const [filterDrafts, setFilterDrafts] = useState({
    matterNumber,
    folderPath,
    dateFrom,
    dateTo,
  });
  const explorerController = useDocumentFileExplorerController({
    clipboard: { keyboard: false },
  });
  const {
    fileContextMenu,
    folderContextMenu,
    openFileContextMenu,
    openFolderContextMenu,
    openFileContextMenuFromKey,
    openFolderContextMenuFromKey,
    closeFileContextMenu,
    closeFolderContextMenu,
  } = explorerController;
  const [searchDraggedItem, setSearchDraggedItem] = useState(null);
  const [searchDropTarget, setSearchDropTarget] = useState('');
  const [searchDragError, setSearchDragError] = useState('');
  const [registryUploadTarget, setRegistryUploadTarget] = useState(false);
  const [draggedRegistryDocument, setDraggedRegistryDocument] = useState(null);
  const [foldersTabDropTarget, setFoldersTabDropTarget] = useState(false);
  const [trashRequest, setTrashRequest] = useState(null);
  const selectionQuery = useMemo(() => JSON.stringify({
    search: debouncedSearch,
    matterNumber,
    folderPath,
    dateFrom,
    dateTo,
    registryStatus,
    organizationFilters,
    registrySort,
  }), [dateFrom, dateTo, debouncedSearch, folderPath, matterNumber, organizationFilters, registrySort, registryStatus]);
  const selection = useDocumentSelection({
    items: visibleDocuments,
    total: totalFiltered,
    queryKey: selectionQuery,
    allowAllMatching: !debouncedSearch && !dateFrom && !dateTo && registryStatus === 'all',
  });
  const bulkSelectionPayload = useCallback(

  () => ({
    ...selection.payload,
    ...(selection.allMatching ? {
      query: {
        matter_number: matterNumber,
        folder_path: folderPath,
        date_from: dateFrom,
        date_to: dateTo,
        registry_status: registryStatus,
      },
    } : {}),
  }), [dateFrom, dateTo, folderPath, matterNumber, registryStatus, selection.allMatching, selection.payload]);
  const requestBulkTrash = useCallback(

  () => {
    if (!selection.count || bulkBusy) return;
    setTrashRequest({
      count: selection.count,
      selection: bulkSelectionPayload(),
      clearSelection: selection.clear,
      returnFocus: document.activeElement,
    });
  }, [bulkBusy, bulkSelectionPayload, selection.clear, selection.count]);
  const requestDocumentTrash = useCallback(

  (document) => {
    const documentSelection = documentClipboardSelection('file', document);
    if (!documentSelection || bulkBusy) return;
    setTrashRequest({
      count: 1,
      selection: documentSelection,
      clearSelection: null,
      returnFocus: window.document.activeElement,
    });
  }, [bulkBusy]);
  const canTrashFolderItem = useCallback(

  (folder) => canTrashFolder(folder, auth.isAdmin), [auth.isAdmin]);
  const requestFolderTrash = useCallback(

  (folder) => {
    if (!canTrashFolderItem(folder) || bulkBusy) return;
    const folderSelection = documentClipboardSelection('folder', folder);
    if (!folderSelection) return;
    setTrashRequest({
      count: 1,
      folderName: folder.name || folder.folder_path || 'Folder',
      selection: folderSelection,
      clearSelection: null,
      returnFocus: window.document.activeElement,
    });
  }, [canTrashFolderItem, bulkBusy]);
  const closeTrashRequest = useCallback(

  () => {
    setTrashRequest((current) => {
      window.setTimeout(() => current?.returnFocus?.focus?.(), 0);
      return null;
    });
  }, []);
  const confirmBulkTrash = useCallback(

  () => {
    if (!trashRequest || bulkBusy) return;
    onBulkOperation('trash', trashRequest.selection, trashRequest.clearSelection)
      .then(() => closeTrashRequest())
      .catch(() => null);
  }, [bulkBusy, closeTrashRequest, onBulkOperation, trashRequest]);
  useEffect(() => { setTrashRequest(null); }, [selectionQuery]);
  useEffect(() => {
    const handleShortcut =

    (event) => {
      const target = event.target;
      if (target?.matches?.('input, textarea, select, [contenteditable="true"]')) return;
      const command = event.metaKey || event.ctrlKey;
      if (command && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        selection.selectPage();
      } else if (command && event.key.toLowerCase() === 'c' && selection.count) {
        event.preventDefault();
        onCopySelection(bulkSelectionPayload());
      } else if (command && event.key.toLowerCase() === 'x' && selection.count) {
        event.preventDefault();
        onCutSelection(bulkSelectionPayload());
      } else if (event.key === 'Delete' && selection.count) {
        event.preventDefault();
        requestBulkTrash();
      } else if (event.key === 'F2' && selection.count === 1) {
        const selected = selection.selectedVisibleItems[0];
        if (selected) {
          event.preventDefault();
          onRenameDocument(selected);
        }
      } else if (event.key === '?' && !command) {
        onShowShortcuts();
      } else if (event.key === 'Escape' && selection.count) {
        selection.clear();
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [bulkSelectionPayload, onCopySelection, onCutSelection, onRenameDocument, onShowShortcuts, requestBulkTrash, selection]);
  useEffect(() => {
    setFilterDrafts({
      matterNumber,
      folderPath,
      dateFrom,
      dateTo,
    });
  }, [dateFrom, dateTo, folderPath, matterNumber]);
  const filtersDirty = (
    filterDrafts.matterNumber !== matterNumber ||
    filterDrafts.folderPath !== folderPath ||
    filterDrafts.dateFrom !== dateFrom ||
    filterDrafts.dateTo !== dateTo
  );
  const updateFilterDraft =

  (name, value) => {
    const nextDrafts = {
      ...filterDrafts,
      [name]: value,
      ...(name === 'matterNumber' ? { folderPath: '' } : {}),
    };
    setFilterDrafts(nextDrafts);
    if (['matterNumber', 'folderPath'].includes(name)) {
      onNonMatterFolderPageChange(0);
      onMatterFolderPageChange(0);
      // Location fields are live folder filters. Commit them immediately so
      // opening a drawer or document cannot discard what the user typed.
      onApplyFilters(nextDrafts);
    }
  };
  const applyFilterDrafts =

  () => onApplyFilters(filterDrafts);
  const applyFiltersOnEnter =

  (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilterDrafts();
    }
  };
  const registryColumns = ['name', 'location', 'modified'];
  const registryDropFolder = useMemo(() => {
    const exact = catalogFolders.find((folder) => (
      String(folder.matter_number || '') === String(matterNumber || '') &&
      normalizeFolderPath(folder.folder_path) === normalizeFolderPath(folderPath)
    ));
    if (exact) return exact;
    return {
      folder_type: folderPath ? 'location' : matterNumber ? 'matter' : 'location',
      matter_number: matterNumber || '',
      folder_path: folderPath || '',
      name: folderPath ? folderTitle(folderPath) : matterNumber || 'Unfiled',
    };
  }, [catalogFolders, folderPath, matterNumber]);
  const normalizedUnifiedSearch = String(search || '').trim().toLocaleLowerCase();
  const unifiedSearchActive = registryDisplay === 'files' && (
    normalizedUnifiedSearch.length >= 3 ||
    Boolean(String(filterDrafts.matterNumber || '').trim()) ||
    Boolean(String(filterDrafts.folderPath || '').trim())
  );
  const unifiedFilesReady = unifiedSearchActive && normalizedUnifiedSearch === String(debouncedSearch || '').trim().toLocaleLowerCase();
  const openFolderInExplorer =

  (folder) => {
    if (registryDisplay === 'files') onRegistryDisplayChange('folders');
    onOpenFolder(folder);
  };
  const matchingSearchFolders = useMemo(

  () => {
    if (!unifiedSearchActive) return [];
    const browserFolders = catalogFolders.filter((folder) => !isHiddenFolderBrowserRoot(folder));
    const byIdentity = new Map(browserFolders.map((folder) => [folderBrowserIdentity(folder), folder]));
    const documentIdentities = new Set();
    if (unifiedFilesReady) {
      documents.forEach((document) => {
        documentFolderSearchIdentities(document).forEach((identity) => documentIdentities.add(identity));
      });
    }
    const matterTerm = String(filterDrafts.matterNumber || '').trim().toLocaleLowerCase();
    const folderTerm = String(filterDrafts.folderPath || '').trim().toLocaleLowerCase();
    const caseMatchedMatterKeys = new Set(browserFolders
      .filter((folder) => folder.folder_type === 'matter' && folderCaseSearchText(folder).includes(normalizedUnifiedSearch))
      .map((folder) => String(folder.matter_number || '').trim().toLocaleLowerCase())
      .filter(Boolean));
    const visible = new Map();
    const caseCandidates = new Map();
    const addWithAncestors =

    (folder) => {
      let current = folder;
      while (current && !visible.has(folderBrowserIdentity(current))) {
        visible.set(folderBrowserIdentity(current), current);
        current = byIdentity.get(folderBrowserParentIdentity(current));
      }
    };

    browserFolders.forEach(

    (folder) => {
      if (matterTerm && !String(folder.matter_number || '').toLocaleLowerCase().includes(matterTerm)) return;
      if (folderTerm && !folderSearchText(folder).includes(folderTerm)) return;
      const directMatch = (
        folderDirectSearchText(folder).includes(normalizedUnifiedSearch) ||
        (normalizedUnifiedSearch.includes('/') && [
          folder?.folder_path,
          folder?.scope_folder_path,
          folder?.source_simul_folder_path,
        ].filter(Boolean).some((path) => String(path).toLocaleLowerCase().includes(normalizedUnifiedSearch)))
      );
      const documentMatch = (
        caseMatchedMatterKeys.size === 0 &&
        Array.from(folderSearchIdentities(folder)).some((identity) => documentIdentities.has(identity))
      );
      if (directMatch || documentMatch) {
        addWithAncestors(folder);
        return;
      }
      if (!folderCaseSearchText(folder).includes(normalizedUnifiedSearch)) return;
      const matterKey = String(folder.matter_number || folderBrowserIdentity(folder)).toLocaleLowerCase();
      const current = caseCandidates.get(matterKey);
      const score = folder.folder_type === 'matter' ? 0 : 10 + nonnegativeInteger(folder.depth, 99);
      if (!current || score < current.score) caseCandidates.set(matterKey, { folder, score });
    });
    caseCandidates.forEach(({ folder }) => addWithAncestors(folder));

    return Array.from(visible.values()).sort(

    (left, right) => {
      const leftMatter = String(left.matter_number || '');
      const rightMatter = String(right.matter_number || '');
      if (leftMatter !== rightMatter) return leftMatter.localeCompare(rightMatter);
      const leftPath = folderBrowserPath(left);
      const rightPath = folderBrowserPath(right);
      const depthDifference = leftPath.split('/').filter(Boolean).length - rightPath.split('/').filter(Boolean).length;
      return depthDifference || leftPath.localeCompare(rightPath);
    });
  }, [catalogFolders, documents, filterDrafts.folderPath, filterDrafts.matterNumber, normalizedUnifiedSearch, unifiedFilesReady, unifiedSearchActive]);
  const columnLabels = {
    name: 'Document', location: 'Matter / folder', created: 'Created', modified: 'Updated', size: 'Size',
    type: 'Type', owner: 'Owner', versions: 'Versions', tags: 'Tags', source: 'Source',
  };
  const registryColumnTemplate = registryColumns.map((column) => column === 'name' ? 'minmax(14rem, 2fr)' : 'minmax(6rem, 1fr)').join(' ');
  const visibleFolderInventory = catalogFolders.filter((folder) => !isHiddenFolderBrowserRoot(folder)).length;
  const createDestinationFolder = activeFolder?.folder_type === 'simul' ? null : activeFolder;
  const browseDragOver =

  (event) => {
    if (!draggedRegistryDocument || isExternalFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setFoldersTabDropTarget(true);
  };
  const browseDrop =

  (event) => {
    if (!draggedRegistryDocument || isExternalFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    const document = draggedRegistryDocument;
    setDraggedRegistryDocument(null);
    setFoldersTabDropTarget(false);
    onMoveDocument(document);
  };
  const renderRegistryColumn =

  (document, column) => {
    if (column === 'name') return <span className="documents-workspace-registry-title" title={document.filename}>
      {document.thumbnail_url && <span className="documents-workspace-registry-file-signifier" aria-hidden="true">
        <img src={document.thumbnail_url} alt="" loading="lazy" />
      </span>}
      <span><Highlight text={document.filename || 'Untitled document'} term={debouncedSearch} /></span>
      <small className="documents-workspace-registry-signals">{documentListSignals(document)}</small>
      {(document.tags || []).length > 0 && <small className="documents-workspace-registry-tags">{document.tags.slice(0, 3).map((tag) => <span key={tag}>{tag}</span>)}</small>}
      {document.match?.snippet && <small className="documents-workspace-registry-match"><Highlight text={document.match.snippet} term={debouncedSearch} /></small>}
    </span>;
    if (column === 'location') return <span>{document.matter_number
      ? `Matter ${document.matter_number}${document.simul_folder_path ? ` · ${document.simul_folder_path}` : document.folder_path ? ` / ${document.folder_path}` : ''}`
      : document.simul_folder_path || document.folder_path || document.collection || 'Unfiled'}</span>;
    if (column === 'created') return <span>{document.created_at ? formatCreated(document.created_at).date : '—'}</span>;
    if (column === 'modified') return <span>{document.modified_at || document.updated_at ? formatCreated(document.modified_at || document.updated_at).date : '—'}</span>;
    if (column === 'size') return <span>{formatFileSize(document.size_bytes)}</span>;
    if (column === 'type') return <span>{String(document.extension || 'file').toUpperCase()}</span>;
    if (column === 'owner') return <span>{document.owner_name || document.owner || document.created_by_display || '—'}</span>;
    if (column === 'versions') return <span>{versionSummary(document)}</span>;
    if (column === 'tags') return <span>{(document.tags || []).join(', ') || '—'}</span>;
    if (column === 'source') return <span>{documentSourceLabel(document)}</span>;
    return null;
  };

  return (
    <section className="documents-workspace-registry" aria-label="Document registry">
      <RecentDocuments
        documents={recentDocuments}
        loading={recentDocumentsLoading}
        error={recentDocumentsError}
        onOpenDocument={onOpenDocument} />
      <section className={`documents-folder-workspace-shell is-${registryDisplay}`} aria-label="Folder workspace">
        <header className="documents-folder-workspace-header">
          <span className="documents-folder-workspace-title">
            <strong>Folders</strong>
            <small>{visibleFolderInventory} {visibleFolderInventory === 1 ? 'folder' : 'folders'}</small>
          </span>
          <FolderWorkspaceModeSwitch
            display={registryDisplay}
            matterSuggestionTotal={matterSuggestionTotal}
            browseDropTarget={foldersTabDropTarget}
            onBrowseDragOver={browseDragOver}
            onBrowseDragLeave={() => setFoldersTabDropTarget(false)}
            onBrowseDrop={browseDrop}
            onChange={onRegistryDisplayChange} />
          <span className="documents-folder-workspace-actions">
            {['folders', 'files'].includes(registryDisplay) && <div className="documents-workspace-status-segment" role="tablist" aria-label="Registry status">
              {[
                ['all', 'All', 'pi pi-inbox'],
                ['locked', 'Locked', 'pi pi-lock'],
              ].map(([value, label, icon]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={registryStatus === value}
                  className={registryStatus === value ? 'is-active' : ''}
                  onClick={() => onRegistryStatusChange(value)}>
                  <AppIcon icon={icon} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>}
            {registryDisplay === 'folders' && <>
              <span className="documents-workspace-folder-actions">
                <button
                  type="button"
                  className="is-labeled"
                  onClick={() => onNewFolder(createDestinationFolder)}
                  aria-label={createDestinationFolder ? 'New subfolder' : 'New folder'}
                  title={createDestinationFolder ? 'Create a subfolder here' : 'Create a folder'}>
                  <AppIcon icon="pi pi-folder-plus" aria-hidden="true" />
                  <span>{createDestinationFolder ? 'New subfolder' : 'New folder'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => onNewDocument(createDestinationFolder)}
                  aria-label="New Document"
                  title={createDestinationFolder ? 'Create a document here' : 'Create a document'}>
                  <AppIcon icon="pi pi-file-plus" aria-hidden="true" />
                </button>
              </span>
            </>}
          </span>
        </header>
      {registryDisplay !== 'folders' && <div className={`documents-workspace-registry-controls ${['review', 'templates'].includes(registryDisplay) ? 'is-review' : ''} ${registryDisplay === 'files' ? 'is-files' : ''}`} aria-label="Document registry controls">
        {registryDisplay === 'review' ? (
          <div className="documents-matter-review-intro">
            <span className="documents-matter-review-heading-icon" aria-hidden="true"><AppIcon icon="pi pi-sitemap" /></span>
            <span>
              <strong>Matter review</strong>
              <small>Resolve documents where the matching signals point to more than one matter.</small>
            </span>
          </div>
        ) : registryDisplay === 'templates' ? (
          <div className="documents-matter-review-intro">
            <span className="documents-matter-review-heading-icon" aria-hidden="true"><AppIcon icon="pi pi-clone" /></span>
            <span>
              <strong>Template workspace</strong>
              <small>Versioned source documents with explicit publication.</small>
            </span>
          </div>
        ) : <><label className={`documents-workspace-search ${registryDisplay === 'files' ? 'is-files' : ''}`}>
          <span className={registryDisplay === 'files' ? 'documents-visually-hidden' : ''}>Search folders and files</span>
          <span className="documents-workspace-search-field">
            <AppIcon icon="pi pi-search" size="1rem" weight="bold" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                onSearchChange(event.target.value);
                onNonMatterFolderPageChange(0);
                onMatterFolderPageChange(0);
              }}
              aria-label={registryDisplay === 'files' ? 'Search folders and files' : undefined}
              placeholder="Name, content, client, defendant, matter, or folder" />
          </span>
          {search.trim().length > 0 && search.trim().length < 3 && <small>Type 3 characters to search</small>}
        </label>
        {['folders', 'files'].includes(registryDisplay) && <div className="documents-workspace-filter-groups" role="group" aria-label="Search filters">
          <fieldset className="documents-workspace-filter-group is-location">
            <legend><AppIcon icon="pi pi-map-marker" aria-hidden="true" />Location</legend>
            <div>
              <label>
                <span>Matter</span>
                <input
                  list="documents-workspace-matters"
                  value={filterDrafts.matterNumber}
                  onChange={(event) => updateFilterDraft('matterNumber', event.target.value)}
                  onKeyDown={applyFiltersOnEnter}
                  placeholder="All matters" />
                <datalist id="documents-workspace-matters">
                  {matterNumbers.map((value) => <option key={value} value={value} />)}
                </datalist>
              </label>
              <label>
                <span>Folder</span>
                <input
                  list="documents-workspace-folders"
                  value={filterDrafts.folderPath}
                  onChange={(event) => updateFilterDraft('folderPath', event.target.value)}
                  onKeyDown={applyFiltersOnEnter}
                  placeholder="Filter folder name or path" />
                <datalist id="documents-workspace-folders">
                  {folders.map((folder) => <option key={`${folder.matter_number}:${folder.folder_path}`} value={folder.folder_path} />)}
                </datalist>
              </label>
            </div>
          </fieldset>
          <fieldset className="documents-workspace-filter-group is-date">
            <legend><AppIcon icon="pi pi-calendar" aria-hidden="true" />Version date</legend>
            <div>
              <label>
                <span>Version created from</span>
                <input type="date" value={filterDrafts.dateFrom} onChange={(event) => updateFilterDraft('dateFrom', event.target.value)} />
              </label>
              <label>
                <span>Version created through</span>
                <input type="date" value={filterDrafts.dateTo} onChange={(event) => updateFilterDraft('dateTo', event.target.value)} />
              </label>
            </div>
          </fieldset>
        </div>}</>}
        {['folders', 'files'].includes(registryDisplay) && <div className="documents-workspace-registry-actions">
          <button
            type="button"
            className={`documents-workspace-action-button ${filtersDirty ? 'is-primary' : 'is-secondary'}`}
            disabled={!filtersDirty}
            onClick={applyFilterDrafts}>
            Apply filters
          </button>
          <button
            type="button"
            className="documents-workspace-action-button is-secondary"
            onClick={onRefresh}>
            <AppIcon icon="pi pi-refresh" aria-hidden="true" />
            Refresh
          </button>
          <button
            type="button"
            className="documents-workspace-action-button is-quiet"
            onClick={onClearFilters}>
            Clear filters
          </button>
        </div>}
        {registryDisplay === 'files' && <RegistryOrganizationControls
          sort={registrySort}
          onSortChange={onRegistrySortChange} />}
      </div>}

      {!['review', 'templates'].includes(registryDisplay) && documentsError && !unifiedSearchActive && <div className="documents-archive-error" role="alert">{documentsError}</div>}
      {!['review', 'templates'].includes(registryDisplay) && !documentsError && documentsWarning && (
        <div className="documents-archive-warning" role="status">
          <AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" />
          <span>{documentsWarning}</span>
        </div>
      )}

      {registryDisplay === 'templates' ? (
        <TemplateWorkspace onOpenDocument={onOpenDocument} />
      ) : registryDisplay === 'review' ? (
        <AmbiguousMatterReview
          suggestions={matterSuggestions}
          total={matterSuggestionTotal}
          loading={matterSuggestionsLoading}
          error={matterSuggestionsError}
          matterNumbers={matterNumbers}
          selections={matterSuggestionSelections}
          submitting={matterSuggestionSubmitting}
          onSelectMatter={onSelectSuggestedMatter}
          onAssignMatter={onAssignSuggestedMatter}
          onOpenDocument={onOpenDocument} />
      ) : registryDisplay === 'files' && unifiedSearchActive ? (
          <div className="documents-unified-search-results" aria-label={`Search results for ${search.trim()}`}>
            <section className="documents-unified-search-group" aria-label="Matching folders">
              <header>
                <span>
                  <AppIcon icon="pi pi-folder" aria-hidden="true" />
                  <strong>Folders</strong>
                </span>
                <small>{foldersLoading ? 'Searching…' : `${matchingSearchFolders.length} ${matchingSearchFolders.length === 1 ? 'match' : 'matches'}`}</small>
              </header>
              {foldersError ? <div className="documents-archive-error" role="alert">{foldersError}</div> : foldersLoading ? (
                <div className="documents-unified-search-empty">Searching folders…</div>
              ) : matchingSearchFolders.length === 0 ? (
                <div className="documents-unified-search-empty">No folders or matters match.</div>
              ) : (
                <div className="documents-unified-search-list">
                  {matchingSearchFolders.slice(0, 100).map(

                  (folder) => {
                    const identity = folderBrowserIdentity(folder);
                    const pathLabel = folderBrowserPath(folder) || folder.folder_path || folder.name || 'Folder';
                    const partyLabel = folderPartyLabel(folder);
                    const title = folder.folder_type === 'matter' && partyLabel
                      ? partyLabel
                      : folder.name || folderTitle(pathLabel);
                    const location = [
                      folder.matter_number ? `Matter ${folder.matter_number}` : folderContext(folder),
                      folderBrowserPath(folder),
                    ].filter(Boolean).join(' · ');
                    const canDrag = Boolean(folder.can_move_internally && folder.folder_id);
                    return (
                      <div
                        key={identity}
                        className={`documents-unified-search-row is-folder ${searchDropTarget === identity ? 'is-drop-target' : ''}`}
                        draggable={canDrag}
                        data-upload-label={searchDropTarget === identity ? folderUploadLabel(folder) : undefined}
                        onDragStart={canDrag ?

                        (event) => {
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', `folder:${folder.folder_id}`);
                          setSearchDraggedItem({ type: 'folder', item: folder });
                          setSearchDragError('');
                        } : undefined}
                        onDragEnd={() => {
                          setSearchDraggedItem(null);
                          setSearchDropTarget('');
                        }}
                        onDragOver={

                        (event) => {
                          if (!isExternalFileDrag(event.dataTransfer) && !searchDraggedItem) return;
                          if (searchDraggedItem?.type === 'folder' && String(searchDraggedItem.item.folder_id || '') === String(folder.folder_id || '')) return;
                          event.preventDefault();
                          event.dataTransfer.dropEffect = isExternalFileDrag(event.dataTransfer) ? 'copy' : 'move';
                          setSearchDropTarget(identity);
                        }}
                        onDragLeave={(event) => {
                          if (!event.currentTarget.contains(event.relatedTarget)) setSearchDropTarget('');
                        }}
                        onDrop={

                        async (event) => {
                          if (isExternalFileDrag(event.dataTransfer)) {
                            event.preventDefault();
                            const transfer = event.dataTransfer;
                            setSearchDropTarget('');
                            onExternalFileDrop(folder, transfer);
                            return;
                          }
                          if (!searchDraggedItem) return;
                          event.preventDefault();
                          event.stopPropagation();
                          const moving = searchDraggedItem;
                          setSearchDraggedItem(null);
                          setSearchDropTarget('');
                          try {
                            await onDropMove(moving, folder);
                          } catch (requestError) {
                            setSearchDragError(requestError?.response?.data?.error || requestError.message || `Could not move ${moving.type}.`);
                          }
                        }}
                        onContextMenu={(event) => openFolderContextMenu(event, folder)}>
                        <button
                          type="button"
                          aria-label={`Open folder ${pathLabel}`}
                          onClick={() => openFolderInExplorer(folder)}
                          onKeyDown={(event) => {
                            openFolderContextMenuFromKey(event, folder);
                          }}>
                          <span className="documents-unified-search-icon" aria-hidden="true"><AppIcon icon="pi pi-folder" /></span>
                          <span>
                            <strong><Highlight text={title} term={search.trim()} /></strong>
                            <small><Highlight text={location || pathLabel} term={search.trim()} /></small>
                          </span>
                        </button>
                      </div>
                    );
                  })}
                  {matchingSearchFolders.length > 100 && (
                    <div className="documents-unified-search-limit">Showing the first 100 folders. Add another word to narrow the results.</div>
                  )}
                </div>
              )}
            </section>

            <section className="documents-unified-search-group" aria-label="Matching files">
              <header>
                <span>
                  <AppIcon icon="pi pi-file" aria-hidden="true" />
                  <strong>Files</strong>
                </span>
                <span className="documents-unified-search-file-actions">
                  <small>{!unifiedFilesReady || documentsLoading ? 'Searching…' : `${totalFiltered} ${totalFiltered === 1 ? 'match' : 'matches'}`}</small>
                </span>
              </header>
              {documentsError && documents.length > 0 && (
                <div className="documents-archive-error" role="alert">{documentsError}</div>
              )}
              {!unifiedFilesReady || (documentsLoading && documents.length === 0) ? (
                <div className="documents-unified-search-empty">Searching document names and contents…</div>
              ) : documentsError && documents.length === 0 ? (
                <div className="documents-archive-error" role="alert">{documentsError}</div>
              ) : documents.length === 0 ? (
                <div className="documents-unified-search-empty">No files match.</div>
              ) : (
                <div className="documents-unified-search-list">
                  {visibleDocuments.map((document) => {
                    const location = document.matter_number
                      ? `Matter ${document.matter_number}${document.folder_path ? ` · ${document.folder_path}` : ''}`
                      : document.simul_folder_path || document.folder_path || document.collection || 'Unfiled';
                    const canDrag = document.available_locally !== false;
                    return (
                      <div
                        key={document.document_id}
                        className="documents-unified-search-row is-file"
                        draggable={canDrag}
                        onDragStart={canDrag ?

                        (event) => {
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', `file:${document.document_id}`);
                          setSearchDraggedItem({ type: 'file', item: document });
                          setSearchDragError('');
                        } : undefined}
                        onDragEnd={() => {
                          setSearchDraggedItem(null);
                          setSearchDropTarget('');
                        }}
                        onContextMenu={(event) => openFileContextMenu(event, document)}>
                        <button
                          type="button"
                          aria-label={`${shouldUseDocumentFileViewer(document) ? 'Preview file' : 'Open document workspace'}: ${document.filename || 'Untitled document'}`}
                          onClick={() => onOpenDocument(document.document_id)}
                          onKeyDown={(event) => {
                            openFileContextMenuFromKey(event, document);
                          }}>
                          {document.thumbnail_url && <span className="documents-unified-search-icon" aria-hidden="true">
                            <img src={document.thumbnail_url} alt="" />
                          </span>}
                          <span>
                            <strong><Highlight text={document.filename || 'Untitled document'} term={search.trim()} /></strong>
                            <small><Highlight text={location} term={search.trim()} /></small>
                            {document.match?.snippet && <small className="documents-unified-search-snippet"><Highlight text={document.match.snippet} term={search.trim()} /></small>}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            <DocumentRegistryPager
              status={registryPagerStatus}
              loading={documentsLoading}
              documentFirst={documentFirst}
              pageSize={pageSize}
              totalFiltered={totalFiltered}
              onPreviousPage={onPreviousPage}
              onNextPage={onNextPage} />
            <InlineOperationError message={searchDragError} />
            <FileContextMenu
              menu={fileContextMenu}
              onClose={closeFileContextMenu}
              onMove={onMoveDocument}
              onCopy={onCopyDocument}
              onCopyToClipboard={onCopyDocumentToClipboard}
              onCut={onCutDocument}
              onDuplicate={onDuplicateDocument}
              onRename={onRenameDocument}
              onTrash={requestDocumentTrash} />
            <FolderContextMenu
              menu={folderContextMenu}
              onClose={closeFolderContextMenu}
              onMove={onMoveFolder}
              onTrash={requestFolderTrash}
              canTrash={canTrashFolderItem}
              onMergeWithMatter={onMergeFolder}
              canMergeWithMatter={(folder) => folderDestinationScope(folder, catalogFolders) === 'internal'}
              onCopy={onCopyFolder}
              onCopyToClipboard={onCopyFolderToClipboard}
              onCut={onCutFolder}
              onPaste={onPasteFolder}
              canPaste={canPaste} />
          </div>
      ) : registryDisplay === 'folders' ? (
        <FolderColumnsBrowser
          embedded
          folders={catalogFolders}
          activeFolder={activeFolder}
          documents={folderDocuments}
          total={folderDocumentsTotal}
          hasNext={folderDocumentsHasNext}
          loading={folderDocumentsLoading}
          loadingMore={folderDocumentsLoadingMore}
          error={folderDocumentsError}
          foldersLoading={foldersLoading}
          foldersError={foldersError}
          folderSearch={folderSearch}
          nonMatterPage={nonMatterFolderPage}
          matterPage={matterFolderPage}
          onNonMatterPageChange={onNonMatterFolderPageChange}
          onMatterPageChange={onMatterFolderPageChange}
          onOpenFolder={openFolderInExplorer}
          onExternalFileDrop={onExternalFileDrop}
          onCloseFolder={onCloseFolder}
          onFolderSearchChange={onFolderSearchChange}
          onOpenDocument={onOpenDocument}
          onMoveDocument={onMoveDocument}
        onRenameDocument={onRenameDocument}
          onTrashDocument={requestDocumentTrash}
          onMoveFolder={onMoveFolder}
          onTrashFolder={requestFolderTrash}
          canTrashFolderItem={canTrashFolderItem}
          onMergeFolder={onMergeFolder}
          onCopyFolder={onCopyFolder}
          onCopyDocumentToClipboard={onCopyDocumentToClipboard}
          onCutDocument={onCutDocument}
          onCopyFolderToClipboard={onCopyFolderToClipboard}
          onCutFolder={onCutFolder}
          onPasteFolder={onPasteFolder}
          canPaste={canPaste}
          onDropMove={onDropMove}
          onRenameFolder={onRenameFolder}
          onNewDocument={onNewDocument}
          onNewFolder={onNewFolder}
          onLoadMore={onLoadMoreFolderDocuments}
          locked={registryStatus === 'locked'}
          onShowAllFolders={() => onRegistryStatusChange('all')} />
      ) : (
        <>
          <BulkSelectionBar
            selection={selection}
            total={totalFiltered}
            allowAllMatching={!debouncedSearch && !dateFrom && !dateTo && registryStatus === 'all'}
            busy={bulkBusy}
            onMove={() => onStartBulkMove(bulkSelectionPayload(), selection.clear, selection.count)}
            onCopy={() => onStartBulkCopy(bulkSelectionPayload(), selection.clear, selection.count)}
            onDownload={() => onBulkOperation('download', bulkSelectionPayload(), selection.clear).catch(() => null)}
            onTrash={requestBulkTrash} />
          {bulkResult && (
            <section
              className={`documents-bulk-result is-${bulkResult.failed.length ? bulkResult.completed.length ? 'partial' : 'failed' : 'success'}`}
              role={bulkResult.failed.length && !bulkResult.completed.length ? 'alert' : 'status'}
              aria-live="polite">
              <div>
                <strong>{bulkResult.failed.length
                  ? bulkResult.completed.length ? 'Some items need attention' : 'Bulk action failed'
                  : 'Bulk action completed'}</strong>
                <span>{bulkResult.completed.length} completed{bulkResult.failed.length ? ` · ${bulkResult.failed.length} failed` : ''}</span>
              </div>
              {bulkResult.failed.length > 0 && (
                <ul>{bulkResult.failed.slice(0, 5).map((result, index) => (
                  <li key={`${result.item_type || 'item'}-${result.item_id || index}`}>{result.error || 'This item could not be changed.'}</li>
                ))}</ul>
              )}
              <button type="button" aria-label="Dismiss bulk action result" onClick={onDismissBulkResult}>×</button>
            </section>
          )}
          <div
            className={`documents-workspace-registry-table is-list ${registryUploadTarget ? 'is-upload-drop-target' : ''}`}
            data-upload-label={registryUploadTarget ? folderUploadLabel(registryDropFolder) : undefined}
            onDragEnter={

            (event) => {
              if (!isExternalFileDrag(event.dataTransfer)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'copy';
              setRegistryUploadTarget(true);
            }}
            onDragOver={

            (event) => {
              if (!isExternalFileDrag(event.dataTransfer)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'copy';
              setRegistryUploadTarget(true);
            }}
            onDragLeave={(event) => {
              if (isExternalFileDrag(event.dataTransfer) && !event.currentTarget.contains(event.relatedTarget)) setRegistryUploadTarget(false);
            }}
            onDrop={

            (event) => {
              if (!isExternalFileDrag(event.dataTransfer)) return;
              event.preventDefault();
              const transfer = event.dataTransfer;
              setRegistryUploadTarget(false);
              onExternalFileDrop(registryDropFolder, transfer);
            }}>
            <div className="documents-workspace-registry-head" style={{ gridTemplateColumns: `2.5rem ${registryColumnTemplate}` }}>
              <label className="documents-selection-checkbox">
                <input
                  type="checkbox"
                  aria-label="Select all documents on this page"
                  checked={selection.pageAllSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = selection.pageSelectedCount > 0 && !selection.pageAllSelected;
                  }}
                  onChange={() => selection.pageAllSelected ? selection.deselectPage() : selection.selectPage()} />
                <span aria-hidden="true" />
              </label>
              {registryColumns.map((column) => <span key={column}>{columnLabels[column] || column}</span>)}
            </div>
            {documentsLoading && visibleDocuments.length === 0 ? (
              <div className="documents-archive-empty">Loading documents...</div>
            ) : documentsError && visibleDocuments.length === 0 ? null : visibleDocuments.length === 0 ? (
              <div className="documents-archive-empty">No documents match this view.</div>
            ) : (
              <div className="documents-workspace-registry-list">
                {visibleDocuments.map((document) => {
                  return (
                    <div
                      key={document.document_id}
                      className={`documents-workspace-registry-row ${selection.isSelected(document.document_id) ? 'is-selected' : ''}`}
                      draggable={document.available_locally !== false}
                      onDragStart={document.available_locally === false ? undefined : (event) => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', `file:${document.document_id}`);
                        setDraggedRegistryDocument(document);
                      }}
                      onDragEnd={() => {
                        setDraggedRegistryDocument(null);
                        setFoldersTabDropTarget(false);
                      }}
                      onClick={(event) => {
                        if (event.shiftKey) {
                          event.preventDefault();
                          selection.toggle(document.document_id, { range: true });
                          return;
                        }
                        onOpenDocument(document.document_id);
                      }}
                      onContextMenu={(event) => openFileContextMenu(event, document)}>
                      <label
                        className="documents-selection-checkbox documents-selection-checkbox--row"
                        onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${document.filename || 'Untitled document'}`}
                          checked={selection.isSelected(document.document_id)}
                          onChange={(event) => selection.toggle(document.document_id, { range: event.nativeEvent.shiftKey })} />
                        <span aria-hidden="true" />
                      </label>
                      <button
                        type="button"
                        className="documents-workspace-registry-row-open"
                        style={{ gridTemplateColumns: registryColumnTemplate }}
                        aria-label={`${shouldUseDocumentFileViewer(document) ? 'Preview file' : 'Open document workspace'}: ${document.filename || 'Untitled document'}`}
                        onKeyDown={(event) => {
                          openFileContextMenuFromKey(event, document);
                        }}>
                        {registryColumns.map((column) => <React.Fragment key={column}>{renderRegistryColumn(document, column)}</React.Fragment>)}
                      </button>
                      <DocumentOrganizationActions
                        document={document}
                        busy={organizationBusy === document.document_id}
                        onUpdate={onOrganizationUpdate} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DocumentRegistryPager
            status={registryPagerStatus}
            loading={documentsLoading}
            documentFirst={documentFirst}
            pageSize={pageSize}
            totalFiltered={totalFiltered}
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage} />
          <FileContextMenu
            menu={fileContextMenu}
            onClose={closeFileContextMenu}
            onMove={onMoveDocument}
            onCopy={onCopyDocument}
            onCopyToClipboard={onCopyDocumentToClipboard}
            onCut={onCutDocument}
            onDuplicate={onDuplicateDocument}
            onRename={onRenameDocument}
            onTrash={requestDocumentTrash} />
        </>
      )}
      </section>
      <DocumentTrashDialog
        request={trashRequest}
        busy={bulkBusy}
        onCancel={closeTrashRequest}
        onConfirm={confirmBulkTrash} />
    </section>
  );
}



function DocumentDetailView({
  selectedDocument,
  destinationSegments,
  versions,
  activity,
  matchingVersions,
  versionsLoading,
  previewVersion,
  activePreviewMode,
  redlineReady,
  redlineAvailable,
  comparison,
  previewRequested,
  selectedChange,
  selectedVersionId,
  changes,
  changesLoading,
  changesError,
  comparisonError,
  baseRevisionId,
  targetRevisionId,
  canCompare,
  comparisonLoading,
  comparisonRenderer,
  onBackToRegistry,
  onMoveDocument,
  onRenameDocument,
  onOpenShare,
  onRequestPreview,
  onSetPreviewMode,
  onComparisonRendererChange,
  onSelectVersion,
  onBaseRevisionChange,
  onTargetRevisionChange,
  onStartComparison,
  onCompareVersions,
  onShareLatest,
  onShareVersion,
  onEditVersionMetadata,
  onOpenUpload,
  onOpenMerge,
  onPrepareMergeReview,
  onPromoteVersion,
  isVersionPromotable,
  onUnlockVersion,
  canUpload,
  canMerge,
  mergeReplacesCurrent,
  canPrepareMergeReview,
  mergeReviewStatus,
  onEditWord,
  editStarting,
  writeSubmitting,
  onAiAdopted,
  onProofreadWordOpened,
}) {
  const [openSidebarSection, setOpenSidebarSection] = useState('versions');
  const [aiReview, setAiReview] = useState(null);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [proofreadRequest, setProofreadRequest] = useState(null);
  const [proofreadAnchor, setProofreadAnchor] = useState(null);
  const [versionComments, setVersionComments] = useState([]);

  useEffect(

  () => {
    setOpenSidebarSection('versions');
    setAiReview(null);
    setAiAvailable(false);
    setProofreadRequest(null); setProofreadAnchor(null);
    setVersionComments([]);
  }, [selectedDocument?.document_id]);

  if (!selectedDocument) {
    return (
      <section className="documents-workspace-empty-home">
        <div className="documents-archive-empty">Select a document from the search page.</div>
        <button type="button" className="documents-archive-link-button" onClick={onBackToRegistry}>Back to documents</button>
      </section>
    );
  }

  const latestVersion = currentVersion(selectedDocument, matchingVersions);
  const archiveState = archiveSafetyState(selectedDocument);
  const selectedLockOwner = lockedBy(previewVersion || latestVersion);
  const readyChanges = changes.filter((change) => isReadyChange(change));
  const aiRun = aiReview?.run || null;
  const displayedComparison = aiRun?.comparison || comparison;
  const reviewingAiChanges = Boolean(aiRun?.comparison?.comparison_id);
  const displayedRedlineReady = reviewingAiChanges
    ? Boolean(
      displayedComparison?.status === 'ready' &&
      displayedComparison.comparison_id
    )
    : redlineReady;
  const displayedRedlineAvailable = reviewingAiChanges || redlineAvailable;
  const activePairBaseId = displayedComparison?.base_revision_id || selectedChange?.base_revision_id ||
    (comparisonLoading || comparisonError ? baseRevisionId : '');
  const activePairTargetId = displayedComparison?.target_revision_id || selectedChange?.target_revision_id ||
    (comparisonLoading || comparisonError ? targetRevisionId : '');
  const activePairBase = versionById(matchingVersions, activePairBaseId);
  const activePairTarget = versionById(matchingVersions, activePairTargetId);

  return (
    <section className="documents-document-page" aria-label="Document detail">
      <VersionControlCommandBar
        selectedDocument={selectedDocument}
        destinationSegments={destinationSegments}
        archiveState={archiveState}
        previewVersion={previewVersion || latestVersion}
        comparisonBase={activePairBase}
        comparisonTarget={activePairTarget}
        comparisonRenderer={comparisonRenderer}
        activePreviewMode={activePreviewMode}
        redlineAvailable={displayedRedlineAvailable}
        changesLoading={changesLoading}
        onBackToRegistry={onBackToRegistry}
        onMoveDocument={onMoveDocument}
        onRenameDocument={onRenameDocument}
        onOpenShare={onOpenShare}
        onShareLatest={onShareLatest}
        onSetPreviewMode={onSetPreviewMode}
        onComparisonRendererChange={onComparisonRendererChange}
        onOpenAi={() => setOpenSidebarSection('ai')}
        aiAvailable={aiAvailable}
        aiExpanded={openSidebarSection === 'ai'}
        aiReview={aiReview}
        onOpenUpload={onOpenUpload}
        onOpenMerge={onOpenMerge}
        onPrepareMergeReview={onPrepareMergeReview}
        canUpload={canUpload}
        canMerge={canMerge}
        mergeReplacesCurrent={mergeReplacesCurrent}
        canPrepareMergeReview={canPrepareMergeReview}
        mergeReviewStatus={mergeReviewStatus}
        onEditWord={onEditWord}
        editStarting={editStarting}
        writeSubmitting={writeSubmitting} />

      <div className="documents-document-layout">
        <section className="documents-document-viewer" aria-label="Document viewer">
          <DocumentPreviewSurface
            commentAnchor={proofreadAnchor?.revision_id === previewVersion?.revision_id ? proofreadAnchor : null}
            activePreviewMode={activePreviewMode}
            previewVersion={previewVersion}
            documentFilename={selectedDocument?.filename}
            redlineReady={displayedRedlineReady}
            redlineAvailable={displayedRedlineAvailable}
            comparison={displayedComparison}
            comparisonLoading={comparisonLoading}
            comparisonError={comparisonError}
            previewRequested={previewRequested}
            autoLoadPreview
            condensedHeader
            onRequestPreview={onRequestPreview}
            onRetryComparison={canCompare
              ? () => onStartComparison(baseRevisionId, targetRevisionId)
              : null}
            onSetPreviewMode={onSetPreviewMode}
            className="documents-document-preview" />
        </section>

        <aside className={`documents-document-sidebar ${openSidebarSection === 'proofread' ? 'is-comments' : 'is-tools'}`} aria-label="Document review">
          <nav className="documents-review-tabs" aria-label="Review pane">
            <button type="button" aria-pressed={openSidebarSection === 'proofread'} onClick={() => setOpenSidebarSection('proofread')}>
              <AppIcon icon="pi pi-comments" /> Comments
              {versionComments.some(comment => !comment.parent_comment_id && !comment.resolved_at) && <span>{versionComments.filter(comment => !comment.parent_comment_id && !comment.resolved_at).length}</span>}
            </button>
            <button type="button" aria-pressed={openSidebarSection !== 'proofread'} onClick={() => { setOpenSidebarSection('versions'); setProofreadAnchor(null); }}><AppIcon icon="pi pi-history" /> Versions &amp; tools</button>
          </nav>
          <VersionGraphPanel
            versions={matchingVersions}
            selectedVersionId={selectedVersionId}
            comparisonBaseId={activePreviewMode === 'redline' ? activePairBaseId : ''}
            comparisonTargetId={activePreviewMode === 'redline' ? activePairTargetId : ''}
            comparison={comparison}
            comparisonLoading={comparisonLoading}
            comparisonError={comparisonError}
            selectedDocument={selectedDocument}
            onSelectVersion={(id) => { setProofreadRequest(null); setProofreadAnchor(null); onSelectVersion(id); }}
            baseRevisionId={baseRevisionId}
            targetRevisionId={targetRevisionId}
            canCompare={canCompare}
            onBaseRevisionChange={onBaseRevisionChange}
            onTargetRevisionChange={onTargetRevisionChange}
            onStartComparison={onStartComparison}
            onCompareVersions={onCompareVersions}
            onPromoteVersion={onPromoteVersion}
            isVersionPromotable={isVersionPromotable}
            onUnlockVersion={onUnlockVersion}
            onShareVersion={onShareVersion}
            onProofreadVersion={

            (version) => {
              onSelectVersion(version.revision_id); onSetPreviewMode('version');
              setProofreadRequest({ revision_id: version.revision_id, key: proofreadRequestKey() });
              setProofreadAnchor(null); setOpenSidebarSection('proofread');
            }}
            onEditMetadata={onEditVersionMetadata}
            writeSubmitting={writeSubmitting}
            expanded={openSidebarSection === 'versions'}
            onExpand={() => setOpenSidebarSection('versions')} />

          <DocumentProofreadPanel
            pane
            key={`${selectedDocument.document_id}:${(previewVersion || latestVersion)?.revision_id}`}
            documentId={selectedDocument.document_id}
            sourceVersion={previewVersion || latestVersion}
            requestKey={proofreadRequest?.revision_id === previewVersion?.revision_id ? proofreadRequest?.key : null}
            expanded={openSidebarSection === 'proofread'}
            onExpand={() => setOpenSidebarSection('proofread')}
            onCommentsChange={setVersionComments}
            onLocate={(anchor) => { onSetPreviewMode('version'); setProofreadAnchor(anchor ? { ...anchor, revision_id: previewVersion?.revision_id } : null); }}
            onWordOpened={onProofreadWordOpened} />

          <AiDocumentEditPanel
            documentId={selectedDocument.document_id}
            sourceVersion={latestVersion}
            versions={matchingVersions}
            onAdopted={onAiAdopted}
            onAvailabilityChange={setAiAvailable}
            sidebar
            expanded={openSidebarSection === 'ai'}
            onExpand={() => setOpenSidebarSection('ai')}
            onReviewChange={(nextReview) => {
              setAiReview(nextReview);
              if (nextReview?.run?.comparison?.comparison_id) {
                setOpenSidebarSection('ai');
                onSetPreviewMode('redline');
                onRequestPreview();
              } else if (nextReview === null && aiReview) {
                onSetPreviewMode('version');
              }
            }} />

          <ActivityPanel
            activity={activity}
            versions={versions}
            expanded={openSidebarSection === 'activity'}
            onExpand={() => setOpenSidebarSection('activity')} />

          <section className={`documents-sidebar-accordion-section documents-document-side-panel ${openSidebarSection === 'selection' ? 'is-expanded' : ''}`} aria-label="Version actions">
            <button
              type="button"
              className="documents-sidebar-accordion-trigger"
              aria-label="Selection"
              aria-expanded={openSidebarSection === 'selection'}
              aria-controls="documents-sidebar-selection"
              onClick={() => setOpenSidebarSection('selection')}>
              <div className="documents-archive-section-title">
                <h2>Selection</h2>
                <span>{previewVersion ? versionLabel(previewVersion) : versionsLoading ? 'Loading' : 'None'}</span>
              </div>
              <AppIcon icon={openSidebarSection === 'selection' ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden="true" />
            </button>
            <div id="documents-sidebar-selection" className="documents-sidebar-selection-content" hidden={openSidebarSection !== 'selection'}>
            <dl className="documents-document-selection-list">
              <div>
                <dt>Latest</dt>
                <dd>{latestVersion ? versionLabel(latestVersion) : '-'}</dd>
              </div>
              <div>
                <dt>Viewing</dt>
                <dd>{previewVersion ? versionLabel(previewVersion) : '-'}</dd>
              </div>
              <div>
                <dt>Path</dt>
                <dd>{branchName(previewVersion || latestVersion) === 'main' ? 'Current version history' : 'Separate draft'}</dd>
              </div>
              <div>
                <dt>Lock</dt>
                <dd>{selectedLockOwner ? `Locked by ${selectedLockOwner}` : 'Unlocked'}</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>{versionSourceLabel(previewVersion || latestVersion)}</dd>
              </div>
              <div>
                <dt>Archive</dt>
                <dd title={archiveState.detail}>{archiveState.label}</dd>
              </div>
              <div>
                <dt>Preview</dt>
                <dd>{(previewVersion || latestVersion)?.preview_status || 'pending'}</dd>
              </div>
              <div>
                <dt>Chat Index</dt>
                <dd>{(previewVersion || latestVersion)?.tree_status || 'pending'}</dd>
              </div>
              <div>
                <dt>Redlines</dt>
                <dd>{changesLoading ? 'Loading' : `${readyChanges.length} ready`}</dd>
              </div>
            </dl>
            {changesError && <div className="documents-archive-error" role="alert">{changesError}</div>}
            {comparisonError && <div className="documents-archive-error" role="alert">{comparisonError}</div>}
            {(previewVersion || latestVersion)?.preview_status === 'failed' && (
              <div className="documents-archive-error" role="alert">
                {(previewVersion || latestVersion)?.preview_error || 'Preview generation failed.'}
              </div>
            )}
            {comparison?.error && <div className="documents-archive-error" role="alert">{comparison.error}</div>}
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}



function DocumentsWorkspaceNavigation({ activePanel, isAdmin, onSelect }) {
  const items = [
    { id: '', label: 'Documents', icon: 'pi pi-folder-open' },
    { id: 'trash', label: 'Trash', icon: 'pi pi-trash' },
    ...(isAdmin ? [{ id: 'governance', label: 'Governance', icon: 'pi pi-shield' }] : []),
  ];
  return (
    <nav className="documents-workspace-navigation" aria-label="Document workspace">
      {items.map((item) => (
        <button
          key={item.id || 'documents'}
          type="button"
          className={activePanel === item.id ? 'is-active' : ''}
          aria-label={item.id === 'trash' ? 'Open deleted items' : undefined}
          aria-current={activePanel === item.id ? 'page' : undefined}
          onClick={() => onSelect(item.id)}>
          <AppIcon icon={item.icon} aria-hidden="true" />
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}



function DocumentsPage() {
  const location = useLocation();
  const store = useStore();
  const dataCache = useDocumentData();
  const params = new URLSearchParams(location.search);
  const folderId = location.pathname === '/documents' ? params.get('folder') || '' : '';
  const folderPath = location.pathname === '/documents' ? params.get('folder_path') || '' : '';
  const folderRoute = folderId ? `id:${folderId}` : folderPath ? `path:${folderPath}` : '';
  const [openedFolderRoute, setOpenedFolderRoute] = useState('');
  const [folderError, setFolderError] = useState('');

  useEffect(

  () => {
    if (!folderRoute) {
      setOpenedFolderRoute('');
      return undefined;
    }
    let cancelled = false;
    setFolderError('');
    store.dispatch(setDocumentsWorkspaceField({ field: 'documentUrl', value: '' }));
    dataCache.load('route-folder', '/documents/folders',

    (data) => {
        const response = { data };
        if (cancelled) return;
        const folder = canonicalFolderCatalog(response.data?.folders || []).find((candidate) => (
          folderId
            ? String(candidate.folder_id || '') === folderId
            : !candidate.matter_number && normalizeFolderPath(candidate.folder_path) === normalizeFolderPath(folderPath)
        ));
        if (!folder) {
          setFolderError('This folder is no longer available.');
          return;
        }
        const fields = {
          activeFolder: folder,
          documentUrl: '',
          registryDisplay: 'folders',
          registryStatus: 'all',
          search: '', matterNumber: '', folderPath: '', folderSearch: '',
          dateFrom: '', dateTo: '', documentFirst: 0,
          nonMatterFolderPage: 0, matterFolderPage: 0,
          organizationFilters: EMPTY_ORGANIZATION_FILTERS,
        };
        Object.entries(fields).forEach(([field, value]) => {
          store.dispatch(setDocumentsWorkspaceField({ field, value }));
        });
        const state = store.getState().documents;
        store.dispatch(setDocumentsFolderTabs({
          activeTabId: state.activeFolderTabId,
          tabs: state.folderTabs.map((tab) => tab.id === state.activeFolderTabId ? {
            ...tab, folderId: folder.folder_id || '', folderKey: folderBrowserIdentity(folder),
            label: folder.name || folderTitle(folder.folder_path),
            title: folder.folder_path || folder.name || 'Folder', previewDocumentId: '',
          } : tab),
        }));
        setOpenedFolderRoute(folderRoute);
      })
      .catch(() => {
        if (!cancelled) setFolderError('Could not open this folder. Please try again.');
      });
    return () => { cancelled = true; };
  }, [dataCache, folderId, folderPath, folderRoute, store]);

  return (
    <div className="documents-page">
      {folderRoute && openedFolderRoute !== folderRoute ? (
        <section className="documents-archive-empty" aria-label="Open document folder">
          {folderError ? <>
            <p role="alert">{folderError}</p>
            <Link to="/documents">Back to documents</Link>
          </> : <span role="status">Opening folder…</span>}
        </section>
      ) : <SimulArchiveReviewWorkspace />}
    </div>
  );
}



function SimulArchiveReviewWorkspace() {
  const showToast = useContext(ToastContext);
  const auth = useContext(AuthContext) || {};
  const location = useLocation();
  const navigate = useNavigate();
  const documentsStore = useStore();
  const dataCache = useDocumentData();
  const documentRoute = useMemo(() => documentsRouteState(location.pathname), [location.pathname]);
  const routedDocumentId = documentRoute.documentId;
  const routedVersionId = useMemo(
    () => new URLSearchParams(location.search).get('version') || '',
    [location.search]
  );
  const routedComparisonId = useMemo(
    () => new URLSearchParams(location.search).get('comparison') || '',
    [location.search]
  );
  const [initialRegistryState] = useState(() => documentsStore.getState().documents);
  // New sessions land in the folder browser. Returning users keep their active
  // workspace, while the chosen Miller/tree presentation remains sticky.
  const initialRegistryDisplay = ['files', 'folders', 'templates', 'review'].includes(initialRegistryState.registryDisplay)
    ? initialRegistryState.registryDisplay
    : 'folders';
  const initialDocumentUrl = storedDocumentUrl(initialRegistryState);
  const [documents, setDocuments] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [recentDocumentsLoading, setRecentDocumentsLoading] = useState(true);
  const [recentDocumentsError, setRecentDocumentsError] = useState('');
  const [versions, setVersions] = useState([]);
  const [activity, setActivity] = useState([]);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(() => routedDocumentId || '');
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [previewVersionId, setPreviewVersionId] = useState('');
  const [search, setSearch] = useDocumentsWorkspaceField('search');
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    const restoredSearch = storedString(initialRegistryState, 'search').trim();
    return restoredSearch.length >= 3 ? restoredSearch : '';
  });
  const [matterNumber, setMatterNumber] = useDocumentsWorkspaceField('matterNumber');
  const [folderPath, setFolderPath] = useDocumentsWorkspaceField('folderPath');
  const [dateFrom, setDateFrom] = useDocumentsWorkspaceField('dateFrom');
  const [dateTo, setDateTo] = useDocumentsWorkspaceField('dateTo');
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [documentsWarning, setDocumentsWarning] = useState('');
  const [comparison, setComparison] = useState(INITIAL_COMPARISON);
  const [comparisonRenderer, setComparisonRenderer] = useState(readComparisonRenderer);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState('');
  const [changes, setChanges] = useState([]);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changesError, setChangesError] = useState('');
  const [selectedChangeId, setSelectedChangeId] = useState('');
  const [baseRevisionId, setBaseRevisionId] = useState('');
  const [targetRevisionId, setTargetRevisionId] = useState('');
  const [previewMode, setPreviewMode] = useState('version');
  const [previewRequested, setPreviewRequested] = useState(false);
  const [workspaceMode, setWorkspaceMode] = useState(() => routedDocumentId ? 'document' : 'registry');
  const [registryStatus, setRegistryStatus] = useDocumentsWorkspaceField('registryStatus');
  const [registryDisplay, setRegistryDisplay] = useDocumentsWorkspaceField('registryDisplay');
  const [registrySort, setRegistrySort] = useDocumentsWorkspaceField('registrySort');
  const [registryViewMode, setRegistryViewMode] = useDocumentsWorkspaceField('registryViewMode');
  const [organizationFilters, setOrganizationFilters] = useDocumentsWorkspaceField('organizationFilters');
  const [organizationPreferences, setOrganizationPreferences] = useState({
    favorite_document_ids: [],
    pinned_document_ids: [],
    saved_views: [],
    view_mode: 'list',
    columns: ['name', 'location', 'modified', 'size', 'owner', 'versions'],
  });
  const [organizationBusy, setOrganizationBusy] = useState('');
  const [nonMatterFolderPage, setNonMatterFolderPage] = useDocumentsWorkspaceField('nonMatterFolderPage');
  const [matterFolderPage, setMatterFolderPage] = useDocumentsWorkspaceField('matterFolderPage');
  const [matterSuggestions, setMatterSuggestions] = useState([]);
  const [matterSuggestionTotal, setMatterSuggestionTotal] = useState(0);
  const [matterSuggestionsLoading, setMatterSuggestionsLoading] = useState(true);
  const [matterSuggestionsError, setMatterSuggestionsError] = useState('');
  const [matterSuggestionSelections, setMatterSuggestionSelections] = useDocumentsWorkspaceField('matterSuggestionSelections');
  const [matterSuggestionSubmitting, setMatterSuggestionSubmitting] = useState({});
  const [activeFolder, setActiveFolder] = useDocumentsWorkspaceField('activeFolder');
  const [folderCatalog, setFolderCatalog] = useState([]);
  const [folderCatalogLoading, setFolderCatalogLoading] = useState(
    ['folders', 'files'].includes(initialRegistryDisplay)
  );
  const [folderCatalogError, setFolderCatalogError] = useState('');
  const [folderDocuments, setFolderDocuments] = useState([]);
  const [folderDocumentsTotal, setFolderDocumentsTotal] = useState(0);
  const [folderDocumentsHasNext, setFolderDocumentsHasNext] = useState(false);
  const [folderDocumentsNextFirst, setFolderDocumentsNextFirst] = useState(0);
  const [folderDocumentsLoading, setFolderDocumentsLoading] = useState(false);
  const [folderDocumentsLoadingMore, setFolderDocumentsLoadingMore] = useState(false);
  const [folderDocumentsError, setFolderDocumentsError] = useState('');
  const [fileViewerDocument, setFileViewerDocument] = useState(null);
  const [folderSearch, setFolderSearch] = useDocumentsWorkspaceField('folderSearch');
  const [documentFirst, setDocumentFirst] = useDocumentsWorkspaceField('documentFirst');
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [matterNumberOptions, setMatterNumberOptions] = useState([]);
  const [folderOptions, setFolderOptions] = useState([]);
  const [writeModal, setWriteModal] = useState(null);
  const [writeForm, setWriteForm] = useState({});
  const [writeFile, setWriteFile] = useState(null);
  const [writeSubmitting, setWriteSubmitting] = useState(false);
  const [writeError, setWriteError] = useState('');
  const [editStarting, setEditStarting] = useState(false);
  const [wordEditMonitor, setWordEditMonitor] = useState(null);
  const [newDocumentOpen, setNewDocumentOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderForm, setNewFolderForm] = useState({
    matter_number: '',
    parent_folder_id: '',
    parent_folder_path: '',
    parent_label: '',
    folder_name: '',
    is_subfolder: false,
  });
  const [newFolderSubmitting, setNewFolderSubmitting] = useState(false);
  const [newFolderError, setNewFolderError] = useState('');
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveSubmitting, setMoveSubmitting] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [utilityPanel, setUtilityPanel] = useState('');
  const [pendingFolderUpload, setPendingFolderUpload] = useState(null);
  const [sharePanelOpen, setSharePanelOpen] = useState(false);
  const [newDocumentForm, setNewDocumentForm] = useState({
    mode: 'blank',
    title: '',
    lock_after_create: false,
    destination_kind: 'matter',
    matter_number: '',
    folder_path: '',
  });
  const [newDocumentFile, setNewDocumentFile] = useState(null);
  const [newDocumentSubmitting, setNewDocumentSubmitting] = useState(false);
  const [newDocumentError, setNewDocumentError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [matterChoices, setMatterChoices] = useState([]);
  const [matterChoicesLoading, setMatterChoicesLoading] = useState(false);
  const [createdDestination, setCreatedDestination] = useState(null);
  const restoredFolderDrawerRef = useRef(!initialRegistryState.activeFolder);
  const beginDocumentRequest = useDocumentRequests(`${selectedDocumentId}:${comparisonRenderer}`);
  const loadedVersionsDocumentRef = useRef('');
  const autoRedlineRequestRef = useRef(new Set());
  const previewQueueRequestRef = useRef(new Set());
  const manualVersionSelectionRef = useRef(false);
  const requestedPreviewVersionRef = useRef('');
  const appliedRoutedVersionRef = useRef('');
  const latestLinkVisitRef = useRef('');
  const appliedRoutedComparisonRef = useRef('');
  const pendingRendererComparisonRef = useRef(null);
  const wordEditObservedActivityRef = useRef(false);
  const wordEditPendingRevisionRef = useRef('');
  const wordEditPollInFlightRef = useRef(false);
  const comparisonSelectionModeRef = useRef('default');
  const recentOpenKeyRef = useRef('');
  const restoreDocumentRouteRef = useRef(routedDocumentId ? '' : initialDocumentUrl);
  const [, setPersistedDocumentUrl] = useDocumentsWorkspaceField('documentUrl');

  useEffect(() => {
    document.title = 'Documents';
  }, []);

  useEffect(

  () => {
    const preventDroppedFileNavigation =

    (event) => {
      if (isExternalFileDrag(event.dataTransfer)) event.preventDefault();
    };
    window.addEventListener('dragover', preventDroppedFileNavigation, true);
    window.addEventListener('drop', preventDroppedFileNavigation, true);
    return () => {
      window.removeEventListener('dragover', preventDroppedFileNavigation, true);
      window.removeEventListener('drop', preventDroppedFileNavigation, true);
    };
  }, []);

  useEffect(() => {
    if (restoreDocumentRouteRef.current && !routedDocumentId) return;
    setPersistedDocumentUrl(
      workspaceMode === 'document' && selectedDocumentId
        ? `${location.pathname}${location.search}`
        : ''
    );
  }, [
    location.pathname,
    location.search,
    routedDocumentId,
    selectedDocumentId,
    setPersistedDocumentUrl,
    workspaceMode,
  ]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const normalizedSearch = search.trim();
      setDebouncedSearch(normalizedSearch.length >= 3 ? normalizedSearch : '');
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(

  () => {
    if (!documentRoute.applies) return;

    if (routedDocumentId) {
      restoreDocumentRouteRef.current = '';
      setWorkspaceMode('document');
      setSelectedDocumentId((current) => current === routedDocumentId ? current : routedDocumentId);
      return;
    }

    if (restoreDocumentRouteRef.current) {
      const restoreUrl = restoreDocumentRouteRef.current;
      restoreDocumentRouteRef.current = '';
      navigate(restoreUrl, { replace: true });
      return;
    }

    setWorkspaceMode('registry');
    setSelectedDocumentId('');
    setSelectedChangeId('');
    setComparison(INITIAL_COMPARISON);
    setPreviewMode('version');
  }, [documentRoute.applies, navigate, routedDocumentId]);

  const loadRecentDocuments = useCallback(

  ({ force = true } = {}) => {
    const url = '/simul/archive/recent-documents';
    setRecentDocumentsLoading(!dataCache.read(url));
    setRecentDocumentsError('');
    return dataCache.load('recent', url, (data) => {
      setRecentDocuments(data.documents || []);
      setRecentDocumentsLoading(false);
    }, { force, onClear: () => setRecentDocuments([]), onError: (err) => {
      setRecentDocumentsError(err.message || 'Could not load recent documents.');
      setRecentDocumentsLoading(false);
    } }).catch(() => []);
  }, [dataCache]);

  const loadOrganization = useCallback(

  () => (
    dataCache.load('organization', '/simul/archive/organization', (data) => {
      const preferences = data.preferences || {};
      setOrganizationPreferences((current) => ({ ...current, ...preferences }));
      if (['list', 'grid'].includes(preferences.view_mode)) setRegistryViewMode(preferences.view_mode);
      else if (preferences.view_mode === 'compact') setRegistryViewMode('list');
    }).catch(() => null)
  ), [dataCache, setRegistryViewMode]);

  const loadDocuments = useCallback(

  ({ force = true } = {}) => {
    const params = new URLSearchParams();
    params.set('first', String(documentFirst));
    params.set('rows', String(DOCUMENT_PAGE_SIZE));
    params.set('registry_status', registryStatus);
    params.set('sort', registrySort);
    params.set('response_mode', 'summary');
    params.set('consumer', 'documents_page');
    params.set('include_facets', 'true');
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (matterNumber.trim()) params.set('matter_number', matterNumber.trim());
    if (folderPath.trim()) params.set('folder_path', folderPath.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (organizationFilters.modifiedFrom) params.set('modified_from', organizationFilters.modifiedFrom);
    if (organizationFilters.modifiedTo) params.set('modified_to', organizationFilters.modifiedTo);
    if (organizationFilters.trashStatus) params.set('trash_status', organizationFilters.trashStatus);
    const url = `/document-library/search?${params}`;
    const cached = dataCache.read(url);
    setDocumentsLoading(!cached);
    setDocumentsError('');
    return dataCache.load('registry', url,

    (data) => {
      const next = data.documents || [];
      setDocuments(next);
      setDocumentsWarning(data.search_warning || '');
      setTotalFiltered(Number(data.total_filtered ?? next.length));
      setTotalDocuments(Number(data.total_documents ?? next.length));
      setMatterNumberOptions(data.facets?.matter_numbers || []);
      setFolderOptions(data.facets?.folders || []);
      setDocumentsLoading(false);
    }, { force, onClear: () => setDocuments([]), onError: (err) => {
      setDocumentsLoading(false);
      setDocumentsError(err.status === 'ECONNABORTED' ? 'Document search timed out. Please try again.'
        : err.response?.data?.error || err.message || 'Failed to load archived documents.');
    } }).catch(() => null);
  }, [dataCache, dateFrom, dateTo, debouncedSearch, documentFirst, folderPath, matterNumber, organizationFilters, registrySort, registryStatus]);

  const loadFolderCatalog = useCallback(

  ({ force = true } = {}) => {
    const url = registryStatus === 'locked' ? '/documents/folders?registry_status=locked' : '/documents/folders';
    setFolderCatalogLoading(!dataCache.read(url));
    setFolderCatalogError('');
    return dataCache.load('catalog', url, (data) => {
      setFolderCatalog(canonicalFolderCatalog(data.folders || []));
      setFolderCatalogLoading(false);
    }, { force, onClear: () => setFolderCatalog([]), onError: (err) => {
      setFolderCatalogLoading(false);
      setFolderCatalogError(err.response?.data?.error || err.message || 'Failed to load folders.');
    } }).then((data) => canonicalFolderCatalog(data.folders || [])).catch(() => []);
  }, [dataCache, registryStatus]);

  const loadMatterSuggestions = useCallback(

  ({ force = false } = {}) => {
    const url = '/simul/archive/matter-suggestions?first=0&rows=200';
    setMatterSuggestionsLoading(!dataCache.read(url));
    setMatterSuggestionsError('');
    return dataCache.load('suggestions', url,

    (data) => {
      const nextSuggestions = data.suggestions || [];
      setMatterSuggestions(nextSuggestions);
      setMatterSuggestionTotal(Number(data.total ?? nextSuggestions.length));
      setMatterSuggestionSelections((current) => Object.fromEntries(nextSuggestions.map((suggestion) => [
        suggestion.document_id, current[suggestion.document_id] ?? defaultSuggestedMatter(suggestion),
      ])));
      setMatterSuggestionsLoading(false);
    }, { force, onClear: () => setMatterSuggestions([]), onError: (err) => {
      setMatterSuggestionsLoading(false);
      setMatterSuggestionsError(err.message || 'Failed to load matter suggestions.');
    } }).catch(() => []);
  }, [dataCache, setMatterSuggestionSelections]);

  const loadFolderDocuments = useCallback(

  (
    folder, scopedSearch = '', { first = 0, append = false, force = true } = {}
  ) => {
    const scopePath = folderScopePath(folder);
    const scopeSimulId = simulFolderId(folder);
    const canonicalFolderId = String(folder?.folder_id || '').trim();
    const isLegacySimulFolder = folder?.folder_type === 'simul';
    if (!folder || (isLegacySimulFolder && !scopeSimulId)) return Promise.resolve();
      const params = new URLSearchParams({
        first: '0',
        rows: String(FOLDER_DRAWER_PAGE_SIZE),
        response_mode: 'summary',
        include_facets: 'false',
        consumer: 'folder_drawer',
      });
      params.set('registry_status', registryStatus);
      let endpoint;
      const normalizedSearch = String(scopedSearch || '').trim();
      if (normalizedSearch.length >= 3 || registryStatus === 'locked') {
        params.set('search', normalizedSearch);
        if (canonicalFolderId && !isLegacySimulFolder) params.set('folder_id', canonicalFolderId);
        else if (scopeSimulId) params.set('simul_folder_id', scopeSimulId);
        else if (scopePath) params.set('folder_path', scopePath);
        if (!canonicalFolderId && folder.matter_number) params.set('matter_number', folder.matter_number);
        endpoint = `/document-library/search?${params.toString()}`;
      } else if (canonicalFolderId && !isLegacySimulFolder) {
        endpoint = `/documents/folders/${encodeURIComponent(canonicalFolderId)}/documents?${params.toString()}`;
      } else if (scopeSimulId) {
        endpoint = `/simul/archive/folders/${encodeURIComponent(scopeSimulId)}/documents?${params.toString()}`;
      } else {
        params.set('folder_scope', 'direct');
        if (folder.matter_number) params.set('matter_number', folder.matter_number);
        if (scopePath) params.set('folder_path', scopePath);
        endpoint = `/document-library/search?${params.toString()}`;
      }
    const options = { pageSize: FOLDER_DRAWER_PAGE_SIZE };
    const cached = dataCache.read(endpoint, options);
    setFolderDocumentsLoading(!cached);
    setFolderDocumentsLoadingMore(append);
    setFolderDocumentsError('');
    if (!cached) {
      setFolderDocuments([]);
      setFolderDocumentsTotal(0);
      setFolderDocumentsHasNext(false);
    }
    return dataCache.load('folder', endpoint,

    (data) => {
      setFolderDocuments(data.documents || []);
      setFolderDocumentsTotal(Number(data.total ?? data.total_filtered ?? data.documents?.length ?? 0));
      setFolderDocumentsHasNext(Boolean(data.pagination?.has_next));
      setFolderDocumentsNextFirst(data.pagination?.next_first || data.documents?.length || 0);
      setFolderDocumentsLoading(false);
      setFolderDocumentsLoadingMore(false);
    }, { ...options, pages: append ? Math.floor(first / FOLDER_DRAWER_PAGE_SIZE) + 1 : 1,
      force: force && !append, onClear: () => setFolderDocuments([]), onError: (err) => {
        setFolderDocumentsLoading(false);
        setFolderDocumentsLoadingMore(false);
        setFolderDocumentsError(err.response?.data?.error || err.message || 'Failed to load folder files.');
      } }).catch(() => null);
  }, [dataCache, registryStatus]);

  const activeFolderFilterKey = registryStatus;
  const previousActiveFolderFilterKeyRef = useRef(activeFolderFilterKey);
  useEffect(

  () => {
    if (previousActiveFolderFilterKeyRef.current === activeFolderFilterKey) return;
    previousActiveFolderFilterKeyRef.current = activeFolderFilterKey;
    if (!activeFolder || registryDisplay !== 'folders') return;
    loadFolderDocuments(activeFolder, folderSearch);
  }, [activeFolder, activeFolderFilterKey, folderSearch, loadFolderDocuments, registryDisplay]);

  useEffect(

  () => {
    const normalizedSearch = folderSearch.trim();
    if (!activeFolder || normalizedSearch.length < 3) return undefined;
    const timeoutId = window.setTimeout(() => {
      loadFolderDocuments(activeFolder, normalizedSearch, { force: false });
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [activeFolder, folderSearch, loadFolderDocuments]);

  useEffect(

  () => {
    if (
      restoredFolderDrawerRef.current ||
      workspaceMode !== 'registry' ||
      !['folders', 'files'].includes(registryDisplay) ||
      !activeFolder ||
      folderCatalogLoading ||
      folderCatalog.length === 0
    ) return;
    const restoredFolder = folderCatalog.find((folder) => (
      activeFolder.folder_id
        ? folder.folder_id === activeFolder.folder_id
        : folder.folder_type === activeFolder.folder_type &&
          folder.matter_number === activeFolder.matter_number &&
          folder.folder_path === activeFolder.folder_path
    ));
    restoredFolderDrawerRef.current = true;
    if (!restoredFolder) {
      setActiveFolder(null);
      return;
    }
    // Warm the remembered Browse location even when Search is selected. Keep
    // the selected tab and its filters intact while the shared request loads.
    setActiveFolder(restoredFolder);
    loadFolderDocuments(restoredFolder, folderSearch, { force: false });
  }, [activeFolder, folderSearch, folderCatalog, folderCatalogLoading, loadFolderDocuments, registryDisplay, setActiveFolder, workspaceMode]);

  const loadVersions = useCallback(

  (documentId, { background = false, force = true, selectLatest = false } = {}) => {
    const isCurrentRequest = beginDocumentRequest('versions');
    const keepSelection = loadedVersionsDocumentRef.current === documentId;
    if (!background && !force && !keepSelection) {
      setComparisonLoading(false);
      // Limit automatic attempts to one per pair per visit, not per mounted page.
      // Returning to a document must recover from an earlier failed request.
      autoRedlineRequestRef.current.clear();
      manualVersionSelectionRef.current = false;
      requestedPreviewVersionRef.current = '';
      comparisonSelectionModeRef.current = 'default';
    }
    if (!documentId) {
      dataCache.stop('versions');
      loadedVersionsDocumentRef.current = '';
      setDocumentDetails(null);
      setVersions([]);
      setActivity([]);
      setSelectedVersionId('');
      setPreviewVersionId('');
      setBaseRevisionId('');
      setTargetRevisionId('');
      setComparison(INITIAL_COMPARISON);
      setChanges([]);
      setSelectedChangeId('');
      setPreviewMode('version');
      return Promise.resolve();
    }
    const url = `/simul/archive/documents/${encodeURIComponent(documentId)}/versions`;
    const cached = dataCache.read(url);
    if (!background && !cached) {
      loadedVersionsDocumentRef.current = '';
      setDocumentDetails(null);
      setVersionsLoading(true);
      setVersions([]);
      setSelectedVersionId('');
      setPreviewVersionId('');
      setBaseRevisionId('');
      setTargetRevisionId('');
      setSelectedChangeId('');
      setChanges([]);
      setComparison(INITIAL_COMPARISON);
      setComparisonError('');
      setPreviewMode('version');
    }
    // Changing comparison engines refreshes subscriptions, not the user's
    // version pair or their deliberate Document/Changes tab selection.
    let delivered = keepSelection || background || (force && Boolean(cached));
    return dataCache.load('versions', url,

    (data) => {
        if (!isCurrentRequest()) return;
        const response = { data };
        setVersionsLoading(false);
        const returnedVersions = (response.data?.versions || []).filter(
          (version) => version.provenance !== 'ai_edit_proposal'
        );
        const current = currentVersion(response.data?.document, returnedVersions);
        const currentRevisionId = current?.revision_id;
        const nextVersions = returnedVersions.map((version) => ({
          ...version,
          is_latest: version.revision_id === currentRevisionId,
        }));
        loadedVersionsDocumentRef.current = documentId;
        setDocumentDetails({
          ...(response.data?.document || {}),
          document_id: documentId,
          latest_revision_id: currentRevisionId || '',
        });
        setActivity(response.data?.activity || []);
        setVersions(nextVersions);
        if (delivered) return;
        delivered = true;
        const latest = currentVersion(response.data?.document, nextVersions);
        const saved = dataCache.view(`document:${documentId}:${comparisonRenderer}`);
        const savedVersionId = nextVersions.some((version) => version.revision_id === saved?.previewVersionId) ? saved.previewVersionId : '';
        const defaultVersionId = savedVersionId || latest?.revision_id || nextVersions[nextVersions.length - 1]?.revision_id || nextVersions[0]?.revision_id || '';
        const defaults = defaultCompareSelection(nextVersions);
        // A freshly accepted AI checkpoint is a document, not a comparison.
        // Keep it visible while its redline is prepared in the background.
        manualVersionSelectionRef.current = Boolean(savedVersionId && saved.manualVersionSelection) || latest?.provenance === 'ai_edit_adopted';
        comparisonSelectionModeRef.current = saved?.comparisonSelectionMode || 'default';
        setSelectedVersionId(defaultVersionId);
        setPreviewVersionId(defaultVersionId);
        setBaseRevisionId(saved?.baseRevisionId && nextVersions.some((v) => v.revision_id === saved.baseRevisionId) ? saved.baseRevisionId : defaults.baseRevisionId);
        setTargetRevisionId(saved?.targetRevisionId && nextVersions.some((v) => v.revision_id === saved.targetRevisionId) ? saved.targetRevisionId : defaults.targetRevisionId);
        if (savedVersionId) setPreviewMode(saved.previewMode || 'version');
      }, { force, onClear: () => { setDocumentDetails(null); setVersions([]); setActivity([]); } })
      .then((data) => {
        // Resolve latest links from the fresh response, overriding remembered
        // selections even when RTK returns an identical cached object.
        if (selectLatest && isCurrentRequest()) {
          const available = (data.versions || []).filter((version) => version.provenance !== 'ai_edit_proposal');
          const latest = currentVersion(data.document, available);
          if (latest) {
            manualVersionSelectionRef.current = true;
            comparisonSelectionModeRef.current = 'default';
            const defaults = defaultCompareSelection(available);
            setSelectedVersionId(latest.revision_id);
            setPreviewVersionId(latest.revision_id);
            setSelectedChangeId('');
            setBaseRevisionId(defaults.baseRevisionId);
            setTargetRevisionId(defaults.targetRevisionId);
            setPreviewMode('version');
          }
        }
        return data.versions || [];
      })
      .catch(

      (err) => {
        if (!isCurrentRequest()) return;
        console.error('Failed to load archived versions:', err);
        if (background || cached) return;
        setDocumentDetails(null);
        setVersions([]);
        setActivity([]);
        setSelectedVersionId('');
        setPreviewVersionId('');
        setBaseRevisionId('');
        setTargetRevisionId('');
      })
      .finally(() => {
        if (!background && isCurrentRequest()) setVersionsLoading(false);
      });
  }, [beginDocumentRequest, comparisonRenderer, dataCache]);

  const updateChanges = useCallback(

  (nextChanges) => {
    const normalizedChanges = Array.isArray(nextChanges) ? nextChanges : [];
    setChanges(normalizedChanges);
    setSelectedChangeId((current) => {
      if (current && normalizedChanges.some((change) => change.change_id === current)) return current;
      return '';
    });
  }, []);

  const loadChanges = useCallback(

  (documentId, { force = true } = {}) => {
    const isCurrentRequest = beginDocumentRequest('changes');
    if (!documentId) {
      dataCache.stop('changes');
      updateChanges([]);
      setChangesLoading(false);
      return Promise.resolve([]);
    }
    const rendererQuery = comparisonRenderer === 'libreoffice' ? '' : `?comparison_engine=${encodeURIComponent(comparisonRenderer)}`;
    const url = `/simul/archive/documents/${encodeURIComponent(documentId)}/comparison-status${rendererQuery}`;
    setChangesLoading(!dataCache.read(url));
    setChangesError('');
    return dataCache.load('changes', url, (data) => {
      if (!isCurrentRequest()) return;
      updateChanges(data.changes || []);
      setChangesLoading(false);
    }, { force, onClear: () => updateChanges([]), onError: (err) => {
      if (!isCurrentRequest()) return;
      setChangesLoading(false);
      setChangesError(err.response?.data?.error || err.message || 'Failed to load redline status.');
    } }).then((data) => data.changes || []).catch(() => []);
  }, [beginDocumentRequest, comparisonRenderer, dataCache, updateChanges]);

  useEffect(() => {
    if (workspaceMode === 'registry') loadDocuments({ force: false });
    return () => dataCache.stop('registry');
  }, [dataCache, loadDocuments, workspaceMode]);
  useEffect(() => {
    if (workspaceMode === 'registry') loadRecentDocuments({ force: false });
    return () => dataCache.stop('recent');
  }, [dataCache, loadRecentDocuments, workspaceMode]);
  useEffect(() => {
    if (workspaceMode === 'registry') loadOrganization();
  }, [loadOrganization, workspaceMode]);
  const folderCatalogVisible = workspaceMode === 'registry' && ['folders', 'files'].includes(registryDisplay);
  useEffect(() => {
    if (folderCatalogVisible) loadFolderCatalog({ force: false });
    return () => dataCache.stop('catalog');
  }, [dataCache, folderCatalogVisible, loadFolderCatalog]);
  useEffect(() => {
    if (workspaceMode === 'registry') loadMatterSuggestions();
  }, [loadMatterSuggestions, workspaceMode]);
  useEffect(

  () => {
    const latestLinkVisit = routedVersionId === 'latest' ? `${location.key}:${selectedDocumentId}` : '';
    const selectLatest = Boolean(latestLinkVisit && latestLinkVisitRef.current !== latestLinkVisit);
    latestLinkVisitRef.current = latestLinkVisit;
    loadVersions(selectedDocumentId, { force: selectLatest, selectLatest });
    loadChanges(selectedDocumentId, { force: false });
    return () => { dataCache.stop('versions'); dataCache.stop('changes'); dataCache.stop('comparison'); };
  }, [dataCache, loadChanges, loadVersions, routedVersionId, selectedDocumentId, location.key]);

  useEffect(() => {
    if (!selectedDocumentId || loadedVersionsDocumentRef.current !== selectedDocumentId || !versions.some((v) => v.revision_id === previewVersionId)) return;
    dataCache.remember(`document:${selectedDocumentId}:${comparisonRenderer}`, {
      previewVersionId, baseRevisionId, targetRevisionId, previewMode,
      manualVersionSelection: manualVersionSelectionRef.current,
      comparisonSelectionMode: comparisonSelectionModeRef.current,
    });
  }, [baseRevisionId, comparisonRenderer, dataCache, previewMode, previewVersionId, selectedDocumentId, targetRevisionId, versions]);

  useEffect(

  () => {
    if (wordEditMonitor || !selectedDocumentId) return;
    const pendingVersion = versions.find((version) => version.pending_edit);
    if (!pendingVersion) return;
    wordEditObservedActivityRef.current = true;
    wordEditPendingRevisionRef.current = pendingVersion.revision_id || '';
    setWordEditMonitor({
      documentId: selectedDocumentId,
      startedAt: Date.now(),
    });
  }, [selectedDocumentId, versions, wordEditMonitor]);

  useEffect(

  () => {
    const documentId = wordEditMonitor?.documentId;
    if (!documentId || documentId !== selectedDocumentId) return undefined;
    let cancelled = false;

    const pollWordEdit =

    () => {
      if (wordEditPollInFlightRef.current) return;
      wordEditPollInFlightRef.current = true;
      loadVersions(documentId, { background: true })
        .then(

        (nextVersions) => {
          if (cancelled || !Array.isArray(nextVersions)) return;
          const pendingVersion = nextVersions.find((version) => version.pending_edit) || null;
          const hasActiveWordEdit = Boolean(
            pendingVersion ||
            nextVersions.some((version) => version.locked && version.lock_status === 'active')
          );

          if (hasActiveWordEdit) {
            wordEditObservedActivityRef.current = true;
            if (
              pendingVersion?.revision_id &&
              wordEditPendingRevisionRef.current !== pendingVersion.revision_id
            ) {
              wordEditPendingRevisionRef.current = pendingVersion.revision_id;
              showToast && showToast({
                severity: 'success',
                summary: 'Word save received',
                detail: 'The saved changes are visible here and will become current when Word closes.',
              });
            }
            return;
          }

          if (wordEditObservedActivityRef.current) {
            const completedRevisionId = wordEditPendingRevisionRef.current;
            setWordEditMonitor(null);
            loadChanges(documentId);
            if (completedRevisionId && !manualVersionSelectionRef.current) {
              setSelectedVersionId(completedRevisionId);
              setPreviewVersionId(completedRevisionId);
              setPreviewMode('version');
              showToast && showToast({
                severity: 'success',
                summary: 'Word version finalized',
                detail: 'The saved Word changes are now the current version.',
              });
            }
            return;
          }

          if (Date.now() - wordEditMonitor.startedAt >= WORD_EDIT_LAUNCH_POLL_TIMEOUT_MS) {
            setWordEditMonitor(null);
          }
        })
        .finally(() => {
          wordEditPollInFlightRef.current = false;
        });
    };

    pollWordEdit();
    const intervalId = window.setInterval(pollWordEdit, WORD_EDIT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      wordEditPollInFlightRef.current = false;
    };
  }, [loadChanges, loadVersions, selectedDocumentId, showToast, wordEditMonitor]);

  const selectedDocument = useMemo(

  () => {
    const listedDocument = documents.find((doc) => doc.document_id === selectedDocumentId) || null;
    const createdLocation = createdDestination?.document_id === selectedDocumentId
      ? {
        matter_number: createdDestination.matter_number,
        folder_path: createdDestination.folder_path,
      }
      : {};
    const hydratedDocument = documentDetails?.document_id === selectedDocumentId ? documentDetails : null;
    const latestVersion = currentVersion(hydratedDocument || listedDocument, versions);
    const fallback = {
      document_id: selectedDocumentId,
      filename: latestVersion?.filename || 'Archived document',
      collection: latestVersion?.collection || '',
      matter_number: latestVersion?.matter_number || '',
      folder_path: latestVersion?.folder_path || latestVersion?.simul_folder_path || '',
      latest_revision_id: latestVersion?.revision_id || '',
      version_count: versions.length,
      ...createdLocation,
    };
    if (listedDocument || hydratedDocument) {
      return { ...fallback, ...listedDocument, ...hydratedDocument, ...createdLocation };
    }
    return selectedDocumentId && workspaceMode === 'document' ? fallback : null;
  }, [createdDestination, documentDetails, documents, selectedDocumentId, versions, workspaceMode]);

  const previewVersion = useMemo(
    () => versionById(versions, previewVersionId),
    [previewVersionId, versions]
  );

  useEffect(

  () => {
    if (workspaceMode !== 'document' || !selectedDocumentId || !previewVersionId) return;
    if (loadedVersionsDocumentRef.current !== selectedDocumentId) return;
    if (!versions.some((version) => version.revision_id === previewVersionId)) return;
    const openKey = `${selectedDocumentId}:${previewVersionId}`;
    if (recentOpenKeyRef.current === openKey) return;
    recentOpenKeyRef.current = openKey;
    api.post(`/simul/archive/documents/${encodeURIComponent(selectedDocumentId)}/recent-open`, {
      revision_id: previewVersionId,
    }).catch((err) => {
      console.error('Failed to update recent documents:', err);
    });
  }, [previewVersionId, selectedDocumentId, versions, workspaceMode]);

  useEffect(

  () => {
    if (workspaceMode !== 'document' || !selectedDocumentId || !previewVersion?.revision_id) return undefined;
    const revisionId = previewVersion.revision_id;
    const status = String(previewVersion.preview_status || '').toLowerCase();
    const ready = Boolean(previewVersion.preview_ready || (
      status === 'ready' && previewVersion.preview_object_key
    ));
    if (ready || status === 'failed') return undefined;

    if (!['queued', 'running'].includes(status) && !previewQueueRequestRef.current.has(revisionId)) {
      previewQueueRequestRef.current.add(revisionId);
      api.post(`/simul/archive/versions/${encodeURIComponent(revisionId)}/preview-pdf/ensure`)
        .catch((err) => {
          previewQueueRequestRef.current.delete(revisionId);
          console.error('Failed to queue archived preview:', err);
        });
    }

    const timer = window.setTimeout(() => {
      loadVersions(selectedDocumentId, { background: true });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [loadVersions, previewVersion, selectedDocumentId, workspaceMode]);

  const baseVersion = useMemo(
    () => versionById(versions, baseRevisionId),
    [baseRevisionId, versions]
  );

  const targetVersion = useMemo(
    () => versionById(versions, targetRevisionId),
    [targetRevisionId, versions]
  );

  const matchingVersions = useMemo(

  () => {
    const fromSearch = selectedDocument?.versions || [];
    if (!versions.length) return fromSearch;
    const searchById = new Map(fromSearch.map((version) => [version.revision_id, version]));
    return versions.map((version) => {
      const searchVersion = searchById.get(version.revision_id) || {};
      return {
        ...searchVersion,
        ...version,
        snippets: searchVersion.snippets || version.snippets || [],
      };
    });
  }, [selectedDocument, versions]);

  const latestVersion = useMemo(
    () => currentVersion(selectedDocument, matchingVersions),
    [matchingVersions, selectedDocument]
  );

  const actionVersion = previewVersion || latestVersion;
  const writeActionVersion = versionById(matchingVersions, writeModal?.revision_id) || actionVersion;

  const scopedFolders = useMemo(() => {
    const unique = new Map();
    [...folderOptions, ...folderCatalog.filter((folder) => folder.folder_type === 'location')].forEach((folder) => {
      const key = `${folder.matter_number || ''}:${folder.folder_path || ''}`.toLowerCase();
      if (folder.folder_path) unique.set(key, folder);
    });
    return [...unique.values()].filter((folder) => !matterNumber || folder.matter_number === matterNumber);
  }, [folderCatalog, folderOptions, matterNumber]);

  const selectedChange = useMemo(
    () => changes.find((change) => change.change_id === selectedChangeId) || null,
    [changes, selectedChangeId]
  );

  const canCompare = Boolean(
    selectedDocumentId &&
    baseRevisionId &&
    targetRevisionId &&
    baseRevisionId !== targetRevisionId &&
    canAttemptVersionComparison(baseVersion) &&
    canAttemptVersionComparison(targetVersion)
  );

  const pollComparison = useCallback(

  (comparisonId) => {
    if (!comparisonId) return Promise.resolve(null);
    const isCurrentRequest = beginDocumentRequest('comparison');
    const url = `/simul/archive/comparisons/${encodeURIComponent(comparisonId)}`;
    const cached = dataCache.read(url);
    return dataCache.load('comparison', url,

    (data) => {
        const response = { data };
        if (!isCurrentRequest()) return null;
        const nextComparison = response.data?.comparison || null;
        setComparison(nextComparison);
        if (nextComparison?.status === 'ready' && !manualVersionSelectionRef.current) {
          setPreviewMode('redline');
        }
        return nextComparison;
      }, {
        force: cached?.comparison?.status !== 'ready' || comparisonEnhancementPending(cached?.comparison),
        onClear: () => setComparison(null),
      })
      .then((data) => data.comparison || null)
      .catch(

      (err) => {
        if (!isCurrentRequest()) return null;
        console.error('Failed to poll comparison:', err);
        setComparisonError(err?.response?.data?.error || err.message || 'Failed to load comparison status.');
        return null;
      });
  }, [beginDocumentRequest, dataCache]);

  useEffect(

  () => {
    if (!routedComparisonId) {
      appliedRoutedComparisonRef.current = '';
      return;
    }
    if (loadedVersionsDocumentRef.current !== selectedDocumentId) return;
    const routeSelectionKey = `${selectedDocumentId}:${routedComparisonId}`;
    if (appliedRoutedComparisonRef.current === routeSelectionKey) return;
    appliedRoutedComparisonRef.current = routeSelectionKey;
    comparisonSelectionModeRef.current = 'custom';
    const isCurrentRoute = beginDocumentRequest('comparison-route');
    setComparisonLoading(true);
    setComparisonError('');
    pollComparison(routedComparisonId)
      .then(

      (nextComparison) => {
        if (!isCurrentRoute()) return;
        if (
          !nextComparison ||
          String(nextComparison.document_id || '') !== String(selectedDocumentId) ||
          !versions.some((version) => version.revision_id === nextComparison.base_revision_id) ||
          !versions.some((version) => version.revision_id === nextComparison.target_revision_id)
        ) {
          appliedRoutedComparisonRef.current = '';
          return;
        }
        const routedTargetVersion = versions.find(
          (version) => version.revision_id === nextComparison.target_revision_id
        );
        if (
          nextComparison.has_changes === false &&
          routedTargetVersion?.provenance === 'ai_edit_adopted'
        ) {
          manualVersionSelectionRef.current = true;
          comparisonSelectionModeRef.current = 'default';
          setSelectedVersionId(nextComparison.target_revision_id);
          setPreviewVersionId(nextComparison.target_revision_id);
          setSelectedChangeId('');
          setComparison(INITIAL_COMPARISON);
          setPreviewMode('version');
          navigate(
            `${documentDetailPath(selectedDocumentId)}?version=${encodeURIComponent(nextComparison.target_revision_id)}`,
            { replace: true }
          );
          return;
        }
        setBaseRevisionId(nextComparison.base_revision_id);
        setTargetRevisionId(nextComparison.target_revision_id);
        setSelectedVersionId(nextComparison.target_revision_id);
        setPreviewVersionId(nextComparison.target_revision_id);
        setSelectedChangeId(`${nextComparison.base_revision_id}..${nextComparison.target_revision_id}`);
        // A saved comparison identifies its engine. Show that engine even if
        // the recipient's preference differs; opening a link isn't a new preference.
        if (COMPARISON_RENDERERS.has(nextComparison.comparison_engine)) {
          setComparisonRenderer(nextComparison.comparison_engine);
        }
        if (!manualVersionSelectionRef.current) setPreviewMode('redline');
      })
      .finally(() => { if (isCurrentRoute()) setComparisonLoading(false); });
  }, [beginDocumentRequest, navigate, pollComparison, routedComparisonId, selectedDocumentId, versions]);

  useEffect(() => {
    if (
      !comparison?.comparison_id ||
      (!POLL_STATUSES.has(comparison.status) && !comparisonEnhancementPending(comparison))
    ) return undefined;
    const intervalId = window.setInterval(() => {
      pollComparison(comparison.comparison_id);
    }, 2500);
    return () => window.clearInterval(intervalId);
  }, [comparison?.comparison_id, comparison?.status, comparison?.changed_pages_status, pollComparison]);

  useEffect(() => {
    if (
      !selectedDocumentId ||
      !changes.some((change) => (
        POLL_STATUSES.has(change.status) ||
        comparisonEnhancementPending(change.comparison)
      ))
    ) return undefined;
    const intervalId = window.setInterval(() => {
      loadChanges(selectedDocumentId);
    }, 3000);
    return () => window.clearInterval(intervalId);
  }, [changes, loadChanges, selectedDocumentId]);

  useEffect(

  () => {
    if (!selectedChange || manualVersionSelectionRef.current) return;
    setBaseRevisionId(selectedChange.base_revision_id || '');
    setTargetRevisionId(selectedChange.target_revision_id || '');
    setSelectedVersionId(selectedChange.target_revision_id || '');
    setPreviewVersionId(selectedChange.target_revision_id || '');
    const nextComparison = selectedChange.comparison || INITIAL_COMPARISON;
    setComparison(nextComparison);
    if (nextComparison?.status === 'ready') {
      setPreviewMode('redline');
    }
  }, [selectedChange]);

  const startComparison = useCallback(

  (requestedBaseRevisionId, requestedTargetRevisionId, options = {}) => {
    const nextBaseRevisionId = typeof requestedBaseRevisionId === 'string' ? requestedBaseRevisionId : baseRevisionId;
    const nextTargetRevisionId = typeof requestedTargetRevisionId === 'string' ? requestedTargetRevisionId : targetRevisionId;
    const requestedBaseVersion = versionById(versions, nextBaseRevisionId);
    const requestedTargetVersion = versionById(versions, nextTargetRevisionId);
    const canStartComparison = Boolean(
      selectedDocumentId &&
      nextBaseRevisionId &&
      nextTargetRevisionId &&
      nextBaseRevisionId !== nextTargetRevisionId &&
      canAttemptVersionComparison(requestedBaseVersion) &&
      canAttemptVersionComparison(requestedTargetVersion)
    );
    if (!canStartComparison) return;
    const isCurrentRequest = beginDocumentRequest('comparison');
    const isCustomComparison = comparisonSelectionModeRef.current === 'custom';
    const activatePreview = options?.activatePreview !== false;

    setBaseRevisionId(nextBaseRevisionId);
    setTargetRevisionId(nextTargetRevisionId);
    setSelectedVersionId(nextTargetRevisionId);
    setPreviewVersionId(nextTargetRevisionId);
    setComparisonLoading(true);
    setComparisonError('');
    setComparison(INITIAL_COMPARISON);
    if (activatePreview) setPreviewMode('redline');
    api.post(`/simul/archive/documents/${encodeURIComponent(selectedDocumentId)}/comparisons`, {
      base_revision_id: nextBaseRevisionId,
      target_revision_id: nextTargetRevisionId,
      ...(comparisonRenderer === 'libreoffice'
        ? {}
        : { comparison_engine: comparisonRenderer }),
    })
      .then(

      (response) => {
        if (!isCurrentRequest()) return;
        const nextComparison = response.data?.comparison || null;
        setComparison(nextComparison);
        if (isCustomComparison && nextComparison?.comparison_id) {
          navigate(
            `${documentDetailPath(selectedDocumentId)}?comparison=${encodeURIComponent(nextComparison.comparison_id)}`,
            { replace: true }
          );
        }
      })
      .catch(

      (err) => {
        if (!isCurrentRequest()) return;
        const detail = err?.response?.data?.error || err.message || 'Failed to start comparison.';
        setComparisonError(detail);
        showToast && showToast({ severity: 'error', summary: 'Comparison failed', detail });
      })
      .finally(() => { if (isCurrentRequest()) setComparisonLoading(false); });
  }, [baseRevisionId, beginDocumentRequest, comparisonRenderer, navigate, selectedDocumentId, showToast, targetRevisionId, versions]);

  const changeComparisonRenderer = useCallback(

  (value) => {
    const nextRenderer = COMPARISON_RENDERERS.has(value) ? value : DEFAULT_COMPARISON_RENDERER;
    if (nextRenderer === comparisonRenderer) return;
    window.localStorage.setItem(COMPARISON_RENDERER_KEY, nextRenderer);
    beginDocumentRequest('comparison');
    beginDocumentRequest('comparison-route');
    const preservePair = canCompare;
    pendingRendererComparisonRef.current = preservePair ? {
      renderer: nextRenderer,
      documentId: selectedDocumentId,
      baseRevisionId,
      targetRevisionId,
      activatePreview: previewMode === 'redline',
    } : null;
    if (preservePair) comparisonSelectionModeRef.current = 'custom';
    // The old comparison URL names the old engine's result. The new request
    // will replace it with a link to the same pair in the newly chosen engine.
    if (routedComparisonId) navigate(documentDetailPath(selectedDocumentId), { replace: true });
    setComparisonRenderer(nextRenderer);
    setComparisonLoading(false);
    setComparison(INITIAL_COMPARISON);
    setComparisonError('');
    setSelectedChangeId('');
    setChanges([]);
  }, [baseRevisionId, beginDocumentRequest, canCompare, comparisonRenderer, navigate, previewMode, routedComparisonId, selectedDocumentId, targetRevisionId]);

  useEffect(

  () => {
    const pending = pendingRendererComparisonRef.current;
    if (!pending || pending.renderer !== comparisonRenderer) return;
    pendingRendererComparisonRef.current = null;
    if (pending.documentId !== selectedDocumentId) return;
    startComparison(pending.baseRevisionId, pending.targetRevisionId, { activatePreview: pending.activatePreview });
  }, [comparisonRenderer, selectedDocumentId, startComparison]);

  const updateDocumentOrganization = useCallback(

  (document, patch) => {
    const documentId = document?.document_id;
    if (!documentId) return Promise.reject(new Error('Document is required.'));
    setOrganizationBusy(documentId);
    return api.patch(`/simul/archive/organization/documents/${encodeURIComponent(documentId)}`, patch)
      .then(

      (response) => {
        const updated = response.data?.document || {};
        setDocuments((current) => current.map((item) => item.document_id === documentId ? { ...item, ...updated } : item));
        setFolderDocuments((current) => current.map((item) => item.document_id === documentId ? { ...item, ...updated } : item));
        setOrganizationPreferences((current) => ({
          ...current,
          favorite_document_ids: patch.favorite === undefined
            ? current.favorite_document_ids
            : patch.favorite
              ? [...new Set([...(current.favorite_document_ids || []), documentId])]
              : (current.favorite_document_ids || []).filter((value) => value !== documentId),
          pinned_document_ids: patch.pinned === undefined
            ? current.pinned_document_ids
            : patch.pinned
              ? [...new Set([...(current.pinned_document_ids || []), documentId])]
              : (current.pinned_document_ids || []).filter((value) => value !== documentId),
        }));
        return updated;
      })
      .finally(() => setOrganizationBusy(''));
  }, []);

  const updateOrganizationPreferences = useCallback(

  (patch) => (
    api.patch('/simul/archive/organization', patch)
      .then((response) => {
        const preferences = response.data?.preferences || {};
        setOrganizationPreferences((current) => ({ ...current, ...preferences }));
        return preferences;
      })
  ), []);

  const saveOrganizationView = useCallback(

  (name, definition) => (
    api.post('/simul/archive/organization/saved-views', { name, definition })
      .then((response) => {
        const view = response.data?.saved_view;
        if (view) setOrganizationPreferences((current) => ({ ...current, saved_views: [...(current.saved_views || []).filter((item) => item.name !== view.name), view] }));
        return view;
      })
  ), []);

  const deleteOrganizationView = useCallback(

  (viewId) => (
    api.delete(`/simul/archive/organization/saved-views/${encodeURIComponent(viewId)}`)
      .then(() => setOrganizationPreferences((current) => ({ ...current, saved_views: (current.saved_views || []).filter((item) => item.view_id !== viewId) })))
  ), []);

  const applyOrganizationView = useCallback(

  (view) => {
    const definition = view?.definition || {};
    setOrganizationFilters({ ...EMPTY_ORGANIZATION_FILTERS, ...(definition.filters || {}) });
    if (definition.sort) setRegistrySort(definition.sort);
    if (['list', 'grid', 'compact'].includes(definition.view_mode)) setRegistryViewMode(definition.view_mode);
    if (Array.isArray(definition.columns)) {
      setOrganizationPreferences((current) => ({ ...current, columns: definition.columns }));
    }
    setDocumentFirst(0);
  }, []);

  const clearFilters =

  () => {
    setSearch('');
    setMatterNumber('');
    setFolderPath('');
    setDateFrom('');
    setDateTo('');
    setRegistryStatus('all');
    setOrganizationFilters(EMPTY_ORGANIZATION_FILTERS);
    setDocumentFirst(0);
  };

  const refreshRegistry =

  () => {
    invalidateDocumentData(documentsStore);
    if (activeFolder) loadFolderDocuments(activeFolder, folderSearch);
    loadDocuments();
    loadRecentDocuments();
    if (registryDisplay === 'folders') loadFolderCatalog();
    if (registryDisplay === 'review') loadMatterSuggestions();
  };

  const closeFolderDrawer =

  () => {
    dataCache.stop('folder');
    setActiveFolder(null);
    setFolderDocuments([]);
    setFolderDocumentsTotal(0);
    setFolderDocumentsHasNext(false);
    setFolderDocumentsNextFirst(0);
    setFolderDocumentsLoading(false);
    setFolderDocumentsLoadingMore(false);
    setFolderDocumentsError('');
    setFolderSearch('');
  };

  const updateRegistryDisplay =

  (value) => {
    setRegistryDisplay(value);
    if (value === 'files') {
      if (!['modified_desc', 'modified_asc', 'name_asc', 'name_desc'].includes(registrySort)) setRegistrySort('modified_desc');
      if (!['list', 'grid'].includes(registryViewMode)) setRegistryViewMode('list');
    }
    if (value === 'review') loadMatterSuggestions();
  };

  const selectSuggestedMatter =

  (documentId, value) => {
    setMatterSuggestionSelections((current) => ({ ...current, [documentId]: value }));
  };

  const assignSuggestedMatter =

  (suggestion, value) => {
    const documentId = suggestion?.document_id;
    const selectedMatter = String(value || '').trim();
    if (!documentId || !selectedMatter) return;
    setMatterSuggestionSubmitting((current) => ({ ...current, [documentId]: true }));
    api.patch(`/simul/archive/documents/${encodeURIComponent(documentId)}/location`, {
      matter_number: selectedMatter,
      folder_path: '',
    })
      .then(

      () => {
        setMatterSuggestions((current) => current.filter((item) => item.document_id !== documentId));
        setMatterSuggestionTotal((current) => Math.max(0, current - 1));
        setMatterSuggestionSelections((current) => {
          const next = { ...current };
          delete next[documentId];
          return next;
        });
        showToast && showToast({
          severity: 'success',
          summary: 'Matter assigned',
          detail: `${suggestion.filename || 'Document'} is now filed under ${selectedMatter}.`,
        });
        loadDocuments({ background: true });
        loadFolderCatalog({ background: true });
      })
      .catch((err) => {
        const detail = err?.response?.data?.error || err.message || 'Could not assign the matter.';
        setMatterSuggestionsError(detail);
        showToast && showToast({ severity: 'error', summary: 'Assignment failed', detail });
      })
      .finally(() => setMatterSuggestionSubmitting((current) => ({ ...current, [documentId]: false })));
  };

  const openFolderDrawer =

  (folder) => {
    const nextSearch = folderBrowserIdentity(activeFolder) === folderBrowserIdentity(folder) ? folderSearch : '';
    setRegistryDisplay('folders');
    setFolderSearch(nextSearch);
    setActiveFolder(folder);
    loadFolderDocuments(folder, nextSearch, { force: false });
  };

  const uploadExternalFilesToFolder =

  async (folder, dataTransfer) => {
    try {
      const files = await droppedFiles(dataTransfer);
      if (!files.length) {
        showToast && showToast({
          severity: 'warn',
          summary: 'Nothing to upload',
          detail: 'Drop one or more files or a folder from your computer.',
        });
        return;
      }
      setPendingFolderUpload({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        files,
        folder,
      });
    } catch (error) {
      showToast && showToast({
        severity: 'error',
        summary: 'Could not read dropped files',
        detail: error?.message || 'Try dropping the files again.',
      });
    }
  };


  const openNewFolder =

  (parentFolder = null) => {
    const contextualFolder = parentFolder || null;
    const parentPath = contextualFolder ? folderCreationPath(contextualFolder, folderCatalog) : '';
    setNewFolderForm({
      matter_number: contextualFolder?.matter_number || '',
      parent_folder_id: contextualFolder?.folder_id || '',
      parent_folder_path: parentPath,
      parent_label: contextualFolder?.name || folderTitle(contextualFolder?.folder_path),
      folder_name: '',
      is_subfolder: Boolean(contextualFolder),
    });
    setNewFolderError('');
    setNewFolderOpen(true);
  };

  const submitNewFolder =

  () => {
    const matter = String(newFolderForm.matter_number || '').trim();
    const parentPath = normalizeFolderPath(newFolderForm.parent_folder_path);
    const folderName = normalizeFolderPath(newFolderForm.folder_name);
    const path = normalizeFolderPath([parentPath, folderName].filter(Boolean).join('/'));
    if (!path) return;
    setNewFolderSubmitting(true);
    setNewFolderError('');
    const parentFolderId = String(newFolderForm.parent_folder_id || '').trim();
    const payload = {
      matter_number: matter,
      folder_path: path,
      folder_name: folderName,
      ...(parentFolderId ? {
        parent_folder_id: parentFolderId,
      } : {}),
    };
    api.post('/documents/folders', payload)
      .then(

      (response) => {
        const folder = response.data?.folder;
        setNewFolderOpen(false);
        showToast && showToast({ severity: 'success', summary: 'Folder created', detail: `${path} is ready for documents.` });
        if (!folder) return loadFolderCatalog({ background: true });

        setFolderCatalog((current) => canonicalFolderCatalog([...current, folder]));

        // The POST response is enough to show the new folder in the current column.
        // Keep the user's current path and files mounted while generated parents and
        // counts reconcile quietly in the background.
        return loadFolderCatalog({ background: true });
      })
      .catch((err) => setNewFolderError(err?.response?.data?.error || err.message || 'Could not create folder.'))
      .finally(() => setNewFolderSubmitting(false));
  };

  const openDocumentLocation =

  () => {
    if (!selectedDocument) return;
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMoveError('');
    setMoveTarget({ type: 'file', item: selectedDocument, context: 'detail' });
  };

  const openDrawerDocumentLocation =

  (document) => {
    if (!document?.document_id) return;
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMoveError('');
    setMoveTarget({ type: 'file', action: 'move', item: document, context: 'drawer' });
  };

  const openDocumentCopy =

  (document) => {
    if (!document?.document_id) return;
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMoveError('');
    setMoveTarget({ type: 'file', action: 'copy', item: document, context: 'registry' });
  };

  const openFolderLocation =

  (folder) => {
    if (!folder?.folder_id || !folder.can_move_internally) return;
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMoveError('');
    setMoveTarget({ type: 'folder', item: folder, context: 'drawer' });
  };

  const openFolderMerge =

  (folder) => {
    if (!folder?.folder_id || !folder.can_move_internally) return;
    if (folderDestinationScope(folder, folderCatalog) !== 'internal') return;
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMoveError('');
    setMoveTarget({ type: 'folder', action: 'merge', item: folder, context: 'drawer' });
  };

  const openFolderCopy =

  (folder) => {
    if (!folder?.folder_id || folder.capabilities?.can_copy === false) return;
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMoveError('');
    setMoveTarget({ type: 'folder', action: 'copy', item: folder, context: 'drawer' });
  };

  const runBulkOperation = useCallback(

  (action, selection, clearSelection, options = {}) => {
    if (!selection || bulkSubmitting) return Promise.resolve(null);
    setBulkSubmitting(true);
    setBulkResult(null);
    return api.post('/simul/archive/operations', {
      action,
      selection,
      options,
      idempotency_key: `${action}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
    })
      .then(

      (response) => {
        const operation = response.data?.operation || {};
        const results = Array.isArray(operation.results) ? operation.results : [];
        const completed = results.filter((result) => ['completed', 'unchanged'].includes(result.status));
        const failed = results.filter((result) => result.status === 'failed');
        setBulkResult({ action, completed, failed, operationId: operation.operation_id || '' });
        if (action === 'download') {
          completed.forEach(

          (result) => {
            if (!result.download_url) return;
            const link = document.createElement('a');
            link.href = apiUrl(result.download_url);
            link.download = '';
            document.body.appendChild(link);
            link.click();
            link.remove();
          });
        }
        if (failed.length && completed.length === 0) {
          throw new Error(failed[0]?.error || `The ${action} operation could not be completed.`);
        }
        clearSelection && clearSelection();
        showToast && showToast({
          severity: failed.length ? 'warn' : 'success',
          summary: failed.length ? 'Bulk action partly completed' : 'Bulk action completed',
          detail: `${completed.length} ${completed.length === 1 ? 'item' : 'items'} completed${failed.length ? ` · ${failed.length} failed` : ''}.`,
        });
        return Promise.all([
          loadDocuments({ background: true }),
          loadFolderCatalog({ background: true }),
        ]).then(([, nextFolders]) => {
          if (action === 'trash' && activeFolder) {
            const remainingFolder = (nextFolders || []).find((folder) => (
              folderBrowserIdentity(folder) === folderBrowserIdentity(activeFolder)
            ));
            if (remainingFolder) {
              return loadFolderDocuments(remainingFolder, folderSearch, { background: true }).then(() => operation);
            }
            setActiveFolder(null);
            setFolderDocuments([]);
          }
          return operation;
        });
      })
      .catch((err) => {
        const detail = err?.response?.data?.error || err.message || `Could not ${action} the selected documents.`;
        showToast && showToast({ severity: 'error', summary: 'Bulk action failed', detail });
        throw err;
      })
      .finally(() => setBulkSubmitting(false));
  }, [activeFolder, bulkSubmitting, folderSearch, loadDocuments, loadFolderCatalog, loadFolderDocuments, setActiveFolder, showToast]);

  const openBulkDocumentLocation = useCallback(

  (selection, clearSelection, count) => {
    if (!selection) return;
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMoveError('');
    setMoveTarget({ type: 'bulk', action: 'move', selection, clearSelection, count, context: 'registry' });
  }, [folderCatalog.length, loadFolderCatalog]);

  const openBulkDocumentCopy = useCallback(

  (selection, clearSelection, count) => {
    if (!selection) return;
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMoveError('');
    setMoveTarget({ type: 'bulk', action: 'copy', selection, clearSelection, count, context: 'registry' });
  }, [folderCatalog.length, loadFolderCatalog]);

  const clipboardController = useDocumentClipboardController({
    activeFolder,
    keyboard: workspaceMode === 'registry',
    onPasteFiles: uploadExternalFilesToFolder,
    onPaste: (clipboard, folder) => runBulkOperation(clipboard.action, clipboard.selection, null, {
      destination: folderMovePayload(folder),
      conflict_policy: 'keep_both',
    }),
    onRemember: ({ action }) => {
      showToast && showToast({
        severity: 'info',
        summary: action === 'move' ? 'Ready to move' : 'Copied',
        detail: 'Open or right-click a destination folder, then paste.',
      });
    },
    onRememberError: () => {
      showToast?.({ severity: 'error', summary: 'Copy failed', detail: 'Allow clipboard access in your browser and try again.' });
    },
    onPasteError: (error) => {
      showToast?.({ severity: 'error', summary: 'Paste failed', detail: error.message });
    },
    onMissingDestination: () => {
      showToast && showToast({
        severity: 'info',
        summary: 'Open a destination folder',
        detail: 'Then press Paste again.',
      });
    },
  });
  const {
    canPaste: canPasteClipboard,
    rememberSelection: rememberClipboard,
    copyDocument: copyDocumentToClipboard,
    cutDocument: cutDocumentToClipboard,
    copyFolder: copyFolderToClipboard,
    cutFolder: cutFolderToClipboard,
    pasteIntoFolder: pasteClipboardToFolder,
  } = clipboardController;

  const renameDocument = useCallback(

  (document, requestedName) => {
    const currentName = String(document?.filename || '');
    const name = requestedName === undefined ? window.prompt('Rename document', currentName) : requestedName;
    if (name === null || name.trim() === currentName) return Promise.resolve(null);
    return runBulkOperation('rename', {
      mode: 'items',
      items: [{
        item_type: 'document',
        item_id: document.document_id,
        expected_updated_at: document.updated_at,
      }],
    }, null, { name: name.trim() }).then(async (operation) => {
      if (operation && document.document_id === selectedDocumentId) {
        await loadVersions(document.document_id, { background: true });
      }
      return operation;
    });
  }, [loadVersions, runBulkOperation, selectedDocumentId]);

  const duplicateDocument = useCallback(

  (document) => runBulkOperation('copy', {
    mode: 'items',
    items: [{
      item_type: 'document',
      item_id: document.document_id,
      expected_updated_at: document.updated_at,
    }],
  }, null, {
    destination: {
      destination_folder_id: document.folder_id || '',
      matter_number: document.matter_number || '',
      folder_path: document.folder_path || '',
    },
    conflict_policy: 'keep_both',
  }), [runBulkOperation]);

  const moveItemToFolder =

  async ({ type, item }, destination) => {
    const destinationId = String(destination?.folder_id || '').trim();
    if (!destinationId) throw new Error('Choose a managed folder tab as the destination.');
    const isFolderMove = type === 'folder';
    if (isFolderMove) {
      if (!item?.folder_id || !item.can_move_internally) throw new Error('This folder cannot be moved.');
      await api.patch(`/documents/folders/${encodeURIComponent(item.folder_id)}/location`, {
        destination_parent_id: destinationId,
      });
    } else {
      if (!item?.document_id || item.available_locally === false) throw new Error('This file is not available to move.');
      await api.patch(`/simul/archive/documents/${encodeURIComponent(item.document_id)}/location`, (
        folderMovePayload(destination)
      ));
    }

    const [, nextFolders] = await Promise.all([
      loadDocuments({ background: true }),
      loadFolderCatalog({ background: true }),
    ]);
    if (activeFolder) {
      const refreshedActiveFolder = nextFolders.find((folder) => (
        String(folder.folder_id || '') === String(activeFolder.folder_id || '')
      )) || activeFolder;
      setActiveFolder(refreshedActiveFolder);
      await loadFolderDocuments(refreshedActiveFolder, folderSearch, { background: true });
    }
    showToast && showToast({
      severity: 'success',
      summary: isFolderMove ? 'Folder moved' : 'Document moved',
      detail: `${item.name || item.filename || (isFolderMove ? 'Folder' : 'Document')} moved to ${destination.name || folderTitle(destination.folder_path)}.`,
    });
  };

  const renameFolder =

  async (folder, name) => {
    if (!folder?.folder_id || folder.folder_type !== 'location' || !folder.can_move_internally) {
      throw new Error('Only internal subfolders can be renamed.');
    }
    const response = await api.patch(
      `/documents/folders/${encodeURIComponent(folder.folder_id)}`,
      { name }
    );
    const updatedFolder = response.data?.folder;
    if (!updatedFolder) throw new Error('The folder was renamed, but its updated location could not be loaded.');
    setActiveFolder(updatedFolder);
    await Promise.all([
      loadDocuments({ background: true }),
      loadFolderCatalog({ background: true }),
      loadFolderDocuments(updatedFolder, folderSearch, { background: true }),
    ]);
    showToast && showToast({
      severity: 'success',
      summary: 'Folder renamed',
      detail: `${updatedFolder.name || name} is ready.`,
    });
    return updatedFolder;
  };

  const moveExcludedFolderIds = useMemo(

  () => {
    const item = moveTarget?.item;
    if (!item) return [];
    if (moveTarget.type === 'file') return item.folder_id ? [item.folder_id] : [];
    const rootId = String(item.folder_id || '');
    const excluded = new Set([rootId].filter(Boolean));
    const pending = [rootId].filter(Boolean);
    while (pending.length > 0) {
      const parentId = pending.shift();
      for (const folder of folderCatalog) {
        const folderId = String(folder.folder_id || '');
        if (folderId && String(folder.parent_folder_id || '') === parentId && !excluded.has(folderId)) {
          excluded.add(folderId);
          pending.push(folderId);
        }
      }
    }
    if ((moveTarget.action || 'move') === 'move' && item.parent_folder_id) excluded.add(String(item.parent_folder_id));
    return [...excluded];
  }, [folderCatalog, moveTarget]);

  const moveExcludedDestinationKeys = useMemo(() => {
    const item = moveTarget?.item;
    if (!item || item.folder_id) return [];
    return [folderDestinationKey({
      matter_number: item.matter_number,
      folder_path: item.folder_path,
      folder_type: normalizeFolderPath(item.folder_path) ? 'location' : 'matter',
    })];
  }, [moveTarget]);

  const moveCreateParent = useMemo(

  () => {
    if (moveTarget?.type !== 'file') return null;
    const matterNumber = String(moveTarget.item?.matter_number || '').trim().toLowerCase();
    if (!matterNumber) return null;
    return folderCatalog.find((folder) => (
      folder.folder_type === 'matter' &&
      String(folder.matter_number || '').trim().toLowerCase() === matterNumber
    )) || null;
  }, [folderCatalog, moveTarget]);

  const createMoveDestination =

  async (payload) => {
    const response = await api.post('/documents/folders', payload);
    const folder = response.data?.folder;
    if (!folder) throw new Error('The folder was created, but its destination could not be loaded.');
    setFolderCatalog((current) => canonicalFolderCatalog([...current, folder]));
    showToast && showToast({
      severity: 'success',
      summary: 'Folder created',
      detail: `${folder.folder_path || payload.folder_path} is selected as the destination.`,
    });
    return folder;
  };

  const submitMove =

  (destination, conflictPolicy = 'keep_both') => {
    if (!moveTarget?.item && moveTarget?.type !== 'bulk') return;
    setMoveSubmitting(true);
    setMoveError('');
    const action = moveTarget.action || 'move';
    if (moveTarget.type === 'bulk') {
      runBulkOperation(action, moveTarget.selection, moveTarget.clearSelection, {
        destination: folderMovePayload(destination),
        conflict_policy: conflictPolicy,
      })
        .then(() => setMoveTarget(null))
        .catch((err) => setMoveError(
          err?.response?.data?.error || err.message || `Could not ${action} the selected documents.`
        ))
        .finally(() => setMoveSubmitting(false));
      return;
    }
    const isFolderMove = moveTarget.type === 'folder';
    const request = isFolderMove && action === 'merge'
      ? api.post(`/documents/folders/${encodeURIComponent(moveTarget.item.folder_id)}/merge-matter`, {
          destination_matter_folder_id: String(destination?.folder_id || '').trim(),
        })
      : isFolderMove && action === 'move'
      ? api.patch(`/documents/folders/${encodeURIComponent(moveTarget.item.folder_id)}/location`, {
          destination_parent_id: String(destination?.folder_id || '').trim(),
        })
      : action === 'move'
      ? api.patch(`/simul/archive/documents/${encodeURIComponent(moveTarget.item.document_id)}/location`, (
          folderMovePayload(destination)
        ))
      : api.post('/simul/archive/operations', {
          action,
          selection: { mode: 'items', items: [{
            item_type: moveTarget.type === 'folder' ? 'folder' : 'document',
            item_id: moveTarget.item.folder_id || moveTarget.item.document_id,
            expected_updated_at: moveTarget.item.updated_at,
          }] },
          options: { destination: folderMovePayload(destination), conflict_policy: conflictPolicy },
          idempotency_key: `${action}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
        });
    request
      .then(

      (response) => {
        if (isFolderMove) {
          const updatedFolder = action === 'move' ? response.data?.folder : null;
          if (updatedFolder?.folder_id) {
            setFolderCatalog((current) => canonicalFolderCatalog(current.map((folder) => (
              String(folder.folder_id || '') === String(updatedFolder.folder_id)
                ? { ...folder, ...updatedFolder }
                : folder
            ))));
            if (String(activeFolder?.folder_id || '') === String(updatedFolder.folder_id)) {
              setActiveFolder((current) => ({ ...current, ...updatedFolder }));
            }
          }
          return Promise.all([
            loadDocuments({ background: true }),
            loadFolderCatalog({ background: true }),
          ]);
        }
        const refreshes = [
          loadDocuments({ background: true }),
          loadFolderCatalog({ background: true }),
        ];
        if (moveTarget.context === 'detail') refreshes.push(loadVersions(moveTarget.item.document_id));
        if (moveTarget.context === 'drawer' && activeFolder) {
          refreshes.push(loadFolderDocuments(activeFolder, folderSearch, { background: true }));
        }
        return Promise.all(refreshes);
      })
      .then(() => {
        setMoveTarget(null);
        showToast && showToast({
          severity: 'success',
          summary: action === 'copy'
            ? `${isFolderMove ? 'Folder' : 'Document'} copied`
            : action === 'merge'
            ? 'Folder merged with matter'
            : isFolderMove ? 'Folder moved' : 'Document moved',
          detail: action === 'copy'
            ? `The ${isFolderMove ? 'folder' : 'document'} and its versions are ready in the destination.`
            : action === 'merge'
            ? 'Its files and subfolders are now inside the canonical matter folder.'
            : isFolderMove
            ? 'The folder and everything inside it now use the new internal location.'
            : 'The document now uses the new internal location.',
        });
      })
      .catch((err) => setMoveError(
        err?.response?.data?.error || err.message || `Could not move ${moveTarget.type}.`
      ))
      .finally(() => setMoveSubmitting(false));
  };

  const selectPreviewMode = useCallback(

  (mode) => {
    // A tab click is an explicit choice, just like opening a version. Preserve
    // it while comparison creation, polling, and status refreshes finish.
    manualVersionSelectionRef.current = mode === 'version';
    setPreviewMode(mode);
  }, []);

  const openVersionPreview = useCallback(

  (revisionId) => {
    requestedPreviewVersionRef.current = revisionId;
    // A deliberate version selection may revisit a pair whose in-flight
    // response is being discarded. Allow that pair to be requested again.
    autoRedlineRequestRef.current.clear();
    beginDocumentRequest('comparison');
    beginDocumentRequest('comparison-route');
    setComparisonLoading(false);
    manualVersionSelectionRef.current = true;
    comparisonSelectionModeRef.current = 'default';
    setSelectedVersionId(revisionId);
    setPreviewVersionId(revisionId);
    setSelectedChangeId('');
    const graphVersions = sortedGraphVersions(matchingVersions);
    const selectedIndex = graphVersions.findIndex((version) => version.revision_id === revisionId);
    const prior = graphVersions[selectedIndex + 1] || null;
    setBaseRevisionId(prior?.revision_id || revisionId);
    setTargetRevisionId(prior ? revisionId : '');
    setComparison(INITIAL_COMPARISON);
    setComparisonError('');
    setPreviewMode('version');
  }, [beginDocumentRequest, matchingVersions]);

  useEffect(

  () => {
    if (!routedVersionId || routedVersionId === 'latest') {
      appliedRoutedVersionRef.current = '';
      return;
    }
    if (loadedVersionsDocumentRef.current !== selectedDocumentId) return;
    if (!versions.some((version) => version.revision_id === routedVersionId)) return;
    const routeSelectionKey = `${selectedDocumentId}:${routedVersionId}`;
    if (appliedRoutedVersionRef.current === routeSelectionKey) return;
    appliedRoutedVersionRef.current = routeSelectionKey;
    if (previewVersionId === routedVersionId) return;
    openVersionPreview(routedVersionId);
  }, [openVersionPreview, previewVersionId, routedVersionId, selectedDocumentId, versions]);

  const shareLatest =

  () => {
    if (!selectedDocumentId) return;
    const shareUrl = new URL(`${documentDetailPath(selectedDocumentId)}?version=latest`, window.location.origin).toString();
    const showLink =

    () => showToast && showToast({ severity: 'info', summary: 'Link to latest version', detail: shareUrl });
    if (!navigator.clipboard?.writeText) {
      showLink();
      return;
    }
    navigator.clipboard.writeText(shareUrl)
      .then(() => showToast && showToast({ severity: 'success', summary: 'Latest version link copied' }))
      .catch(showLink);
  };

  const shareVersion =

  (version) => {
    const shareUrl = documentVersionUrl(selectedDocumentId, version);
    if (!shareUrl) return;
    if (!navigator.clipboard?.writeText) {
      showToast && showToast({ severity: 'info', summary: `${versionLabel(version)} link`, detail: shareUrl });
      return;
    }
    navigator.clipboard.writeText(shareUrl)
      .then(() => showToast && showToast({ severity: 'success', summary: 'Version link copied', detail: versionLabel(version) }))
      .catch(() => showToast && showToast({ severity: 'info', summary: `${versionLabel(version)} link`, detail: shareUrl }));
  };

  const activeComparison = comparison || selectedChange?.comparison || INITIAL_COMPARISON;
  const redlineReady = Boolean(
    activeComparison?.status === 'ready' &&
    activeComparison.comparison_id &&
    comparisonArtifactIsCurrent(activeComparison)
  );
  const redlineAvailable = Boolean(baseRevisionId && targetRevisionId && baseRevisionId !== targetRevisionId);
  const activePreviewMode = previewMode;
  const actionIsPendingWordEdit = Boolean(actionVersion?.pending_edit);
  const canUpload = Boolean(selectedDocumentId && actionVersion && !actionIsPendingWordEdit);
  const actionIsBranch = Boolean(
    actionVersion && (branchName(actionVersion) !== 'main' || actionVersion.forked_from_revision_id)
  );
  const actionIsUnmergedBranch = Boolean(
    actionIsBranch &&
    actionVersion?.merge_status !== 'merged' &&
    !actionVersion?.merged_into_revision_id
  );
  const isVersionPromotable = useCallback(

  (version) => Boolean(
    version &&
    latestVersion &&
    branchName(version) === 'main' &&
    !version.pending_edit &&
    isDocxVersion(version) &&
    version.revision_id !== latestVersion.revision_id
  ), [latestVersion]);
  const mergeReviewChange = useMemo(
    () => {
      if (!actionVersion?.revision_id || !latestVersion?.revision_id) return null;
      return changes.find((change) => changeMatchesRevisionPair(
        change,
        actionVersion.revision_id,
        latestVersion.revision_id
      )) || null;
    },
    [actionVersion?.revision_id, changes, latestVersion?.revision_id]
  );
  const activeMergeComparison = comparisonMatchesRevisionPair(
    activeComparison,
    actionVersion?.revision_id,
    latestVersion?.revision_id
  ) ? activeComparison : null;
  const storedMergeComparison = mergeReviewChange?.comparison || null;
  const mergeReviewComparison = readyComparisonMatchesMerge(
    storedMergeComparison,
    actionVersion?.revision_id,
    latestVersion?.revision_id
  ) ? storedMergeComparison : activeMergeComparison || storedMergeComparison;
  const mergeReviewReady = readyComparisonMatchesMerge(
    mergeReviewComparison,
    actionVersion?.revision_id,
    latestVersion?.revision_id
  );
  const mergeComparisonId = mergeReviewReady ? mergeReviewComparison.comparison_id : '';
  const selectedMergePath = mergePathState(matchingVersions, actionVersion, latestVersion);
  const mergeReplacesCurrent = selectedMergePath.currentDiverged;
  const canPrepareMergeReview = Boolean(
    selectedDocumentId &&
    actionVersion &&
    latestVersion &&
    isDocxVersion(actionVersion) &&
    isDocxVersion(latestVersion) &&
    actionIsUnmergedBranch &&
    selectedMergePath.mergeable &&
    !actionIsPendingWordEdit &&
    actionVersion.revision_id !== latestVersion.revision_id &&
    !mergeReviewReady
  );
  const mergeReviewStatus = canPrepareMergeReview ?
    comparisonStatusLabel(mergeReviewChange?.status || mergeReviewChange?.comparison?.status || '') :
    '';
  const canMerge = Boolean(
    selectedDocumentId &&
    actionVersion &&
    latestVersion &&
    isDocxVersion(actionVersion) &&
    actionIsUnmergedBranch &&
    selectedMergePath.mergeable &&
    !actionIsPendingWordEdit &&
    actionVersion.revision_id !== latestVersion.revision_id &&
    latestVersion.content_sha256 &&
    mergeReviewReady
  );

  useEffect(() => {
    setPreviewRequested(false);
  }, [activePreviewMode, previewVersionId, comparison?.comparison_id]);

  useEffect(

  () => {
    if (
      comparisonSelectionModeRef.current !== 'default' ||
      (manualVersionSelectionRef.current && requestedPreviewVersionRef.current && requestedPreviewVersionRef.current !== previewVersionId) ||
      !selectedDocumentId ||
      loadedVersionsDocumentRef.current !== selectedDocumentId ||
      !previewVersionId ||
      versionsLoading ||
      changesLoading ||
      comparisonLoading
    ) return;
    const graphVersions = sortedGraphVersions(matchingVersions);
    const selectedIndex = graphVersions.findIndex((version) => version.revision_id === previewVersionId);
    const selected = graphVersions[selectedIndex];
    const prior = graphVersions[selectedIndex + 1];
    if (!selected || !prior || !canAttemptVersionComparison(selected) || !canAttemptVersionComparison(prior)) return;
    const keepDocumentVisible = manualVersionSelectionRef.current;
    setBaseRevisionId(prior.revision_id);
    setTargetRevisionId(selected.revision_id);
    if (!keepDocumentVisible) setPreviewMode('redline');
    const activePairMatches = comparisonMatchesRevisionPair(activeComparison, prior.revision_id, selected.revision_id);
    const existingChange = changes.find((change) => changeMatchesRevisionPair(
      change,
      prior.revision_id,
      selected.revision_id
    ));
    const pairComparison = activePairMatches ? activeComparison : existingChange?.comparison;
    if (pairComparison && pairComparison.status !== 'failed' && !comparisonNeedsUpgrade(pairComparison)) {
      if (!activePairMatches) {
        setSelectedChangeId(existingChange.change_id);
        setComparison(pairComparison);
      }
      if (pairComparison.status === 'ready' && !keepDocumentVisible) setPreviewMode('redline');
      return;
    }
    // Failed stored results need a fresh request too. Share the same guard with
    // initial generation and upgrades so another failure cannot cause a loop.
    const requestKey = `${selectedDocumentId}:${comparisonRenderer}:artifact-${CURRENT_COMPARISON_ARTIFACT_VERSION}:${prior.revision_id}:${selected.revision_id}`;
    if (autoRedlineRequestRef.current.has(requestKey)) return;
    autoRedlineRequestRef.current.add(requestKey);
    startComparison(prior.revision_id, selected.revision_id, { activatePreview: !keepDocumentVisible });
  }, [
    activeComparison,
    changes,
    changesLoading,
    comparisonLoading,
    comparisonRenderer,
    matchingVersions,
    previewVersionId,
    selectedDocumentId,
    startComparison,
    versionsLoading,
  ]);

  const updateWriteForm =

  (updates) => {
    setWriteForm((current) => ({ ...current, ...updates }));
  };

  const closeWriteModal =

  () => {
    if (writeSubmitting) return;
    setWriteModal(null);
    setWriteForm({});
    setWriteFile(null);
    setWriteError('');
  };

  const openWriteModal =

  (type, requestedVersion = null) => {
    const modalVersion = requestedVersion || actionVersion;
    if (!modalVersion) return;
    const currentBranch = branchName(modalVersion);
    const draftBranch = currentBranch === 'main' ? `draft-${shortRevision(modalVersion.revision_id)}` : currentBranch;
    const createsFork = type === 'upload' && currentBranch === 'main' && modalVersion.revision_id !== latestVersion?.revision_id;
    const editCreatesFork = type === 'edit' && currentBranch === 'main' && modalVersion.revision_id !== latestVersion?.revision_id;
    setWriteError('');
    setWriteFile(null);
    setWriteForm({
      reason: '',
      branch_name: type === 'fork' || createsFork ? draftBranch : currentBranch,
      lock_after_create: type === 'fork',
      creates_fork: createsFork || editCreatesFork,
      confirm: false,
    });
    setWriteModal({ type, revision_id: modalVersion.revision_id });
  };

  const openVersionMetadata =

  (version) => {
    if (!version?.revision_id) return;
    comparisonSelectionModeRef.current = 'default';
    setSelectedVersionId(version.revision_id);
    setPreviewVersionId(version.revision_id);
    setWriteError('');
    setWriteFile(null);
    setWriteForm({
      version_name: version.version_name || '',
      version_note: version.version_note || '',
    });
    setWriteModal({ type: 'metadata', revision_id: version.revision_id });
  };

  const openVersionPromotion =

  (version) => {
    if (!isVersionPromotable(version)) return;
    openVersionPreview(version.revision_id);
    openWriteModal('revert', version);
  };

  const openLockModal =

  (version) => {
    if (!version || !lockedBy(version) || !version.lock_id) return;
    openWriteModal('unlock', version);
  };

  const refreshAfterWrite =

  (revisionId = '', { exitComparison = false } = {}) => {
    const documentId = selectedDocumentId;
    return Promise.all([
      loadDocuments(),
      loadVersions(documentId, { background: true }),
      loadChanges(documentId),
    ]).then(() => {
      if (revisionId) {
        setSelectedVersionId(revisionId);
        setPreviewVersionId(revisionId);
        setPreviewMode('version');
        if (exitComparison) {
          manualVersionSelectionRef.current = true;
          comparisonSelectionModeRef.current = 'default';
          // React Router can commit this navigation after the refreshed versions.
          // Retain the old route's consumed key so its comparison cannot overwrite
          // the accepted checkpoint while the URL transition is pending.
          appliedRoutedComparisonRef.current = `${documentId}:${routedComparisonId}`;
          beginDocumentRequest('comparison-route');
          setSelectedChangeId('');
          setComparison(INITIAL_COMPARISON);
          navigate(
            `${documentDetailPath(documentId)}?version=${encodeURIComponent(revisionId)}`,
            { replace: true }
          );
        }
      }
    });
  };

  const submitWriteAction =

  () => {
    if (!writeModal || !selectedDocumentId || !writeActionVersion) return;
    const type = writeModal.type;
    setWriteSubmitting(true);
    setWriteError('');

    if (type === 'edit') {
      setWriteModal(null);
      setWriteForm({});
      setWriteSubmitting(false);
      startWordEdit();
      return;
    }

    let request;
    if (type === 'fork') {
      request = api.post(`/simul/archive/documents/${encodeURIComponent(selectedDocumentId)}/forks`, {
        source_revision_id: writeActionVersion.revision_id,
        source_content_sha256: writeActionVersion.content_sha256 || '',
        branch_name: writeForm.branch_name || `draft-${shortRevision(writeActionVersion.revision_id)}`,
        reason: writeForm.reason || '',
        lock_after_create: Boolean(writeForm.lock_after_create),
      });
    } else if (type === 'lock') {
      request = api.post(`/simul/archive/documents/${encodeURIComponent(selectedDocumentId)}/locks`, {
        revision_id: writeActionVersion.revision_id,
        branch_name: branchName(writeActionVersion),
        reason: writeForm.reason || '',
      });
    } else if (type === 'unlock') {
      request = api.post(`/simul/archive/locks/${encodeURIComponent(writeActionVersion.lock_id)}/unlock`, {
        reason: writeForm.reason || '',
      });
    } else if (type === 'upload') {
      const formData = new FormData();
      formData.append('file', writeFile);
      formData.append('parent_revision_id', writeActionVersion.revision_id);
      formData.append('branch_name', writeForm.branch_name || branchName(writeActionVersion));
      formData.append('reason', writeForm.reason || '');
      formData.append('parent_content_sha256', writeActionVersion.content_sha256 || '');
      if (writeActionVersion.lock_id) formData.append('lock_id', writeActionVersion.lock_id);
      request = api.post(`/simul/archive/documents/${encodeURIComponent(selectedDocumentId)}/versions`, formData);
    } else if (type === 'merge') {
      request = api.post(`/simul/archive/documents/${encodeURIComponent(selectedDocumentId)}/merge`, {
        source_revision_id: writeActionVersion.revision_id,
        target_revision_id: latestVersion?.revision_id || '',
        target_content_sha256: latestVersion?.content_sha256 || '',
        comparison_id: mergeComparisonId,
        reason: writeForm.reason || '',
        confirm: Boolean(writeForm.confirm),
      });
    } else if (type === 'revert') {
      request = api.post(`/simul/archive/documents/${encodeURIComponent(selectedDocumentId)}/revert`, {
        source_revision_id: writeActionVersion.revision_id,
        current_revision_id: latestVersion?.revision_id || '',
        current_content_sha256: latestVersion?.content_sha256 || '',
        reason: writeForm.reason || '',
        confirm: Boolean(writeForm.confirm),
      });
    } else if (type === 'metadata') {
      request = api.patch(`/simul/archive/versions/${encodeURIComponent(writeActionVersion.revision_id)}/metadata`, {
        version_name: writeForm.version_name || '',
        version_note: writeForm.version_note || '',
      });
    }

    if (!request) {
      setWriteSubmitting(false);
      return;
    }

    request
      .then(

      (response) => {
        const nextRevisionId = response.data?.version?.revision_id || response.data?.document?.latest_revision_id || writeActionVersion.revision_id;
        setWriteModal(null);
        setWriteForm({});
        setWriteFile(null);
        showToast && showToast({ severity: 'success', summary: 'Document updated', detail: 'Archive workflow state was saved.' });
        return refreshAfterWrite(nextRevisionId);
      })
      .catch((err) => {
        const detail = err?.response?.data?.error || err.message || 'Document workflow action failed.';
        setWriteError(detail);
        showToast && showToast({ severity: 'error', summary: 'Action failed', detail });
      })
      .finally(() => setWriteSubmitting(false));
  };

  const startWordEdit =

  () => {
    if (!selectedDocumentId || !actionVersion || editStarting) return;
    setEditStarting(true);
    api.post(`/simul/archive/documents/${encodeURIComponent(selectedDocumentId)}/edit-sessions`, {
      source_revision_id: actionVersion.revision_id,
      source_content_sha256: actionVersion.content_sha256 || '',
      reason: 'Edit in Microsoft Word',
    })
      .then(

      (response) => {
        const session = response.data?.edit_session;
        const revisionId = response.data?.version?.revision_id;
        if (!session?.office_uri) throw new Error('The Word editing URL was not returned.');
        if (revisionId) {
          setSelectedVersionId(revisionId);
          setPreviewVersionId(revisionId);
        }
        wordEditObservedActivityRef.current = false;
        wordEditPendingRevisionRef.current = '';
        setWordEditMonitor({
          documentId: selectedDocumentId,
          startedAt: Date.now(),
        });
        window.location.href = session.office_uri;
        showToast && showToast({
          severity: 'success',
          summary: 'Opening Microsoft Word',
          detail: 'Word saves are collected into one version and finalized when the document closes.',
        });
        return refreshAfterWrite(revisionId);
      })
      .catch((err) => {
        const detail = err?.response?.data?.error || err.message || 'Failed to start Word editing.';
        showToast && showToast({ severity: 'error', summary: 'Could not open Word', detail });
      })
      .finally(() => setEditStarting(false));
  };

  const openWordEdit =

  () => {
    if (!actionVersion || editStarting) return;
    const createsFork = branchName(actionVersion) === 'main' && actionVersion.revision_id !== latestVersion?.revision_id;
    if (createsFork) {
      openWriteModal('edit');
      return;
    }
    startWordEdit();
  };

  const openNewDocument =

  (folder = null) => {
    const sourceFolder = folder?.folder_path ? folder : null;
    const sourceMatter = String(sourceFolder?.matter_number || '').trim();
    const sourcePath = folderCreationPath(sourceFolder, folderCatalog);
    setNewDocumentForm({
      mode: 'blank',
      title: '',
      lock_after_create: false,
      destination_kind: sourceMatter ? 'matter' : sourceFolder ? 'internal' : 'matter',
      matter_number: sourceMatter,
      folder_path: sourceFolder
        ? sourcePath || (sourceMatter ? '' : 'Firm documents')
        : '',
    });
    setNewDocumentFile(null);
    setNewDocumentError('');
    setNewDocumentOpen(true);
    api.get('/simul/archive/templates')
      .then((response) => setTemplates(response.data?.templates || []))
      .catch(() => setTemplates([]));
    if (folderCatalog.length === 0) loadFolderCatalog();
    setMatterChoicesLoading(true);
    api.get('/matters')
      .then((response) => setMatterChoices(Array.isArray(response.data) ? response.data : []))
      .catch(() => setMatterChoices([]))
      .finally(() => setMatterChoicesLoading(false));
  };

  const submitNewDocument =

  () => {
    setNewDocumentSubmitting(true);
    setNewDocumentError('');
    const mode = newDocumentForm.mode || 'blank';
    const destination = creationDestination(newDocumentForm, matterChoices, folderCatalog);
    let request;
    if (mode === 'upload') {
      const formData = new FormData();
      formData.append('mode', 'upload');
      formData.append('file', newDocumentFile);
      formData.append('title', newDocumentForm.title || 'Untitled document');
      formData.append('lock_after_create', newDocumentForm.lock_after_create ? 'true' : 'false');
      formData.append('matter_number', destination.matter_number);
      formData.append('folder_path', destination.folder_path);
      if (destination.folder_id) formData.append('folder_id', destination.folder_id);
      request = api.post('/simul/archive/documents', formData);
    } else {
      request = api.post('/simul/archive/documents', {
        mode,
        title: newDocumentForm.title,
        template_id: newDocumentForm.template_id || '',
        lock_after_create: Boolean(newDocumentForm.lock_after_create),
        matter_number: destination.matter_number,
        folder_path: destination.folder_path,
        ...(destination.folder_id ? { folder_id: destination.folder_id } : {}),
        filename: `${newDocumentForm.title || 'Untitled document'}.docx`,
      });
    }
    request
      .then(

      (response) => {
        const documentId = response.data?.document?.document_id;
        setNewDocumentOpen(false);
        if (documentId) {
          setCreatedDestination({
            document_id: documentId,
            matter_number: destination.matter_number,
            folder_path: destination.folder_path,
            segments: destination.segments,
          });
        }
        showToast && showToast({
          severity: 'success',
          summary: 'Document created',
          detail: `Saved in ${destination.plainLabel}.`,
        });
        return loadDocuments().then(() => documentId && openDocument(documentId, destination));
      })
      .catch((err) => setNewDocumentError(err?.response?.data?.error || err.message || 'Failed to create document.'))
      .finally(() => setNewDocumentSubmitting(false));
  };

  const openDocument =

  (documentId, destination = null, revisionId = '') => {
    if (!documentId) return;
    const file = [...folderDocuments, ...documents, ...recentDocuments].find((document) => (
      String(document.document_id) === String(documentId)
    ));
    if (file && shouldUseDocumentFileViewer(file)) {
      setFileViewerDocument(file);
      return;
    }
    setCreatedDestination(destination ? {
      document_id: documentId,
      matter_number: destination.matter_number,
      folder_path: destination.folder_path,
      segments: destination.segments,
    } : null);
    manualVersionSelectionRef.current = false;
    comparisonSelectionModeRef.current = 'default';
    setSelectedDocumentId(documentId);
    setSelectedChangeId('');
    setComparison(INITIAL_COMPARISON);
    setPreviewMode('version');
    setUtilityPanel('');
    setSharePanelOpen(false);
    setWorkspaceMode('document');
    navigate(revisionId
      ? `${documentDetailPath(documentId)}?version=${encodeURIComponent(revisionId)}`
      : documentDetailPath(documentId));
  };

  const backToRegistry =

  () => {
    restoreDocumentRouteRef.current = '';
    manualVersionSelectionRef.current = false;
    recentOpenKeyRef.current = '';
    setSharePanelOpen(false);
    setWorkspaceMode('registry');
    setSelectedDocumentId('');
    navigate('/documents');
  };

  const updateBaseRevision =

  (revisionId) => {
    beginDocumentRequest('comparison');
    beginDocumentRequest('comparison-route');
    setComparisonLoading(false);
    comparisonSelectionModeRef.current = 'custom';
    setBaseRevisionId(revisionId);
    setComparison(INITIAL_COMPARISON);
    setPreviewMode('version');
  };

  const updateTargetRevision =

  (revisionId) => {
    beginDocumentRequest('comparison');
    beginDocumentRequest('comparison-route');
    setComparisonLoading(false);
    comparisonSelectionModeRef.current = 'custom';
    setTargetRevisionId(revisionId);
    setComparison(INITIAL_COMPARISON);
    setPreviewMode('version');
  };

  const compareVersions =

  (baseId, targetId) => {
    comparisonSelectionModeRef.current = 'custom';
    startComparison(baseId, targetId);
  };

  return (
    <main className={`documents-archive-workspace documents-workspace documents-workspace--${workspaceMode}`}>
      {workspaceMode === 'registry' && (
        <>
          <DocumentsWorkspaceNavigation
            activePanel={utilityPanel}
            isAdmin={Boolean(auth.isAdmin)}
            onSelect={(panel) => {
              setUtilityPanel(panel);
            }} />
          {!utilityPanel && (
        <RegistryView
          recentDocuments={recentDocuments}
          recentDocumentsLoading={recentDocumentsLoading}
          recentDocumentsError={recentDocumentsError}
          documents={documents}
          documentsLoading={documentsLoading}
          documentsError={documentsError}
          documentsWarning={documentsWarning}
          search={search}
          debouncedSearch={debouncedSearch}
          matterNumber={matterNumber}
          folderPath={folderPath}
          dateFrom={dateFrom}
          dateTo={dateTo}
          matterNumbers={matterNumberOptions}
          folders={scopedFolders}
          catalogFolders={folderCatalog}
          foldersLoading={folderCatalogLoading}
          foldersError={folderCatalogError}
          registryStatus={registryStatus}
          registryDisplay={registryDisplay}
          nonMatterFolderPage={nonMatterFolderPage}
          matterFolderPage={matterFolderPage}
          matterSuggestions={matterSuggestions}
          matterSuggestionTotal={matterSuggestionTotal}
          matterSuggestionsLoading={matterSuggestionsLoading}
          matterSuggestionsError={matterSuggestionsError}
          matterSuggestionSelections={matterSuggestionSelections}
          matterSuggestionSubmitting={matterSuggestionSubmitting}
          activeFolder={activeFolder}
          folderDocuments={folderDocuments}
          folderDocumentsTotal={folderDocumentsTotal}
          folderDocumentsHasNext={folderDocumentsHasNext}
          folderDocumentsLoading={folderDocumentsLoading}
          folderDocumentsLoadingMore={folderDocumentsLoadingMore}
          folderDocumentsError={folderDocumentsError}
          folderSearch={folderSearch}
          totalFiltered={totalFiltered}
          totalDocuments={totalDocuments}
          documentFirst={documentFirst}
          pageSize={DOCUMENT_PAGE_SIZE}
          onSearchChange={(value) => { setSearch(value); setDocumentFirst(0); }}
          onApplyFilters={

          (values) => {
            setMatterNumber(values.matterNumber.trim());
            setFolderPath(values.folderPath.trim());
            setDateFrom(values.dateFrom);
            setDateTo(values.dateTo);
            setDocumentFirst(0);
          }}
          onRegistryStatusChange={(value) => { setRegistryStatus(value); setDocumentFirst(0); }}
          onRegistryDisplayChange={updateRegistryDisplay}
          onNonMatterFolderPageChange={setNonMatterFolderPage}
          onMatterFolderPageChange={setMatterFolderPage}
          onSelectSuggestedMatter={selectSuggestedMatter}
          onAssignSuggestedMatter={assignSuggestedMatter}
          onOpenFolder={openFolderDrawer}
          onExternalFileDrop={uploadExternalFilesToFolder}
          onNewFolder={openNewFolder}
          onCloseFolder={closeFolderDrawer}
          onFolderSearchChange={(value) => {
            setFolderSearch(value);
            const nextLength = String(value || '').trim().length;
            if (activeFolder && (nextLength === 0 || (nextLength < 3 && folderSearch.trim().length >= 3))) {
              loadFolderDocuments(activeFolder);
            }
          }}
          onLoadMoreFolderDocuments={() => {
            if (!activeFolder || folderDocumentsLoading || folderDocumentsLoadingMore) return;
            if (folderDocumentsError && folderDocuments.length === 0) {
              loadFolderDocuments(activeFolder, folderSearch);
              return;
            }
            if (!folderDocumentsHasNext) {
              if (folderDocumentsError) loadFolderDocuments(activeFolder, folderSearch);
              return;
            }
            loadFolderDocuments(activeFolder, folderSearch, {
              first: folderDocumentsNextFirst,
              append: true,
            });
          }}
          onRefresh={refreshRegistry}
          onClearFilters={clearFilters}
          onOpenDocument={openDocument}
          onMoveDocument={openDrawerDocumentLocation}
          onMoveFolder={openFolderLocation}
          onMergeFolder={openFolderMerge}
          onCopyFolder={openFolderCopy}
          bulkBusy={bulkSubmitting}
          onBulkOperation={runBulkOperation}
          onStartBulkMove={openBulkDocumentLocation}
          onStartBulkCopy={openBulkDocumentCopy}
          onCopyDocument={openDocumentCopy}
          onCopyDocumentToClipboard={copyDocumentToClipboard}
          onCutDocument={cutDocumentToClipboard}
          onCopyFolderToClipboard={copyFolderToClipboard}
          onCutFolder={cutFolderToClipboard}
          onPasteFolder={pasteClipboardToFolder}
          canPaste={canPasteClipboard}
          onCutSelection={(selection) => rememberClipboard('move', selection)}
          onCopySelection={(selection) => rememberClipboard('copy', selection)}
          onDuplicateDocument={(document) => duplicateDocument(document).catch(() => null)}
          onRenameDocument={(document) => renameDocument(document).catch(() => null)}
          onShowShortcuts={() => setShortcutHelpOpen(true)}
          organizationFilters={organizationFilters}
          organizationPreferences={organizationPreferences}
          registrySort={registrySort}
          organizationBusy={organizationBusy}
          onOrganizationFiltersChange={(value) => { setOrganizationFilters(value); setDocumentFirst(0); }}
          onOrganizationUpdate={updateDocumentOrganization}
          onRegistrySortChange={(value) => { setRegistrySort(value); setDocumentFirst(0); }}
          onSaveOrganizationView={(name, definition) => saveOrganizationView(name, definition).catch(() => null)}
          onApplyOrganizationView={applyOrganizationView}
          onDeleteOrganizationView={(viewId) => deleteOrganizationView(viewId).catch(() => null)}
          onRegistryColumnsChange={(columns) => {
            setOrganizationPreferences((current) => ({ ...current, columns }));
            updateOrganizationPreferences({ columns }).catch(() => null);
          }}
          bulkResult={bulkResult}
          onDismissBulkResult={() => setBulkResult(null)}
          onDropMove={moveItemToFolder}
          onRenameFolder={renameFolder}
          onNewDocument={openNewDocument}
          onPreviousPage={() => setDocumentFirst((current) => Math.max(0, current - DOCUMENT_PAGE_SIZE))}
          onNextPage={() => setDocumentFirst((current) => current + DOCUMENT_PAGE_SIZE)} />
          )}
          {pendingFolderUpload && (
            <DocumentTransferManager
              key={pendingFolderUpload.id}
              background
              destinationFolderId={pendingFolderUpload.folder?.folder_id || ''}
              destinationLabel={folderBrowserPath(pendingFolderUpload.folder) || pendingFolderUpload.folder?.folder_path || ''}
              matterNumber={pendingFolderUpload.folder?.matter_number || matterNumber}
              folderPath={pendingFolderUpload.folder?.folder_path || folderPath}
              initialFiles={pendingFolderUpload.files}
              autoStart
              onNotice={(message, kind) => {
                if (!message || kind !== 'alert') return;
                setPendingFolderUpload(null);
                showToast && showToast({ severity: 'error', summary: 'Upload failed', detail: message });
              }}
              onUploadComplete={

              (upload) => {
                const destinationFolder = pendingFolderUpload.folder;
                const addedCount = (upload?.files || []).filter((file) => file.status === 'complete').length;
                const destination = folderBrowserPath(destinationFolder) || destinationFolder?.folder_path || 'folder';
                const destinationIsOpen = Boolean(
                  activeFolder && folderBrowserIdentity(activeFolder) === folderBrowserIdentity(destinationFolder)
                );
                setPendingFolderUpload(null);
                showToast && showToast({
                  severity: 'success',
                  summary: `${addedCount} file${addedCount === 1 ? '' : 's'} added`,
                  detail: `Uploaded to ${destination}.`,
                });
                loadFolderCatalog({ background: true });
                if (destinationIsOpen) {
                  loadFolderDocuments(destinationFolder, folderSearch, { background: true });
                }
              }} />
          )}
          {utilityPanel === 'trash' && (
            <section className="documents-utility-surface" aria-label="Deleted documents">
              <TrashView
                canPermanentlyDelete={Boolean(auth.isAdmin)}
                onChanged={refreshRegistry} />
            </section>
          )}
          {utilityPanel === 'governance' && Boolean(auth.isAdmin) && (
            <section className="documents-utility-surface" aria-label="Document governance">
              <DocumentGovernanceSettings />
            </section>
          )}
        </>
      )}

      {workspaceMode === 'document' && (
        <DocumentDetailView
          selectedDocument={selectedDocument}
          destinationSegments={
            createdDestination?.document_id === selectedDocumentId
              ? createdDestination.segments
              : null
          }
          versions={versions}
          activity={activity}
          matchingVersions={matchingVersions}
          versionsLoading={versionsLoading}
          previewVersion={previewVersion}
          activePreviewMode={activePreviewMode}
          redlineReady={redlineReady}
          redlineAvailable={redlineAvailable}
          comparison={activeComparison}
          comparisonRenderer={comparisonRenderer}
          previewRequested={previewRequested}
          selectedChange={selectedChange}
          selectedVersionId={selectedVersionId}
          changes={changes}
          changesLoading={changesLoading}
          changesError={changesError}
          comparisonError={comparisonError}
          baseRevisionId={baseRevisionId}
          targetRevisionId={targetRevisionId}
          canCompare={canCompare}
          comparisonLoading={comparisonLoading}
          onBackToRegistry={backToRegistry}
          onMoveDocument={openDocumentLocation}
          onRenameDocument={(name) => renameDocument(selectedDocument, name)}
          onOpenShare={() => setSharePanelOpen(true)}
          onRequestPreview={() => setPreviewRequested(true)}
          onSetPreviewMode={selectPreviewMode}
          onComparisonRendererChange={changeComparisonRenderer}
          onSelectVersion={openVersionPreview}
          onBaseRevisionChange={updateBaseRevision}
          onTargetRevisionChange={updateTargetRevision}
          onStartComparison={startComparison}
          onCompareVersions={compareVersions}
          onShareLatest={shareLatest}
          onShareVersion={shareVersion}
          onEditVersionMetadata={openVersionMetadata}
          onOpenUpload={() => openWriteModal('upload')}
          onOpenMerge={() => openWriteModal('merge')}
          onPrepareMergeReview={() => startComparison(latestVersion?.revision_id, actionVersion?.revision_id)}
          onPromoteVersion={openVersionPromotion}
          isVersionPromotable={isVersionPromotable}
          onUnlockVersion={openLockModal}
          canUpload={canUpload}
          canMerge={canMerge}
          mergeReplacesCurrent={mergeReplacesCurrent}
          canPrepareMergeReview={canPrepareMergeReview}
          mergeReviewStatus={mergeReviewStatus}
          onEditWord={openWordEdit}
          editStarting={editStarting}
          writeSubmitting={writeSubmitting}
          onProofreadWordOpened={

          (data) => {
            wordEditObservedActivityRef.current = false;
            wordEditPendingRevisionRef.current = '';
            setWordEditMonitor({ documentId: selectedDocumentId, startedAt: Date.now() });
            window.location.href = data.edit_session.office_uri;
            refreshAfterWrite(data.version?.revision_id, { exitComparison: true });
          }}
          onAiAdopted={(revisionId) => refreshAfterWrite(revisionId, { exitComparison: true })} />
      )}
      {workspaceMode === 'document' && sharePanelOpen && selectedDocument && (
        <div className="documents-share-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSharePanelOpen(false);
        }}>
          <DocumentSharePanel
            hideComments
            resourceType="document"
            resourceId={selectedDocument.document_id}
            resourceName={selectedDocument.filename}
            directVersionUrl={documentVersionUrl(selectedDocumentId, previewVersion || latestVersion)}
            onClose={() => setSharePanelOpen(false)} />
        </div>
      )}
      <ArchiveWriteModal
        modal={writeModal}
        form={writeForm}
        file={writeFile}
        selectedVersion={writeActionVersion}
        latestVersion={latestVersion}
        selectedChange={selectedChange}
        redlineReady={mergeReviewReady}
        mergeReplacesCurrent={mergeReplacesCurrent}
        submitting={writeSubmitting}
        error={writeError}
        onClose={closeWriteModal}
        onChange={updateWriteForm}
        onFileChange={setWriteFile}
        onSubmit={submitWriteAction} />
      <NewDocumentModal
        visible={newDocumentOpen}
        form={newDocumentForm}
        file={newDocumentFile}
        templates={templates}
        matters={matterChoices}
        matterNumbers={matterNumberOptions}
        folders={folderCatalog}
        mattersLoading={matterChoicesLoading}
        submitting={newDocumentSubmitting}
        error={newDocumentError}
        onClose={() => !newDocumentSubmitting && setNewDocumentOpen(false)}
        onChange={(updates) => setNewDocumentForm((current) => ({ ...current, ...updates }))}
        onFileChange={setNewDocumentFile}
        onSubmit={submitNewDocument} />
      <NewFolderModal
        visible={newFolderOpen}
        form={newFolderForm}
        submitting={newFolderSubmitting}
        error={newFolderError}
        onChange={(updates) => setNewFolderForm((current) => ({ ...current, ...updates }))}
        onClose={() => !newFolderSubmitting && setNewFolderOpen(false)}
        onSubmit={submitNewFolder} />
      <FolderDestinationModal
        visible={Boolean(moveTarget)}
        action={moveTarget?.action || 'move'}
        itemType={moveTarget?.type === 'bulk' ? 'file' : moveTarget?.type || 'file'}
        itemCount={moveTarget?.type === 'bulk' ? moveTarget?.count : 1}
        itemLabel={moveTarget?.type === 'bulk'
          ? `${moveTarget?.count || 'Selected'} selected documents`
          : moveTarget?.item?.filename || moveTarget?.item?.name || folderTitle(moveTarget?.item?.folder_path)}
        folders={folderCatalog}
        excludedFolderIds={moveExcludedFolderIds}
        excludedDestinationKeys={moveExcludedDestinationKeys}
        requireFolderId={moveTarget?.type === 'folder'}
        defaultScope={
          moveTarget?.action === 'merge' || (
            moveTarget?.type === 'folder' && !String(moveTarget?.item?.matter_number || '').trim()
          )
            ? 'matter'
            : 'all'
        }
        preserveSimulLocation={Boolean(
          moveTarget?.item?.source_simul_folder_id ||
          moveTarget?.item?.source_simul_folder_path ||
          moveTarget?.item?.simul_folder_id ||
          moveTarget?.item?.simul_folder_path
        )}
        submitting={moveSubmitting}
        error={moveError}
        defaultCreateParent={moveCreateParent}
        onClose={() => !moveSubmitting && setMoveTarget(null)}
        onCreateFolder={createMoveDestination}
        onSubmit={submitMove} />
      <DocumentShortcutHelp visible={shortcutHelpOpen} onClose={() => setShortcutHelpOpen(false)} />
      <DocumentFileViewerModal
        document={fileViewerDocument}
        onClose={() => setFileViewerDocument(null)} />
    </main>
  );
}

export default DocumentsPage;
